/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';

import { 
  Home, 
  Calendar,
  Sprout, 
  Droplets, 
  Info, 
  Menu, 
  X, 
  ChevronRight, 
  ChevronUp,
  ChevronDown,
  Leaf,
  Bug,
  BookOpen,
  HelpCircle,
  ChevronLeft,
  ShieldCheck,
  LogOut,
  Plus,
  Upload,
  UserCircle,
  Book,
  Sun,
  Cloud,
  CloudRain,
  CloudSun,
  CloudOff,
  CloudDrizzle,
  CloudLightning,
  Snowflake,
  Wind,
  Video,
  Play,
  Share2,
  Volume2,
  Loader2,
  RefreshCcw,
  FileText,
  Copy,
  Check,
  AlertTriangle,
  Megaphone,
  ArrowUp,
  ArrowDown,
  Mail,
  Bot,
  MessageSquare,
  ShoppingBag,
  TrendingUp,
  TrendingDown,
  Minus,
  Store,
  Settings,
  Trash2,
  Phone,
  MapPin,
  Star,
  Award,
  Search,
  Filter,
  IndianRupee,
  Camera,
  Move,
  QrCode,
  Download,
  ListChecks
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { QRCodeSVG } from 'qrcode.react';
import { CultivationMap } from './components/CultivationMap';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { auth, db, handleFirestoreError, OperationType, googleProvider } from './firebase';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  User,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword
} from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { 
  collection, 
  onSnapshot, 
  doc, 
  setDoc, 
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  writeBatch,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';

import AIAdvisor from './components/AIAdvisor';
import Forum from './components/Forum';
import MarketplaceScreen from './components/MarketplaceScreen';
import ExpertConnectScreen from './components/ExpertConnectScreen';
import PlantDoctorScreen from './components/PlantDoctorScreen';
import TraceabilityScreen from './components/TraceabilityScreen';
import CropCalendar from './components/CropCalendar';
import CropCalendarAdmin from './components/CropCalendarAdmin';
import MarketPricesScreen from './components/MarketPricesScreen';
import ErrorBoundary from './components/ErrorBoundary';
import { ScannerScreen } from './components/ScannerScreen';
import { Language, translations } from './translations';
import { Toast, ConfirmDialog, VideoSection } from './components/UI';
import { AdMobService } from './services/admob';
import BannerAd from './components/BannerAd';
import { exportToExcel } from './lib/excelUtils';
import { 
  VideoItem, 
  HandbookSection, 
  HandbookItem, 
  HandbookCategory, 
  ChatMessage,
  FAQ,
  PlantDiagnosis,
  Screen,
  MarketPrice,
  Listing,
  Expert,
  ExpertMessage,
  PageContent,
  MenuItem,
  Calculator,
  CalculatorRow,
  SignUpField,
  UserProfile,
  CropSchedule,
  Practice,
  UserCrop
} from './types';

const INITIAL_MENU_ITEMS: MenuItem[] = [
  { id: 'home', screen: 'home', labelKey: 'home', iconName: 'Home', order: 1, visible: true },
  { id: 'marketplace', screen: 'marketplace', labelKey: 'marketplace', iconName: 'ShoppingBag', order: 2, visible: true },
  { id: 'plantDoctor', screen: 'plantDoctor', labelKey: 'plantDoctor', iconName: 'Bug', order: 3, visible: true },
  { id: 'marketPrices', screen: 'marketPrices', labelKey: 'marketPrices', iconName: 'TrendingUp', order: 4, visible: true },
  { id: 'cropCalendar', screen: 'cropCalendar', labelKey: 'cropCalendar', iconName: 'Calendar', order: 5, visible: true },
  { id: 'traceability', screen: 'apcnfTraceability', labelKey: 'apcnfTraceability', iconName: 'Search', order: 6, visible: true },
  { id: 'forum', screen: 'forum', labelKey: 'forum', iconName: 'MessageSquare', order: 7, visible: true },
  { id: 'scanner', screen: 'scanner', labelKey: 'scanner', iconName: 'QrCode', order: 8, visible: true },
  { id: 'videos', screen: 'videos', labelKey: 'videos', iconName: 'Video', order: 9, visible: true },
  { id: 'handbook', screen: 'handbook', labelKey: 'handbook', iconName: 'BookOpen', order: 10, visible: true },
  { id: 'expertConnect', screen: 'expertConnect', labelKey: 'expertConnect', iconName: 'Bot', order: 11, visible: true },
  { id: 'admin', screen: 'admin', labelKey: 'adminPanel', iconName: 'ShieldCheck', order: 12, visible: true, isAdminOnly: true },
  { id: 'settings', screen: 'settings', labelKey: 'settings', iconName: 'Settings', order: 13, visible: true },
  { id: 'privacy', screen: 'privacy', labelKey: 'privacyPolicy', iconName: 'ShieldCheck', order: 14, visible: true },
  { id: 'terms', screen: 'terms', labelKey: 'termsAndConditions', iconName: 'Book', order: 15, visible: true },
  { id: 'about-us', screen: 'aboutUs', labelKey: 'aboutUs', iconName: 'Info', order: 16, visible: true },
];

const getIcon = (name: string, size = 20, className = "") => {
  const icons: { [key: string]: any } = {
    Home, Calendar, Sprout, Droplets, Info, Menu, X, ChevronRight, Leaf, Bug, BookOpen, HelpCircle, ChevronLeft, ShieldCheck, LogOut, Plus, Upload, UserCircle, Book, Sun, Cloud, CloudRain, CloudSun, CloudOff, Wind, Video, Play, Share2, Volume2, Loader2, Copy, Check, AlertTriangle, Megaphone, ArrowUp, ArrowDown, Mail, Bot, MessageSquare, ShoppingBag, TrendingUp, TrendingDown, Minus, Store, Settings, Trash2, Phone, MapPin, Star, Award, Search, Filter
  };
  const IconComponent = icons[name] || HelpCircle;
  return <IconComponent size={size} className={className} />;
};

const getLocalized = (obj: any, field: string, lang: string) => obj[`${field}_${lang}`] || obj[field] || '';

function HandbookItemDetail({ 
  item, 
  onBack, 
  onNavigate, 
  language, 
  t 
}: { 
  item: HandbookItem, 
  onBack: () => void, 
  onNavigate: (s: Screen) => void,
  language: Language,
  t: any
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const contentToCopy = item.sections.map(s => `${getLocalized(s, 'title', language)}\n${getLocalized(s, 'content', language)}`).join('\n\n');
    navigator.clipboard.writeText(contentToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    const contentToShare = item.sections.map(s => `${getLocalized(s, 'title', language)}\n${getLocalized(s, 'content', language)}`).join('\n\n');
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Prakruthi Mithra: ${getLocalized(item, 'name', language)}`,
          text: contentToShare,
        });
      } catch (err) {
        console.error('Share failed:', err);
      }
    } else {
      handleCopy();
    }
  };

  return (
    <div className="p-4 space-y-6 pb-20">
      <button 
        onClick={onBack}
        className="flex items-center gap-2 text-[#1b7d36] font-bold mb-4"
      >
        <ChevronLeft size={20} /> {t.back}
      </button>

      <div className="bg-white rounded-[40px] overflow-hidden shadow-lg border border-black/5">
        {item.image && (
          <img src={item.image} alt={getLocalized(item, 'name', language)} className="w-full aspect-video object-cover" referrerPolicy="no-referrer" />
        )}
        <div className="p-8 space-y-6">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-xl font-bold text-[#1b7d36]">{getLocalized(item, 'name', language)}</h2>
            <div className="flex gap-3">
              <button 
                onClick={handleCopy}
                className={`transition-colors ${copied ? 'text-emerald-500' : 'text-stone-400 hover:text-[#1b7d36]'}`}
                title={t.copy}
              >
                {copied ? <Check size={18} /> : <Copy size={18} />}
              </button>
              <button 
                onClick={handleShare}
                className="text-stone-400 hover:text-[#1b7d36] transition-colors"
                title={t.share}
              >
                <Share2 size={18} />
              </button>
            </div>
          </div>
          
          <div className="space-y-8">
            {item.sections.map((section) => (
              <div key={section.id} className="space-y-2">
                <h4 className="text-base font-bold text-stone-800 border-l-4 border-[#1b7d36] pl-3">{getLocalized(section, 'title', language)}</h4>
                <div className="text-stone-600 leading-relaxed pl-4 text-justify markdown-body">
                  <ReactMarkdown>{getLocalized(section, 'content', language)}</ReactMarkdown>
                </div>
              </div>
            ))}
            {item.sections.length === 0 && (
              <p className="text-stone-500 italic">{t.noDetailsAvailable}</p>
            )}
          </div>

          <button 
            onClick={() => onNavigate(item.screen)}
            className="w-full bg-[#1b7d36] text-white py-4 rounded-2xl font-bold shadow-md hover:bg-[#16652b] transition-colors mt-8"
          >
            {t.moreInfo}
          </button>
        </div>
      </div>
    </div>
  );
}

const resizeImage = (base64Str: string, maxWidth = 320, maxHeight = 240, quality = 0.35): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = base64Str;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > maxWidth) {
          height *= maxWidth / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width *= maxHeight / height;
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
  });
};

const compressCategory = async (cat: HandbookCategory): Promise<HandbookCategory> => {
  const compressedItems = await Promise.all(
    cat.items.map(async (item) => {
      if (item.image && item.image.startsWith('data:image')) {
        try {
          const compressed = await resizeImage(item.image, 320, 240, 0.35);
          return { ...item, image: compressed };
        } catch (e) {
          console.error("Failed to compress item image in compressCategory:", item.id, e);
          return item;
        }
      }
      return item;
    })
  );
  return { ...cat, items: compressedItems };
};

const INITIAL_HANDBOOK: HandbookCategory[] = [
  {
    id: '1',
    name: 'PMDS విధానం',
    name_en: 'PMDS Method',
    name_te: 'PMDS విధానం',
    name_hi: 'PMDS पद्धति',
    order: 0,
    items: [
      { 
        id: '1-1', 
        name: 'PMDS విధానం', 
        name_en: 'PMDS Method',
        name_te: 'PMDS విధానం',
        name_hi: 'PMDS पद्धति',
        screen: 'home', 
        image: 'https://picsum.photos/seed/pmds/800/400',
        sections: [
          { 
            id: 's1', 
            title: 'పరిచయం', 
            title_en: 'Introduction',
            title_te: 'పరిచయం',
            title_hi: 'परिचय',
            content: 'PMDS అంటే ప్రీ మాన్సూన్ డ్రై సోయింగ్. వర్షాకాలం కంటే ముందే విత్తనాలు విత్తుకోవడం.',
            content_en: 'PMDS stands for Pre-Monsoon Dry Sowing. Sowing seeds before the rainy season.',
            content_te: 'PMDS అంటే ప్రీ మాన్సూన్ డ్రై సోయింగ్. వర్షాకాలం కంటే ముందే విత్తనాలు విత్తుకోవడం.',
            content_hi: 'PMDS का अर्थ है प्री-मानसून ड्राई सोइंग। मानसून से पहले बीज बोना।'
          }
        ]
      },
      { 
        id: '1-2', 
        name: 'విత్తన గుళికలు', 
        screen: 'home', 
        image: 'https://picsum.photos/seed/seeds/800/400',
        sections: [
          { id: 's1', title: 'తయారీ', content: 'మట్టి, పేడ మరియు విత్తనాలను కలిపి గుళికలుగా తయారు చేయడం.' }
        ]
      }
    ]
  },
  {
    id: '2',
    name: 'కషాయాలు',
    order: 1,
    items: [
      { 
        id: '2-1', 
        name: 'ఘన జీవామృతం', 
        screen: 'inputs', 
        image: 'https://picsum.photos/seed/ghana/800/400',
        sections: [
          { id: 's1', title: 'కావలసిన పదార్థాలు', content: '100 కిలోల ఆవు పేడ, 2 కిలోల బెల్లం, 2 కిలోల పప్పు పిండి, గుప్పెడు పొలం మట్టి, కొద్దిగా ఆవు మూత్రం.' },
          { id: 's2', title: 'తయారీ విధానం', content: 'అన్ని పదార్థాలను కలిపి నీడలో ఆరబెట్టాలి. బాగా ఎండిన తర్వాత పొడి చేసి నిల్వ చేసుకోవాలి.' },
          { id: 's3', title: 'వాడే విధానం', content: 'విత్తే సమయంలో లేదా దుక్కిలో ఎరువుగా వాడుకోవచ్చు.' }
        ]
      },
      { 
        id: '2-2', 
        name: 'బీజామృతం', 
        screen: 'inputs', 
        image: 'https://picsum.photos/seed/beeja/800/400',
        sections: [
          { id: 's1', title: 'కావలసిన పదార్థాలు', content: '5 లీటర్ల ఆవు మూత్రం, 5 కిలోల ఆవు పేడ, 50 గ్రాముల సున్నం, గుప్పెడు మట్టి, 20 లీటర్ల నీరు.' },
          { id: 's2', title: 'తయారీ విధానం', content: 'అన్నింటినీ కలిపి 24 గంటలు ఉంచాలి. రోజుకు రెండుసార్లు కర్రతో తిప్పాలి.' },
          { id: 's3', title: 'వాడే విధానం', content: 'విత్తన శుద్ధి కోసం ఉపయోగిస్తారు. విత్తనాలను ఈ ద్రావణంలో ముంచి ఆరబెట్టి నాటుకోవాలి.' }
        ]
      },
      { 
        id: '2-3', 
        name: 'ద్రవ జీవామృతం', 
        screen: 'inputs', 
        image: 'https://picsum.photos/seed/drava/800/400',
        sections: [
          { id: 's1', title: 'కావలసిన పదార్థాలు', content: '200 లీటర్ల నీరు, 10 కిలోల ఆవు పేడ, 10 లీటర్ల ఆవు మూత్రం, 2 కిలోల బెల్లం, 2 కిలోల పప్పు పిండి, గుప్పెడు మట్టి.' },
          { id: 's2', title: 'తయారీ విధానం', content: 'అన్నింటినీ కలిపి 2-3 రోజులు నీడలో ఉంచి మురగబెట్టాలి. రోజుకు రెండుసార్లు తిప్పాలి.' },
          { id: 's3', title: 'వాడే విధానం', content: 'నీటితో కలిపి పొలానికి పారించాలి లేదా పిచికారీ చేయాలి.' }
        ]
      }
    ]
  },
  {
    id: '3',
    name: 'పంటలు',
    order: 2,
    items: [
      { 
        id: '3-1', 
        name: 'వరి', 
        screen: 'crops', 
        image: 'https://picsum.photos/seed/paddy/800/400',
        sections: [
          { id: 's1', title: 'విత్తే సమయం', content: 'ఖరీఫ్: జూన్-జూలై, రబీ: నవంబర్-డిసెంబర్.' },
          { id: 's2', title: 'ప్రకృతి పద్ధతులు', content: 'బీజామృతంతో విత్తన శుద్ధి, ద్రవ జీవామృతం పారించడం.' },
          { id: 's3', title: 'పురుగుల నివారణ', content: 'నీమాస్త్రం, బ్రహ్మాస్త్రం పిచికారీ చేయడం ద్వారా పురుగులను నివారించవచ్చు.' }
        ]
      },
      { 
        id: '3-2', 
        name: 'వేరుశనగ', 
        screen: 'crops', 
        image: 'https://picsum.photos/seed/groundnut/800/400',
        sections: [
          { id: 's1', title: 'విత్తే సమయం', content: 'ఖరీఫ్: జూన్-జూలై.' },
          { id: 's2', title: 'యాజమాన్యం', content: 'ఘన జీవామృతం ఎరువుగా వాడటం.' }
        ]
      },
      { 
        id: '3-3', 
        name: 'మిరప', 
        screen: 'crops', 
        image: 'https://picsum.photos/seed/chilli/800/400',
        sections: [
          { id: 's1', title: 'విత్తే సమయం', content: 'ఖరీఫ్: జూలై-ఆగస్టు.' },
          { id: 's2', title: 'యాజమాన్యం', content: 'నారుమడి తయారీలో ఘన జీవామృతం వాడటం.' }
        ]
      },
      { 
        id: '3-4', 
        name: 'శనగ', 
        screen: 'crops', 
        image: 'https://picsum.photos/seed/chickpea/800/400',
        sections: [
          { id: 's1', title: 'విత్తే సమయం', content: 'రబీ: అక్టోబర్-నవంబర్.' },
          { id: 's2', title: 'ప్రకృతి పద్ధతులు', content: 'విత్తన శుద్ధి మరియు ఆఖరి దుక్కిలో ఘన జీవామృతం.' }
        ]
      },
      { 
        id: '3-5', 
        name: 'పత్తి', 
        screen: 'crops', 
        image: 'https://picsum.photos/seed/cotton/800/400',
        sections: [
          { id: 's1', title: 'విత్తే సమయం', content: 'ఖరీఫ్: జూన్-జూలై.' },
          { id: 's2', title: 'పురుగుల నివారణ', content: 'గులాబీ రంగు పురుగు నివారణకు పసుపు రంగు జిగురు అట్టలు.' }
        ]
      },
      { 
        id: '3-6', 
        name: 'మొక్కజొన్న', 
        screen: 'crops', 
        image: 'https://picsum.photos/seed/maize/800/400',
        sections: [
          { id: 's1', title: 'విత్తే సమయం', content: 'ఖరీఫ్: జూన్, రబీ: అక్టోబర్.' },
          { id: 's2', title: 'యాజమాన్యం', content: 'కత్తెర పురుగు నివారణకు జీవామృతం మరియు ఇసుక మిశ్రమం.' }
        ]
      },
      { 
        id: '3-7', 
        name: 'మినుము/పెసర', 
        screen: 'crops', 
        image: 'https://picsum.photos/seed/pulses/800/400',
        sections: [
          { id: 's1', title: 'విత్తే సమయం', content: 'ఖరీఫ్: జూన్, రబీ: నవంబర్.' },
          { id: 's2', title: 'ప్రకృతి పద్ధతులు', content: 'విత్తన శుద్ధికి బీజామృతం వాడటం.' }
        ]
      },
      { 
        id: '3-8', 
        name: 'పసుపు', 
        screen: 'crops', 
        image: 'https://picsum.photos/seed/turmeric/800/400',
        sections: [
          { id: 's1', title: 'విత్తే సమయం', content: 'మే-జూన్.' },
          { id: 's2', title: 'యాజమాన్యం', content: 'దుంప కుళ్ళు నివారణకు ట్రైకోడెర్మా విరిడి కలిపిన ఘన జీవామృతం.' }
        ]
      },
    ]
  }
];

const INITIAL_CALCULATORS: Calculator[] = [
  {
    id: 'pmds-seed',
    title: 'PMDS విత్తన క్యాలిక్యులేటర్',
    title_en: 'PMDS Seed Calculator',
    title_te: 'PMDS విత్తన క్యాలిక్యులేటర్',
    title_hi: 'PMDS बीज कैलकुलेटर',
    baseRate: 13,
    rows: [
      { 
        id: '1', 
        label: 'ధాన్యాలు', 
        label_en: 'Cereals',
        label_te: 'ధాన్యాలు',
        label_hi: 'अनाज',
        sublabel: 'సజ్జలు, జొన్నలు, రాగులు, కొర్రలు, ఆరికలు', 
        sublabel_en: 'Pearl Millet, Sorghum, Finger Millet, Foxtail Millet, Kodo Millet',
        sublabel_te: 'సజ్జలు, జొన్నలు, రాగులు, కొర్రలు, ఆరికలు',
        sublabel_hi: 'बाजरा, ज्वार, रागी, कंगनी, कोदो',
        ratio: 3.9 
      },
      { id: '2', label: 'పప్పు దినుసులు', sublabel: 'కందులు, పెసలు, మినుములు, అలసందలు, శనగలు', ratio: 3.9 },
      { id: '3', label: 'నూనె గింజలు', sublabel: 'వేరుశనగ, నువ్వులు, కుసుమలు, ఆముదాలు, ఆవాలు', ratio: 2.6 },
      { id: '4', label: 'పశుగ్రాసం', sublabel: 'పిల్లిపెసర, అలసంద, గడ్డి రకాలు', ratio: 1.3 },
      { id: '5', label: 'కూరగాయలు', sublabel: 'బెండ, గోరుచిక్కుడు, సొరకాయ, టమోటా', ratio: 1.3 },
      { id: '6', label: 'పంట మార్పిడి పంటలు', sublabel: 'అలసంద, పెసర, మినుము', ratio: 1.3 },
    ]
  }
];

const INITIAL_VIDEOS: VideoItem[] = [
  {
    id: '1',
    title: 'ప్రకృతి వ్యవసాయం పరిచయం',
    url: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    thumbnail: 'https://picsum.photos/seed/video1/800/400'
  }
];

const INITIAL_SIGNUP_FIELDS: SignUpField[] = [
  { id: 'role', 
    label: 'userRole', 
    placeholder: 'roleRequired', 
    type: 'select', 
    required: true, 
    order: 1,
    options: [
      { value: 'farmer', label: 'Farmer' },
      { value: 'aggregator', label: 'Aggregator' },
      { value: 'buyer', label: 'Buyer' }
    ]
  },
  { id: 'name', label: 'fullName', placeholder: 'enterFullName', type: 'text', required: true, order: 2 },
  { id: 'phone', label: 'phoneNumber', placeholder: 'enterPhoneNumber', type: 'tel', required: true, order: 3 },
  { id: 'location', label: 'location', placeholder: 'enterVillageName', type: 'text', required: true, order: 4 },
  { id: 'farmerId', label: 'farmerId', placeholder: 'enterFarmerId', type: 'text', required: false, order: 5 },
];

const getYouTubeEmbedUrl = (url: string) => {
  if (!url) return '';
  if (url.includes('embed/')) return url;
  
  let videoId = '';
  if (url.includes('watch?v=')) {
    videoId = url.split('watch?v=')[1].split('&')[0];
  } else if (url.includes('youtu.be/')) {
    videoId = url.split('youtu.be/')[1].split('?')[0];
  }
  
  if (videoId) {
    return `https://www.youtube.com/embed/${videoId}`;
  }
  return url;
};

export default function App() {
  const [currentScreen, setCurrentScreen] = useState<Screen>('home');
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem('prakruthi_mithra_language');
    return (saved as Language) || 'en';
  });

  useEffect(() => {
    localStorage.setItem('prakruthi_mithra_language', language);
  }, [language]);

  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const t = translations[language];
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userRole, setUserRole] = useState<string>('');
  const [isExpert, setIsExpert] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [handbookData, setHandbookData] = useState<HandbookCategory[]>(INITIAL_HANDBOOK);
  const [activeHandbookCategoryId, setActiveHandbookCategoryId] = useState<string | null>(null);
  const [calculators, setCalculators] = useState<Calculator[]>(INITIAL_CALCULATORS);
  const [videos, setVideos] = useState<VideoItem[]>(INITIAL_VIDEOS);
  const [pages, setPages] = useState<PageContent[]>([]);
  const [signUpFields, setSignUpFields] = useState<SignUpField[]>(INITIAL_SIGNUP_FIELDS);
  const [marketPrices, setMarketPrices] = useState<MarketPrice[]>([]);
  const [listings, setListings] = useState<Listing[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>(INITIAL_MENU_ITEMS);
  const [branding, setBranding] = useState<{ logoUrl: string }>({ logoUrl: '' });
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [cropSchedules, setCropSchedules] = useState<CropSchedule[]>([]);
  const [admobConfig, setAdmobConfig] = useState<{
    bannerId: string;
    interstitialId: string;
    rewardedId: string;
    isTesting: boolean;
  }>({
    bannerId: 'ca-app-pub-4999656050364868/1813969680',
    interstitialId: 'ca-app-pub-4999656050364868/2801403286',
    rewardedId: 'ca-app-pub-4999656050364868/4600232357',
    isTesting: false
  });
  const [marketplaceConfig, setMarketplaceConfig] = useState<{
    availableCertifications: string[];
    farmingMethods: string[];
    productCategories: string[];
    visibleFields: {
      location: boolean;
      contact: boolean;
      certificate: boolean;
      farmingMethods: boolean;
    }
  }>({
    availableCertifications: [
      'Practicing Natural Farming',
      'Organic Certified',
      'ZBNF Practitioner',
      'Pesticide Free',
      'Soil Health Card Holder'
    ],
    farmingMethods: ['ZBNF', 'Natural', 'Organic'],
    productCategories: ['Vegetables', 'Fruits', 'Grains', 'Millets', 'Inputs'],
    visibleFields: {
      location: true,
      contact: true,
      certificate: true,
      farmingMethods: true
    }
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scannedFarmerId, setScannedFarmerId] = useState<string | null>(null);
  const [isAdMobSaving, setIsAdMobSaving] = useState(false);
  const [toast, setToast] = useState<{message: string, type: 'success' | 'error' | 'info'} | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{isOpen: boolean, title: string, message: string, onConfirm: () => void, type?: 'danger' | 'info'} | null>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [showQrOnly, setShowQrOnly] = useState(false);
  const [marketplaceInitialAdd, setMarketplaceInitialAdd] = useState(false);
  const [editingProfile, setEditingProfile] = useState<{ 
    displayName: string, 
    photoURL: string, 
    phone: string, 
    location: string, 
    role: string, 
    farmerId: string,
    farmingMethods: string,
    certifications: string[],
    certificateUrl: string,
    certificatePdfUrl: string,
    cultivationPractices: { description: string, photos: string[] },
    crops: { id: string, name: string, harvestTime: string, area?: string }[],
    cultivationArea: { description: string, lat: number, lng: number }
  }>({ 
    displayName: '', 
    photoURL: '', 
    phone: '', 
    location: '',
    role: '',
    farmerId: '',
    farmingMethods: '',
    certifications: [],
    certificateUrl: '',
    certificatePdfUrl: '',
    cultivationPractices: { description: '', photos: [] },
    crops: [],
    cultivationArea: { description: '', lat: 0, lng: 0 }
  });
  const [passwordFields, setPasswordFields] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const farmerId = params.get('farmerId');
    if (farmerId) {
      setCurrentScreen('marketplace');
    }
  }, []);

  // AdMob will be initialized after configuration is loaded from Firestore

  useEffect(() => {
    const q = query(collection(db, 'menu_items'), orderBy('order', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        const firestoreItems = snapshot.docs.map(doc => doc.data() as MenuItem);
        const merged = INITIAL_MENU_ITEMS.map(initial => {
          const fromDb = firestoreItems.find(dbItem => dbItem.id === initial.id);
          return fromDb ? { ...initial, ...fromDb } : initial;
        });
        setMenuItems(merged.sort((a, b) => a.order - b.order));
      }
    }, (error) => {
      // Only log if it's not a permission error during initial load
      if (!(error instanceof Error && error.message.includes('permission'))) {
        handleFirestoreError(error, OperationType.LIST, 'menu_items');
      }
    });

    // Seed signup fields if empty
    const unsubFields = onSnapshot(collection(db, 'signup_fields'), (snapshot) => {
      if (!snapshot.empty) {
        // Just setting the data, no seeding here to avoid loops
      }
    }, (error) => {
      if (!(error instanceof Error && error.message.includes('permission'))) {
        handleFirestoreError(error, OperationType.LIST, 'signup_fields');
      }
    });

    // Seed initial entities if missing or force update specific ones
    const syncAppEntities = async () => {
      if (!user) return;
      
      // Check if user is admin before attempting to write
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const isAdminUser = user.email === 'prakruthimithra2026@gmail.com' || user.uid === '1KlvoeGBHOchBDZJXkJf5TC2Lf83' || (userDoc.exists() && userDoc.data()?.role === 'admin');
      
      if (!isAdminUser) return;

      try {
        // Sync Menu Items
        for (const item of INITIAL_MENU_ITEMS) {
          const docRef = doc(db, 'menu_items', item.id);
          const docSnap = await getDoc(docRef);
          if (!docSnap.exists() || ['forum', 'advisor', 'videos', 'handbook', 'marketPrices', 'traceability'].includes(item.id)) {
            await setDoc(docRef, item);
          }
        }

        // Sync Signup Fields
        for (const field of INITIAL_SIGNUP_FIELDS) {
          const docRef = doc(db, 'signup_fields', field.id);
          const docSnap = await getDoc(docRef);
          if (!docSnap.exists()) {
            await setDoc(docRef, field);
          }
        }

        // Sync Marketplace Config
        const marketConfigRef = doc(db, 'marketplace_config', 'farmer_profile');
        const marketConfigSnap = await getDoc(marketConfigRef);
        if (!marketConfigSnap.exists()) {
          await setDoc(marketConfigRef, {
            availableCertifications: [
              'Practicing Natural Farming',
              'Organic Certified',
              'ZBNF Practitioner',
              'Pesticide Free',
              'Soil Health Card Holder'
            ],
            farmingMethods: ['ZBNF', 'Natural', 'Organic'],
            visibleFields: {
              location: true,
              contact: true,
              certificate: true,
              farmingMethods: true
            }
          });
        }
      } catch (error) {
        console.error("Error syncing app entities:", error);
      }
    };

    syncAppEntities();

    return () => {
      unsubscribe();
      unsubFields();
    };
  }, [user]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        setVerificationEmail('');
      }
    });

    // Handle redirect result for mobile/webview platforms
    getRedirectResult(auth).then(async (result) => {
      if (result) {
        const redirectUser = result.user;
        const userDoc = await getDoc(doc(db, 'users', redirectUser.uid));
        if (!userDoc.exists()) {
          await setDoc(doc(db, 'users', redirectUser.uid), {
            uid: redirectUser.uid,
            email: redirectUser.email,
            displayName: redirectUser.displayName,
            photoURL: redirectUser.photoURL,
            role: redirectUser.email === "prakruthimithra2026@gmail.com" ? "admin" : "user",
            createdAt: serverTimestamp()
          });
        }
      }
    }).catch((error) => {
      console.error("Redirect auth error:", error);
    });

    return () => unsubscribe();
  }, []);

  // Handle roles, expert status, and profile
  useEffect(() => {
    if (user) {
      // Fetch user profile/role
      const unsubRole = onSnapshot(doc(db, 'users', user.uid), (snapshot) => {
        const isAdminUser = user.email === 'prakruthimithra2026@gmail.com' || user.uid === '1KlvoeGBHOchBDZJXkJf5TC2Lf83';
        if (snapshot.exists()) {
          const data = snapshot.data() as UserProfile;
          setProfile(data);
          const role = data.role || '';
          setUserRole(role);
          setIsAdmin(role === 'admin' || isAdminUser);
        } else {
          setProfile(null);
          setUserRole('');
          setIsAdmin(isAdminUser);
        }
      }, (error) => {
        if (!(error instanceof Error && error.message.includes('permission'))) {
          handleFirestoreError(error, OperationType.GET, `users/${user.uid}`);
        } else {
          console.warn("Initial role fetch permission restricted or doc missing, retrying...");
        }
      });

      // Fetch isExpert status
      let unsubExpert = () => {};
      if (user.email) {
        const email = user.email.toLowerCase().trim();
        const q = query(collection(db, 'experts'), where('email', '==', email));
        unsubExpert = onSnapshot(q, (snapshot) => {
          setIsExpert(!snapshot.empty);
        }, (error) => {
          handleFirestoreError(error, OperationType.LIST, 'experts');
        });
      }

      return () => {
        unsubRole();
        unsubExpert();
      };
    } else {
      setIsAdmin(false);
      setIsExpert(false);
    }
  }, [user]);

  // Real-time Firestore listeners
  useEffect(() => {
    // Signup fields are needed for the login/signup screen, but we'll follow the rule strictly if possible.
    // However, if we restrict them, the signup screen will be empty.
    // We'll allow signup_fields to be read publicly in rules, but here we can keep the listener.
    // Initial public subscriptions
    const unsubSignUpFields = onSnapshot(collection(db, 'signup_fields'), (snapshot) => {
      if (!snapshot.empty) {
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as SignUpField));
        data.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        setSignUpFields(data);
      }
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'signup_fields');
    });

    const unsubMarketplaceConfig = onSnapshot(doc(db, 'marketplace_config', 'farmer_profile'), (snapshot) => {
      if (snapshot.exists()) {
        setMarketplaceConfig(snapshot.data() as any);
      }
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, 'marketplace_config/farmer_profile');
    });

    const unsubHandbook = onSnapshot(collection(db, 'handbook'), (snapshot) => {
      if (!snapshot.empty) {
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as HandbookCategory));
        data.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        setHandbookData(data);
      }
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'handbook');
    });

    const unsubCalculators = onSnapshot(collection(db, 'calculators'), (snapshot) => {
      if (!snapshot.empty) {
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Calculator));
        setCalculators(data);
      }
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'calculators');
    });

    const unsubVideos = onSnapshot(collection(db, 'videos'), (snapshot) => {
      if (!snapshot.empty) {
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as VideoItem));
        data.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        setVideos(data);
      }
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'videos');
    });

    const unsubPages = onSnapshot(collection(db, 'pages'), (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as PageContent));
      setPages(data);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'pages');
    });

    const unsubMarketPrices = onSnapshot(collection(db, 'market_prices'), (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MarketPrice));
      setMarketPrices(data);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'market_prices');
    });

    const unsubCropSchedules = onSnapshot(collection(db, 'cropCalendar'), (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CropSchedule));
      setCropSchedules(data);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'cropCalendar');
    });

    const unsubBranding = onSnapshot(doc(db, 'app_config', 'branding'), (snapshot) => {
      if (snapshot.exists()) {
        setBranding(snapshot.data() as any);
      }
    }, (error) => {
      if (!(error instanceof Error && error.message.includes('permission'))) {
        handleFirestoreError(error, OperationType.GET, 'app_config/branding');
      }
    });

    const unsubAdMob = onSnapshot(doc(db, 'app_config', 'admob'), (snapshot) => {
      if (snapshot.exists()) {
        const config = snapshot.data() as any;
        // Ensure defaults are kept if fields are missing
        const fullConfig = {
          bannerId: config.bannerId || 'ca-app-pub-4999656050364868/1813969680',
          interstitialId: config.interstitialId || 'ca-app-pub-4999656050364868/2801403286',
          rewardedId: config.rewardedId || 'ca-app-pub-4999656050364868/4600232357',
          isTesting: config.isTesting ?? false
        };
        setAdmobConfig(fullConfig);
        AdMobService.configure(fullConfig);
        // Initialize or re-initialize with new config
        AdMobService.initialize();
      } else {
        // Doc doesn't exist, use initial state values
        AdMobService.configure(admobConfig);
        AdMobService.initialize();
      }
    }, (error) => {
      if (!(error instanceof Error && error.message.includes('permission'))) {
        handleFirestoreError(error, OperationType.GET, 'app_config/admob');
      }
    });

    return () => {
      unsubSignUpFields();
      unsubMarketplaceConfig();
      unsubHandbook();
      unsubCalculators();
      unsubVideos();
      unsubPages();
      unsubMarketPrices();
      unsubCropSchedules();
      unsubBranding();
      unsubAdMob();
    };
  }, [user]);

  // Show interstitial periodically when switching screens
  useEffect(() => {
    if (currentScreen !== 'home' && Math.random() < 0.2) {
      AdMobService.showInterstitial().catch(err => console.error('Interstitial error:', err));
    }
  }, [currentScreen]);

  const handleUpdateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) return;
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `users/${user.uid}`);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setCurrentScreen('home');
    } catch (error) {
      console.error("Logout error", error);
    }
  };

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleNavigateToAddProduct = () => {
    setMarketplaceInitialAdd(true);
    setCurrentScreen('marketplace');
    setIsMenuOpen(false);
  };

  const handleOpenProfileModal = async (qrOnly: boolean = false) => {
    if (user) {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.exists() ? userDoc.data() : {};
      setEditingProfile({
        displayName: userData.displayName || user.displayName || '',
        photoURL: userData.photoURL || user.photoURL || '',
        phone: userData.phone || '',
        location: userData.location || '',
        role: userData.role || '',
        farmerId: userData.farmerId || '',
        farmingMethods: userData.farmingMethods || '',
        certifications: userData.certifications || [],
        certificateUrl: userData.certificateUrl || '',
        certificatePdfUrl: userData.certificatePdfUrl || '',
        cultivationPractices: userData.cultivationPractices || { description: '', photos: [] },
        crops: userData.crops || [],
        cultivationArea: userData.cultivationArea || { description: '', lat: 0, lng: 0 }
      });
      setPasswordFields({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordChange(false);
      setShowQrOnly(qrOnly);
      setIsProfileModalOpen(true);
      setIsMenuOpen(false);
    }
  };

  const handleSaveProfileForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setIsSavingProfile(true);
    try {
      // Update auth profile
      // Note: firebase/auth's updateProfile is not imported yet, I'll need to check if it is
      // Actually I should update the firestore user document
      await updateDoc(doc(db, 'users', user.uid), {
        displayName: editingProfile.displayName,
        photoURL: editingProfile.photoURL,
        phone: editingProfile.phone,
        location: editingProfile.location,
        role: editingProfile.role,
        farmerId: editingProfile.farmerId,
        farmingMethods: editingProfile.farmingMethods,
        certifications: editingProfile.certifications,
        certificateUrl: editingProfile.certificateUrl,
        certificatePdfUrl: editingProfile.certificatePdfUrl,
        cultivationPractices: editingProfile.cultivationPractices,
        crops: editingProfile.crops,
        cultivationArea: editingProfile.cultivationArea,
        updatedAt: serverTimestamp()
      });
      
      setToast({ message: language === 'te' ? 'ప్రొఫైల్ విజయవంతంగా నవీకరించబడింది' : 'Profile updated successfully', type: 'success' });
      setIsProfileModalOpen(false);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `users/${user.uid}`);
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !user.email) return;
    
    if (passwordFields.newPassword !== passwordFields.confirmPassword) {
      setToast({ message: t.passwordMismatch, type: 'error' });
      return;
    }

    if (passwordFields.newPassword.length < 6) {
      setToast({ message: t.passwordTooShort, type: 'error' });
      return;
    }

    setIsChangingPassword(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, passwordFields.currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, passwordFields.newPassword);
      
      setToast({ message: t.passwordChanged, type: 'success' });
      setShowPasswordChange(false);
      setPasswordFields({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error: any) {
      console.error("Password change error:", error);
      let message = t.somethingWentWrong;
      if (error.code === 'auth/wrong-password') {
        message = t.incorrectEmailPassword;
      }
      setToast({ message, type: 'error' });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const renderScreen = () => {
    const publicScreens = ['home', 'forum', 'marketplace', 'handbook', 'videos', 'crops', 'inputs', 'expertConnect', 'settings', 'privacy', 'terms', 'aboutUs', 'scanner', 'apcnfTraceability'];
    if (!user && !publicScreens.includes(currentScreen)) {
      return (
        <LoginScreen 
          onLogin={() => setCurrentScreen('home')} 
          signUpFields={signUpFields} 
          verificationEmail={verificationEmail}
          setVerificationEmail={setVerificationEmail}
          branding={branding}
        />
      );
    }

    switch (currentScreen) {
      case 'home': return (
        <HomeScreen 
          onNavigate={setCurrentScreen} 
          calculators={calculators} 
          language={language} 
          t={t} 
          user={user} 
          onCategoryChange={setActiveHandbookCategoryId}
          marketPrices={marketPrices}
          setToast={setToast}
          videos={videos}
          isAdmin={isAdmin}
          userRole={userRole}
          profile={profile}
          cropSchedules={cropSchedules}
          onUpdateProfile={handleUpdateProfile}
        />
      );
      case 'crops': return <CropsScreen categories={handbookData} language={language} t={t} onNavigate={setCurrentScreen} />;
      case 'inputs': return <InputsScreen categories={handbookData} language={language} t={t} onNavigate={setCurrentScreen} />;
      case 'handbook': return (
        <HandbookScreen 
          onNavigate={setCurrentScreen} 
          categories={handbookData} 
          language={language} 
          t={t} 
          initialCategoryId={activeHandbookCategoryId}
          onCategoryChange={setActiveHandbookCategoryId}
          videos={videos}
        />
      );
      case 'videos': return <VideosScreen videos={videos} t={t} />;
      case 'scanner': return (
        <ScannerScreen 
          onBack={() => setCurrentScreen('home')} 
          language={language} 
          setToast={setToast}
          onNavigateToFarmer={(farmerId) => {
            setScannedFarmerId(farmerId);
            setCurrentScreen('marketplace');
          }}
        />
      );
      case 'advisor': return <AIAdvisor user={user} language={language} videos={videos} />;
      case 'forum': return <Forum user={user} language={language} videos={videos} />;
      case 'marketplace': return (
        <MarketplaceScreen 
          language={language} 
          t={t} 
          config={marketplaceConfig} 
          isAdmin={isAdmin} 
          userRole={userRole} 
          initialShowAdd={marketplaceInitialAdd}
          onResetInitialAdd={() => setMarketplaceInitialAdd(false)}
          initialFarmerId={scannedFarmerId}
          onClearInitialFarmerId={() => setScannedFarmerId(null)}
        />
      );
      case 'myProducts': return (
        <MarketplaceScreen 
          language={language} 
          t={t} 
          config={marketplaceConfig} 
          isAdmin={isAdmin} 
          userRole={userRole} 
          showMyProductsOnly={true}
          initialFarmerId={scannedFarmerId}
          onClearInitialFarmerId={() => setScannedFarmerId(null)}
        />
      );
      case 'marketPrices': return <MarketPricesScreen t={t} onBack={() => setCurrentScreen('home')} localPrices={marketPrices} />;
      case 'cropCalendar': return (
        <CropCalendar 
          language={language} 
          t={t} 
          user={user} 
          profile={profile} 
          onUpdateProfile={handleUpdateProfile} 
        />
      );
      case 'apcnfTraceability': return <TraceabilityScreen onBack={() => setCurrentScreen('home')} title={t.apcnfTraceability} t={t} />;
      case 'plantDoctor': return <PlantDoctorScreen user={user} language={language} onBack={() => setCurrentScreen('home')} />;
      case 'expertConnect': return <ExpertConnectScreen user={user} language={language} isExpert={isExpert} isAdmin={isAdmin} />;
      case 'settings': return <SettingsScreen language={language} setLanguage={setLanguage} t={t} onBack={() => setCurrentScreen('home')} />;
      case 'privacy': return <PageScreen content={pages.find(p => p.id === 'privacy') || { id: 'privacy', title: t.privacyPolicy, content: '' }} onBack={() => setCurrentScreen('home')} t={t} language={language} />;
      case 'terms': return <PageScreen content={pages.find(p => p.id === 'terms') || { id: 'terms', title: t.termsAndConditions, content: '' }} onBack={() => setCurrentScreen('home')} t={t} language={language} />;
      case 'aboutUs': return <PageScreen content={pages.find(p => p.id === 'about-us') || { id: 'about-us', title: t.aboutUs, content: '' }} onBack={() => setCurrentScreen('home')} t={t} language={language} />;
      case 'admin': 
        return (
          <AdminScreen 
            onNavigate={setCurrentScreen}
            isAdmin={isAdmin}
            isExpert={isExpert}
            user={user}
            onLogout={() => { setUser(null); setCurrentScreen('home'); }}
            categories={handbookData}
            setCategories={setHandbookData}
            calculators={calculators}
            setCalculators={setCalculators as any}
            videos={videos}
            setVideos={setVideos}
            pages={pages}
            marketPrices={marketPrices}
            setMarketPrices={setMarketPrices}
            menuItems={menuItems}
            setMenuItems={setMenuItems}
            marketplaceConfig={marketplaceConfig}
            branding={branding}
            admobConfig={admobConfig}
            setAdmobConfig={setAdmobConfig}
            isAdMobSaving={isAdMobSaving}
            setIsAdMobSaving={setIsAdMobSaving}
            language={language}
            t={t}
            setToast={setToast}
            setConfirmDialog={setConfirmDialog}
          />
        );
      default: return (
        <HomeScreen 
          onNavigate={setCurrentScreen} 
          calculators={calculators} 
          language={language} 
          t={t} 
          user={user} 
          onCategoryChange={setActiveHandbookCategoryId}
          marketPrices={marketPrices}
          setToast={setToast}
          videos={videos}
          isAdmin={isAdmin}
          userRole={userRole}
          profile={profile}
          cropSchedules={cropSchedules}
          onUpdateProfile={handleUpdateProfile}
        />
      );
    }
  };

  return (
    <ErrorBoundary>
      <div className="h-screen flex flex-col bg-[#f8f9fa] overflow-hidden font-sans w-full lg:w-[70%] lg:mx-auto lg:shadow-2xl border-x border-black/5">
        {/* Header */}
        {currentScreen !== 'apcnfTraceability' && (
          <header className="bg-[#1b5e20] px-4 py-3 flex items-center justify-between sticky top-0 z-50 text-white shadow-md">
            <div className="flex items-center gap-3">
              {currentScreen !== 'home' && (
                <button onClick={() => setCurrentScreen('home')} className="p-1 hover:bg-white/10 rounded-full transition-colors">
                  <ChevronLeft size={24} />
                </button>
              )}
              <div className="flex items-center gap-2">
                {branding.logoUrl && (
                  <img src={branding.logoUrl} className="w-10 h-10 rounded-full border border-white/20 object-cover shadow-sm" referrerPolicy="no-referrer" />
                )}
                <h1 className="text-xl font-bold tracking-tight">{t.appName}</h1>
              </div>
              {!isOnline && (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-red-500/20 text-red-200 text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 border border-red-500/30"
                >
                  <CloudOff size={10} /> {t.offline || 'Offline'}
                </motion.div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setIsMenuOpen(true)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
                title="Menu"
              >
                <Menu size={24} />
              </button>
            </div>
          </header>
        )}

      {/* Sidebar Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMenuOpen(false)}
              className="fixed inset-0 bg-black/50 z-[60]"
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed top-0 right-0 h-full w-72 bg-white z-[70] shadow-2xl flex flex-col"
            >
              <div className="p-6 border-b flex items-center justify-between bg-[#1b7d36] text-white">
                <div className="flex items-center gap-3">
                  {branding.logoUrl ? (
                    <img src={branding.logoUrl} className="w-12 h-12 rounded-xl border border-white/20 object-cover shadow-sm" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="p-2 bg-white/10 rounded-xl">
                      <Leaf size={24} />
                    </div>
                  )}
                  <div className="flex flex-col">
                    <h2 className="font-bold text-lg">{t.menu || 'Menu'}</h2>
                    {user && <span className="text-xs opacity-80 truncate max-w-[150px]">{user.email}</span>}
                  </div>
                </div>
                <button onClick={() => setIsMenuOpen(false)} className="p-1 hover:bg-white/10 rounded-full">
                  <X size={24} />
                </button>
              </div>
              
              <div className="flex-1 py-4 overflow-y-auto">
                {user && (
                  <button 
                    onClick={() => handleOpenProfileModal()}
                    className="w-full px-6 py-4 flex items-center gap-4 hover:bg-emerald-50 text-emerald-700 transition-colors border-b border-stone-100"
                  >
                    <UserCircle size={20} />
                    <span className="font-bold">{language === 'te' ? 'ప్రొఫైల్ సవరించండి' : 'Edit Profile'}</span>
                  </button>
                )}
                {(isAdmin || userRole === 'farmer' || userRole === 'aggregator') && (
                  <button 
                    onClick={() => { setCurrentScreen('myProducts'); setIsMenuOpen(false); }}
                    className="w-full px-6 py-4 flex items-center gap-4 hover:bg-emerald-50 text-[#1b7d36] transition-colors border-b border-stone-100"
                  >
                    <ShoppingBag size={20} />
                    <span className="font-bold">{language === 'te' ? 'నా ఉత్పత్తులు' : 'My Products'}</span>
                  </button>
                )}
                {(userRole === 'farmer' || userRole === 'aggregator') && (
                  <button 
                    onClick={() => { handleOpenProfileModal(true); setIsMenuOpen(false); }}
                    className="w-full px-6 py-4 flex items-center gap-4 hover:bg-emerald-50 text-emerald-600 transition-colors border-b border-stone-100"
                  >
                    <QrCode size={20} />
                    <span className="font-bold">{language === 'te' ? 'నా క్యూఆర్ కోడ్' : 'My QR code'}</span>
                  </button>
                )}
                {menuItems.filter(item => {
                  const isAdminUser = isAdmin || user?.email === 'prakruthimithra2026@gmail.com';
                  return item.visible && (!item.isAdminOnly || isAdminUser || (item.id === 'admin' && isExpert));
                }).map((item) => (
                  <button 
                    key={item.id}
                    onClick={() => { setCurrentScreen(item.screen); setIsMenuOpen(false); }}
                    className="w-full px-6 py-4 flex items-center gap-4 hover:bg-stone-50 text-stone-700 transition-colors"
                  >
                    {getIcon(item.iconName)}
                    <span className="font-medium">{t[item.labelKey] || item.labelKey}</span>
                  </button>
                ))}
              </div>
 
              <div className="p-4 border-t bg-stone-50">
                {user ? (
                  <button 
                    onClick={() => { handleLogout(); setIsMenuOpen(false); }}
                    className="w-full px-6 py-4 flex items-center gap-4 hover:bg-rose-50 text-rose-600 rounded-2xl transition-colors"
                  >
                    <LogOut size={20} />
                    <span className="font-bold">{t.logout}</span>
                  </button>
                ) : (
                  <button 
                    onClick={() => { setCurrentScreen('advisor'); setIsMenuOpen(false); }}
                    className="w-full px-6 py-4 flex items-center gap-4 hover:bg-emerald-50 text-emerald-600 rounded-2xl transition-colors"
                  >
                    <UserCircle size={20} />
                    <span className="font-bold">{t.login || 'Login'}</span>
                  </button>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentScreen}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="h-full"
          >
            {renderScreen()}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Modals */}
      <AnimatePresence>
        {isProfileModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsProfileModalOpen(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-[40px] shadow-2xl overflow-hidden"
            >
              <div className="p-6 border-b border-stone-100 flex items-center justify-between bg-emerald-600 text-white">
                <h3 className="text-xl font-bold">
                  {showQrOnly 
                    ? (language === 'te' ? 'నా క్యూఆర్ కోడ్' : 'My QR Code')
                    : (language === 'te' ? 'ప్రొఫైల్ సవరించండి' : 'Edit Profile')}
                </h3>
                <button onClick={() => setIsProfileModalOpen(false)} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                  <X size={24} />
                </button>
              </div>

              {showQrOnly ? (
                <div className="p-10 flex flex-col items-center justify-center space-y-8 bg-stone-50">
                  <div className="bg-white p-8 rounded-[40px] shadow-xl border border-emerald-100 relative">
                    <QRCodeSVG 
                      value={`${window.location.origin}${window.location.pathname}?farmerId=${user?.uid}`}
                      size={200}
                      level="H"
                      includeMargin={true}
                    />
                    <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-emerald-600 text-white px-4 py-1 rounded-full text-xs font-bold shadow-lg">
                      {user?.displayName || 'Farmer'}
                    </div>
                  </div>
                  
                  <div className="text-center space-y-2">
                    <p className="text-stone-600 font-medium">
                      {language === 'te' ? 'మీ ప్రొఫైల్‌ను పంచుకోవడానికి ఈ కోడ్‌ను స్కాన్ చేయండి' : 'Scan this code to share your profile'}
                    </p>
                    <p className="text-stone-400 text-xs">
                      {language === 'te' ? 'ID: ' + user?.uid : 'UID: ' + user?.uid}
                    </p>
                  </div>

                  <button
                    onClick={() => {
                      const svg = document.querySelector('svg');
                      if (svg) {
                        const svgData = new XMLSerializer().serializeToString(svg);
                        const canvas = document.createElement("canvas");
                        const ctx = canvas.getContext("2d");
                        const img = new Image();
                        img.onload = () => {
                          canvas.width = img.width + 40;
                          canvas.height = img.height + 40;
                          if (ctx) {
                            ctx.fillStyle = "white";
                            ctx.fillRect(0, 0, canvas.width, canvas.height);
                            ctx.drawImage(img, 20, 20);
                            const pngUrl = canvas.toDataURL("image/png");
                            const downloadLink = document.createElement("a");
                            downloadLink.href = pngUrl;
                            downloadLink.download = `farmer_qr_${user?.uid}.png`;
                            document.body.appendChild(downloadLink);
                            downloadLink.click();
                            document.body.removeChild(downloadLink);
                          }
                        };
                        img.src = "data:image/svg+xml;base64," + btoa(svgData);
                      }
                    }}
                    className="w-full py-4 bg-emerald-600 text-white rounded-2xl font-bold shadow-lg shadow-emerald-600/20 hover:bg-emerald-700 transition-all flex items-center justify-center gap-2"
                  >
                    <Download size={20} />
                    {language === 'te' ? 'క్యూఆర్ కోడ్‌ను డౌన్‌లోడ్ చేయండి' : 'Download QR Code'}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSaveProfileForm} className="p-8 space-y-5 overflow-y-auto max-h-[70vh]">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-stone-700 ml-1">{t.fullName}</label>
                  <input
                    type="text"
                    required
                    value={editingProfile.displayName}
                    onChange={(e) => setEditingProfile({ ...editingProfile, displayName: e.target.value })}
                    className="w-full px-5 py-3 bg-stone-50 border border-stone-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-medium"
                    placeholder={t.enterFullName}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-stone-700 ml-1">{t.phoneNumber}</label>
                  <input
                    type="tel"
                    required
                    value={editingProfile.phone}
                    onChange={(e) => setEditingProfile({ ...editingProfile, phone: e.target.value })}
                    className="w-full px-5 py-3 bg-stone-50 border border-stone-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-medium"
                    placeholder={t.enterPhoneNumber}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-stone-700 ml-1">{t.location}</label>
                  <input
                    type="text"
                    required
                    value={editingProfile.location}
                    onChange={(e) => setEditingProfile({ ...editingProfile, location: e.target.value })}
                    className="w-full px-5 py-3 bg-stone-50 border border-stone-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-medium"
                    placeholder={t.enterVillageName}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-stone-700 ml-1">{language === 'te' ? 'ప్రొఫైల్ చిత్రం URL' : 'Profile Image URL'}</label>
                  <input
                    type="url"
                    value={editingProfile.photoURL}
                    onChange={(e) => setEditingProfile({ ...editingProfile, photoURL: e.target.value })}
                    className="w-full px-5 py-3 bg-stone-50 border border-stone-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-medium"
                    placeholder="https://example.com/image.jpg"
                  />
                  {editingProfile.photoURL && (
                    <div className="mt-2 flex justify-center">
                      <img 
                        src={editingProfile.photoURL} 
                        alt="Preview" 
                        className="w-16 h-16 rounded-full object-cover border-2 border-emerald-500 shadow-md"
                        onError={(e) => (e.currentTarget.src = 'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y')}
                        referrerPolicy="no-referrer"
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-stone-700 ml-1">{t.userRole}</label>
                  <select
                    value={editingProfile.role}
                    onChange={(e) => setEditingProfile({ ...editingProfile, role: e.target.value })}
                    className="w-full px-5 py-3 bg-stone-50 border border-stone-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-medium"
                  >
                    <option value="">{t.roleRequired}</option>
                    <option value="farmer">{t.farmer}</option>
                    <option value="aggregator">{t.aggregator}</option>
                    <option value="buyer">{t.buyer}</option>
                    {isAdmin && <option value="admin">Admin</option>}
                  </select>
                </div>

                {editingProfile.role === 'farmer' && (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-stone-700 ml-1">{t.farmerId}</label>
                      <input
                        type="text"
                        value={editingProfile.farmerId}
                        onChange={(e) => setEditingProfile({ ...editingProfile, farmerId: e.target.value })}
                        className="w-full px-5 py-3 bg-stone-50 border border-stone-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-medium"
                        placeholder={t.enterFarmerId}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-bold text-stone-700 ml-1">{t.farmingMethod}</label>
                      <select
                        value={editingProfile.farmingMethods}
                        onChange={(e) => setEditingProfile({ ...editingProfile, farmingMethods: e.target.value })}
                        className="w-full px-5 py-3 bg-stone-50 border border-stone-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-medium"
                      >
                        <option value="">{language === 'te' ? 'పద్ధతిని ఎంచుకోండి' : 'Select Method'}</option>
                        {marketplaceConfig.farmingMethods.map(m => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-bold text-stone-700 ml-1">{language === 'te' ? 'ధృవీకరణలు' : 'Certifications'}</label>
                      <div className="flex flex-wrap gap-2 p-3 bg-stone-50 border border-stone-200 rounded-2xl">
                        {marketplaceConfig.availableCertifications.map(cert => (
                          <button
                            key={cert}
                            type="button"
                            onClick={() => {
                              const newCerts = editingProfile.certifications.includes(cert)
                                ? editingProfile.certifications.filter(c => c !== cert)
                                : [...editingProfile.certifications, cert];
                              setEditingProfile({ ...editingProfile, certifications: newCerts });
                            }}
                            className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                              editingProfile.certifications.includes(cert)
                                ? 'bg-emerald-600 text-white shadow-md'
                                : 'bg-white text-stone-500 border border-stone-100'
                            }`}
                          >
                            {cert}
                          </button>
                        ))}
                      </div>
                    </div>




                    <div className="pt-4 border-t border-stone-100 space-y-4">
                      <h4 className="text-md font-bold text-emerald-800 flex items-center gap-2">
                        <Leaf size={18} />
                        {t.cultivationPractices}
                      </h4>
                      
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-stone-500 ml-1">{t.practicesFollowed}</label>
                        <textarea
                          value={editingProfile.cultivationPractices.description}
                          onChange={(e) => setEditingProfile({ 
                            ...editingProfile, 
                            cultivationPractices: { ...editingProfile.cultivationPractices, description: e.target.value }
                          })}
                          className="w-full px-5 py-3 bg-stone-50 border border-stone-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-medium min-h-[100px]"
                          placeholder={t.jeevamrutham + ", " + t.beejamrutham + "..."}
                        />
                      </div>



                    </div>

                    <div className="pt-4 border-t border-stone-100 space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-md font-bold text-emerald-800 flex items-center gap-2">
                          <Calendar size={18} />
                          {t.cropDetails}
                        </h4>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingProfile({
                              ...editingProfile,
                              crops: [...editingProfile.crops, { id: Date.now().toString(), name: '', harvestTime: '', area: '' }]
                            });
                          }}
                          className="p-1.5 bg-emerald-50 text-emerald-600 rounded-lg hover:bg-emerald-100 transition-colors"
                        >
                          <Plus size={18} />
                        </button>
                      </div>

                      <div className="space-y-3">
                        {editingProfile.crops.map((crop, index) => (
                          <div key={crop.id} className="p-4 bg-stone-50 rounded-2xl border border-stone-100 space-y-3 relative">
                            <button
                              type="button"
                              onClick={() => {
                                const newCrops = [...editingProfile.crops];
                                newCrops.splice(index, 1);
                                setEditingProfile({ ...editingProfile, crops: newCrops });
                              }}
                              className="absolute top-2 right-2 p-1 text-stone-400 hover:text-red-500"
                            >
                              <Trash2 size={16} />
                            </button>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-1">
                                <label className="text-[10px] font-bold text-stone-500 ml-1 uppercase">{t.cropName}</label>
                                <input
                                  type="text"
                                  value={crop.name}
                                  onChange={(e) => {
                                    const newCrops = [...editingProfile.crops];
                                    newCrops[index].name = e.target.value;
                                    setEditingProfile({ ...editingProfile, crops: newCrops });
                                  }}
                                  className="w-full px-3 py-2 bg-white border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                                />
                              </div>
                              <div className="space-y-1">
                                <label className="text-[10px] font-bold text-stone-500 ml-1 uppercase">{t.harvestTime}</label>
                                <input
                                  type="text"
                                  value={crop.harvestTime}
                                  onChange={(e) => {
                                    const newCrops = [...editingProfile.crops];
                                    newCrops[index].harvestTime = e.target.value;
                                    setEditingProfile({ ...editingProfile, crops: newCrops });
                                  }}
                                  className="w-full px-3 py-2 bg-white border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                                  placeholder="Nov 2024"
                                />
                              </div>
                              <div className="space-y-1">
                                <label className="text-[10px] font-bold text-stone-500 ml-1 uppercase">{t.cropArea}</label>
                                <input
                                  type="text"
                                  value={crop.area || ''}
                                  onChange={(e) => {
                                    const newCrops = [...editingProfile.crops];
                                    newCrops[index].area = e.target.value;
                                    setEditingProfile({ ...editingProfile, crops: newCrops });
                                  }}
                                  className="w-full px-3 py-2 bg-white border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                                  placeholder="1 Acre"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="pt-4 border-t border-stone-100 space-y-4">
                      <h4 className="text-md font-bold text-emerald-800 flex items-center gap-2">
                        <MapPin size={18} />
                        {t.cultivationArea}
                      </h4>
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-stone-500 ml-1">{t.areaDetails}</label>
                        <input
                          type="text"
                          value={editingProfile.cultivationArea.description}
                          onChange={(e) => setEditingProfile({
                            ...editingProfile,
                            cultivationArea: { ...editingProfile.cultivationArea, description: e.target.value }
                          })}
                          className="w-full px-5 py-3 bg-stone-50 border border-stone-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-medium text-sm"
                          placeholder={t.enterVillageName}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="text-[10px] font-bold text-stone-500 ml-1 uppercase">{t.latitude}</label>
                          <input
                            type="number"
                            step="any"
                            value={editingProfile.cultivationArea.lat || ''}
                            onChange={(e) => setEditingProfile({
                              ...editingProfile,
                              cultivationArea: { ...editingProfile.cultivationArea, lat: parseFloat(e.target.value) || 0 }
                            })}
                            className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded-xl text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[10px] font-bold text-stone-500 ml-1 uppercase">{t.longitude}</label>
                          <input
                            type="number"
                            step="any"
                            value={editingProfile.cultivationArea.lng || ''}
                            onChange={(e) => setEditingProfile({
                              ...editingProfile,
                              cultivationArea: { ...editingProfile.cultivationArea, lng: parseFloat(e.target.value) || 0 }
                            })}
                            className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded-xl text-sm"
                          />
                        </div>
                      </div>
                      <CultivationMap 
                        lat={editingProfile.cultivationArea.lat}
                        lng={editingProfile.cultivationArea.lng}
                        onLocationChange={(lat, lng) => setEditingProfile({
                          ...editingProfile,
                          cultivationArea: { ...editingProfile.cultivationArea, lat, lng }
                        })}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (navigator.geolocation) {
                            navigator.geolocation.getCurrentPosition(
                              (pos) => {
                                setEditingProfile({
                                  ...editingProfile,
                                  cultivationArea: {
                                    ...editingProfile.cultivationArea,
                                    lat: pos.coords.latitude,
                                    lng: pos.coords.longitude
                                  }
                                });
                                setToast({ message: language === 'te' ? 'స్థానం విజయవంతంగా పొందబడింది' : 'Location retrieved successfully', type: 'success' });
                              },
                              (err) => {
                                console.error("Geolocation error:", err);
                                setToast({ 
                                  message: language === 'te' ? 'స్థానాన్ని పొందడంలో విఫలమైంది. దయచేసి స్థాన అనుమతులను తనిఖీ చేయండి.' : 'Failed to get location. Please check your location permissions.', 
                                  type: 'error' 
                                });
                              }
                            );
                          } else {
                            setToast({ 
                              message: language === 'te' ? 'ఈ బ్రౌజర్‌లో భౌగోళిక స్థానం మద్దతు లేదు.' : 'Geolocation is not supported by this browser.', 
                              type: 'error' 
                            });
                          }
                        }}
                        className="w-full py-2 bg-emerald-50 text-emerald-700 rounded-xl font-bold text-xs flex items-center justify-center gap-2 border border-emerald-100"
                      >
                        <MapPin size={14} />
                        {language === 'te' ? 'ప్రస్తుత స్థానాన్ని పొందండి' : 'Get Current Location'}
                      </button>
                    </div>
                  </>
                )}

                <div className="pt-4 border-t border-stone-100">
                  <button
                    type="button"
                    onClick={() => setShowPasswordChange(!showPasswordChange)}
                    className="flex items-center gap-2 text-emerald-600 font-bold hover:text-emerald-700 transition-colors"
                  >
                    {showPasswordChange ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    {t.changePassword}
                  </button>

                  <AnimatePresence>
                    {showPasswordChange && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="pt-4 space-y-4">
                          <div className="space-y-2">
                            <label className="text-xs font-bold text-stone-500 ml-1">{t.currentPassword}</label>
                            <input
                              type="password"
                              value={passwordFields.currentPassword}
                              onChange={(e) => setPasswordFields({ ...passwordFields, currentPassword: e.target.value })}
                              className="w-full px-5 py-3 bg-stone-50 border border-stone-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-medium"
                              placeholder="••••••••"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="text-xs font-bold text-stone-500 ml-1">{t.newPassword}</label>
                            <input
                              type="password"
                              value={passwordFields.newPassword}
                              onChange={(e) => setPasswordFields({ ...passwordFields, newPassword: e.target.value })}
                              className="w-full px-5 py-3 bg-stone-50 border border-stone-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-medium"
                              placeholder="••••••••"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="text-xs font-bold text-stone-500 ml-1">{t.confirmPassword}</label>
                            <input
                              type="password"
                              value={passwordFields.confirmPassword}
                              onChange={(e) => setPasswordFields({ ...passwordFields, confirmPassword: e.target.value })}
                              className="w-full px-5 py-3 bg-stone-50 border border-stone-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-medium"
                              placeholder="••••••••"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={handleChangePassword}
                            disabled={isChangingPassword || !passwordFields.currentPassword || !passwordFields.newPassword}
                            className="w-full py-3 bg-stone-800 text-white rounded-2xl font-bold hover:bg-stone-900 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            {isChangingPassword ? <Loader2 size={18} className="animate-spin" /> : <ShieldCheck size={18} />}
                            {t.save}
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <button
                  type="submit"
                  disabled={isSavingProfile}
                  className="w-full py-4 bg-emerald-600 text-white rounded-2xl font-bold shadow-lg shadow-emerald-600/20 hover:bg-emerald-700 transition-all disabled:opacity-50 disabled:shadow-none active:scale-[0.98] flex items-center justify-center gap-2"
                >
                  {isSavingProfile ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      {language === 'te' ? 'నవీకరిస్తోంది...' : 'Updating...'}
                    </>
                  ) : (
                    <>
                      <ShieldCheck size={20} />
                      {language === 'te' ? 'నవీకరించు' : 'Update Profile'}
                    </>
                  )}
                </button>
              </form>
            )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Bottom Nav */}
      <footer className="bg-white border-t border-black/5 px-4 py-2 flex items-center justify-around shadow-[0_-2px_10px_rgba(0,0,0,0.05)] sticky bottom-0 z-50">
        <BottomNavButton 
          icon={<Home size={20} />} 
          label={t.home} 
          active={currentScreen === 'home'} 
          onClick={() => setCurrentScreen('home')} 
        />
        <BottomNavButton 
          icon={<ShoppingBag size={20} />} 
          label={t.marketplace} 
          active={currentScreen === 'marketplace'} 
          onClick={() => setCurrentScreen('marketplace')} 
        />
        <BottomNavButton 
          icon={<QrCode size={20} />} 
          label={t.scanner} 
          active={currentScreen === 'scanner'} 
          onClick={() => setCurrentScreen('scanner')} 
        />
        <BottomNavButton 
          icon={<Video size={20} />} 
          label={t.videos} 
          active={currentScreen === 'videos'} 
          onClick={() => setCurrentScreen('videos')} 
        />
        <BottomNavButton 
          icon={<Book size={20} />} 
          label={t.handbook} 
          active={currentScreen === 'handbook'} 
          onClick={() => {
            setActiveHandbookCategoryId(null);
            setCurrentScreen('handbook');
          }} 
        />
      </footer>

      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}

      {confirmDialog && (
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
          type={confirmDialog.type}
        />
      )}

      {/* Global AdMob components */}
      <BannerAd 
        autoShow={true} 
        adId={admobConfig.bannerId} 
        isTesting={admobConfig.isTesting} 
      />
      
      {/* Spacer for Banner Ad to prevent overlap with content if needed */}
      {Capacitor.isNativePlatform() && <div className="h-14 w-full" />}
    </div>
    </ErrorBoundary>
  );
}

function BottomNavButton({ icon, label, active, onClick }: { icon: React.ReactNode, label: string, active: boolean, onClick: () => void }) {
  return (
    <button 
      onClick={onClick}
      className={`flex flex-col items-center gap-1 transition-all ${active ? 'text-[#1b7d36]' : 'text-stone-400'}`}
    >
      <div className={`${active ? 'scale-110' : ''} transition-transform`}>
        {icon}
      </div>
      <span className="text-[10px] font-medium">{label}</span>
    </button>
  );
}

function SettingsScreen({ language, setLanguage, t, onBack }: { language: Language, setLanguage: (l: Language) => void, t: any, onBack: () => void }) {
  return (
    <div className="flex flex-col h-full bg-[#f8f9fa]">
      <div className="p-4 border-b flex items-center gap-4 sticky top-0 bg-white z-10">
        <button onClick={onBack} className="p-2 hover:bg-stone-100 rounded-full transition-colors text-stone-600">
          <ChevronLeft size={24} />
        </button>
        <h2 className="text-xl font-bold text-stone-800">{t.settings}</h2>
      </div>
      
      <div className="flex-1 overflow-y-auto p-6 space-y-8">
        <section className="space-y-4">
          <h3 className="text-sm font-bold text-stone-400 uppercase tracking-wider px-2">{t.language}</h3>
          <div className="bg-white rounded-[32px] border border-black/5 overflow-hidden shadow-sm">
            <button 
              onClick={() => setLanguage('en')}
              className={`w-full flex items-center justify-between p-5 hover:bg-stone-50 transition-colors border-b border-stone-50 ${language === 'en' ? 'bg-emerald-50/50' : ''}`}
            >
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-2xl flex items-center justify-center font-bold ${language === 'en' ? 'bg-[#1b7d36] text-white' : 'bg-stone-100 text-stone-500'}`}>
                  EN
                </div>
                <span className={`font-bold ${language === 'en' ? 'text-[#1b7d36]' : 'text-stone-700'}`}>{t.english}</span>
              </div>
              {language === 'en' && <Check size={20} className="text-[#1b7d36]" />}
            </button>

            <button 
              onClick={() => setLanguage('te')}
              className={`w-full flex items-center justify-between p-5 hover:bg-stone-50 transition-colors border-b border-stone-50 ${language === 'te' ? 'bg-emerald-50/50' : ''}`}
            >
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-2xl flex items-center justify-center font-bold ${language === 'te' ? 'bg-[#1b7d36] text-white' : 'bg-stone-100 text-stone-500'}`}>
                  తె
                </div>
                <span className={`font-bold ${language === 'te' ? 'text-[#1b7d36]' : 'text-stone-700'}`}>{t.telugu}</span>
              </div>
              {language === 'te' && <Check size={20} className="text-[#1b7d36]" />}
            </button>

            <button 
              onClick={() => setLanguage('hi')}
              className={`w-full flex items-center justify-between p-5 hover:bg-stone-50 transition-colors ${language === 'hi' ? 'bg-emerald-50/50' : ''}`}
            >
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-2xl flex items-center justify-center font-bold ${language === 'hi' ? 'bg-[#1b7d36] text-white' : 'bg-stone-100 text-stone-500'}`}>
                  हि
                </div>
                <span className={`font-bold ${language === 'hi' ? 'text-[#1b7d36]' : 'text-stone-700'}`}>{t.hindi}</span>
              </div>
              {language === 'hi' && <Check size={20} className="text-[#1b7d36]" />}
            </button>
          </div>
        </section>

        <section className="space-y-4">
          <h3 className="text-sm font-bold text-stone-400 uppercase tracking-wider px-2">App Info</h3>
          <div className="bg-white rounded-[32px] border border-black/5 p-6 shadow-sm space-y-4">
            <div className="flex justify-between items-center text-sm">
              <span className="text-stone-500">Version</span>
              <span className="font-bold text-stone-800">1.0.0</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-stone-500">Developer</span>
              <span className="font-bold text-stone-800">Prakruthi Mithra Team</span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function PageScreen({ content, onBack, t, language }: { content: PageContent, onBack: () => void, t: any, language: Language }) {
  const displayTitle = language === 'te' ? content.title_te : language === 'hi' ? content.title_hi : content.title_en;
  const displayContent = language === 'te' ? content.content_te : language === 'hi' ? content.content_hi : content.content_en;

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="p-4 border-b flex items-center gap-4 sticky top-0 bg-white z-10">
        <button onClick={onBack} className="p-2 hover:bg-stone-100 rounded-full transition-colors text-stone-600">
          <ChevronLeft size={24} />
        </button>
        <h2 className="text-xl font-bold text-stone-800">{displayTitle || content.title}</h2>
      </div>
      <div className="flex-1 overflow-y-auto p-6">
        <div className="prose prose-stone max-w-none markdown-body text-justify">
          <ReactMarkdown>{displayContent || content.content || `*${t.noContentYet}*`}</ReactMarkdown>
        </div>
      </div>
    </div>
  );
}

function HomeScreen({ 
  onNavigate, 
  calculators, 
  language, 
  t, 
  user, 
  onCategoryChange, 
  marketPrices, 
  setToast, 
  videos, 
  isAdmin, 
  userRole,
  profile,
  cropSchedules,
  onUpdateProfile
}: { 
  onNavigate: (s: Screen) => void, 
  calculators: Calculator[], 
  language: Language, 
  t: any, 
  user: User | null,
  onCategoryChange?: (id: string | null) => void,
  marketPrices: MarketPrice[],
  setToast: (toast: { message: string, type: 'success' | 'error' | 'info' } | null) => void,
  videos: VideoItem[],
  isAdmin: boolean,
  userRole: string,
  profile: UserProfile | null,
  cropSchedules: CropSchedule[],
  onUpdateProfile: (updates: Partial<UserProfile>) => Promise<void>
}) {
  const [acres, setAcres] = useState(1);
  const [weather, setWeather] = useState<any>(null);
  const [forecast, setForecast] = useState<any[]>([]);
  const [showForecast, setShowForecast] = useState(false);
  const [alerts, setAlerts] = useState<string[]>([]);
  const [loadingWeather, setLoadingWeather] = useState(true);
  const [locationName, setLocationName] = useState<string>('');
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const [locationSearch, setLocationSearch] = useState('');
  const [showLocationSearch, setShowLocationSearch] = useState(false);
  const [showAddMarketPrice, setShowAddMarketPrice] = useState(false);
  const [hasTasksToday, setHasTasksToday] = useState(false);


  useEffect(() => {
    if (!user) return;
    const userCropsQuery = query(
      collection(db, 'user_crops'),
      where('userId', '==', user.uid)
    );
    const unsubCrops = onSnapshot(userCropsQuery, (snapshot) => {
      setHasTasksToday(!snapshot.empty);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'user_crops');
    });
    return () => unsubCrops();
  }, [user]);

  const handleAddMarketPrice = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user) {
      setToast({ message: t.loginToContinue || "Please login to continue", type: 'info' });
      return;
    }
    const formData = new FormData(e.currentTarget);
    const newPrice = {
      cropName: formData.get('cropName') as string,
      price: parseFloat(formData.get('price') as string),
      trend: formData.get('trend') as 'up' | 'down' | 'stable',
      lastUpdated: new Date(),
      authorId: user.uid
    };

    try {
      await setDoc(doc(db, 'market_prices', Date.now().toString()), newPrice);
      setShowAddMarketPrice(false);
      setToast({ message: t.listingCreated || "Price added successfully!", type: 'success' });
    } catch (error) {
      console.error("Error adding market price", error);
      handleFirestoreError(error, OperationType.WRITE, `market_prices/${Date.now().toString()}`);
      setToast({ message: "Failed to add market price. Please try again.", type: 'error' });
    }
  };

  const isMounted = useRef(true);

  const fetchWeather = async (lat: number, lon: number) => {
    if (!isMounted.current) return;
    setLoadingWeather(true);
    
    // 1. Fetch Weather Data
    try {
      const weatherResponse = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,apparent_temperature,weather_code,wind_speed_10m&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max,uv_index_max&timezone=auto&forecast_days=10`,
        { mode: 'cors', cache: 'default' }
      );
      
      if (!weatherResponse.ok) {
        throw new Error(`Weather API error: ${weatherResponse.status}`);
      }
      
      const weatherData = await weatherResponse.json();

      if (weatherData && weatherData.current) {
        const daily = weatherData.daily;
        const current = weatherData.current;
        const code = current.weather_code;
        const temp = current.temperature_2m;
        const wind = current.wind_speed_10m;
        const todayRainChance = daily?.precipitation_probability_max?.[0] || 0;

        setWeather({
          ...current,
          uv_index: daily?.uv_index_max?.[0] || 0,
          summary: getWeatherText(code),
          lastUpdated: new Date()
        });

        if (daily && daily.time) {
          const forecastData = daily.time.map((time: string, i: number) => {
            const [year, month, dayNum] = time.split('-').map(Number);
            return {
              date: new Date(year, month - 1, dayNum),
              code: daily.weather_code[i],
              max: daily.temperature_2m_max[i],
              min: daily.temperature_2m_min[i],
              rainChance: daily.precipitation_probability_max ? daily.precipitation_probability_max[i] : 0,
              uv: daily.uv_index_max ? daily.uv_index_max[i] : 0
            };
          });
          setForecast(forecastData);
        }

        // Generate Alerts
        const newAlerts: string[] = [];
        const humidity = current.relative_humidity_2m;

        // Storm & Rain Alerts
        if (code >= 95) {
          newAlerts.push(t.stormAlert);
        } else if (code >= 80 || code === 65 || code === 63) {
          newAlerts.push(t.heavyRainAlert);
        } else if (code >= 51 && code <= 61) {
          newAlerts.push(t.rainChanceAlert); // For drizzle and light/moderate rain
        } else if (todayRainChance > 50 && code < 51) {
          newAlerts.push(t.rainChanceAlert);
        }
        
        // Temperature Alerts
        if (temp > 42) newAlerts.push(t.heatWaveAlert);
        else if (temp > 35) newAlerts.push(t.highTempAlert);
        else if (temp < 10) newAlerts.push(t.frostAlert);

        // Wind Alerts
        if (wind > 30 && !newAlerts.includes(t.stormAlert)) {
          newAlerts.push(t.stormAlert);
        }

        // Humidity/Fungal Alerts
        if (humidity > 85) {
          newAlerts.push(t.fungalAlert);
        }

        // Visibility Alerts
        if (code === 45 || code === 48) {
          newAlerts.push(t.visibilityAlert);
        }

        setAlerts(newAlerts);
      }
    } catch (error) {
      console.warn('Weather fetch failed:', error);
    }

    // 2. Fetch Location Name (Reverse Geocoding)
    try {
      const geoResponse = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=${language}`
      );
      
      if (geoResponse.ok) {
        const geoData = await geoResponse.json();
        if (isMounted.current && geoData) {
          const city = geoData.city || geoData.locality || geoData.principalSubdivision || t.currentLocation;
          setLocationName(city);
        }
      } else {
        throw new Error('Geo API error');
      }
    } catch (error) {
      console.warn('Geo fetch failed:', error);
      if (isMounted.current) {
        setLocationName(t.currentLocation);
      }
    } finally {
      if (isMounted.current) setLoadingWeather(false);
    }
  };

  const fetchByIP = async () => {
    try {
      const ipResponse = await fetch('https://ipapi.co/json/');
      if (ipResponse.ok) {
        const ipData = await ipResponse.json();
        if (isMounted.current && ipData.latitude && ipData.longitude) {
          fetchWeather(ipData.latitude, ipData.longitude);
          return;
        }
      }
      throw new Error('IP lookup failed');
    } catch (error) {
      console.warn('IP fetch failed, falling back to default location:', error);
      if (isMounted.current) {
        // Fallback to Vijayawada
        fetchWeather(16.5062, 80.6480);
        setLocationName(t.vijayawada || 'Vijayawada');
      }
    }
  };

  const detectLocation = () => {
    if (navigator.geolocation) {
      setLoadingWeather(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => fetchWeather(pos.coords.latitude, pos.coords.longitude),
        () => fetchByIP()
      );
    } else {
      fetchByIP();
    }
  };

  useEffect(() => {
    isMounted.current = true;
    detectLocation();
    return () => { isMounted.current = false; };
  }, [language]);

  const handleLocationSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!locationSearch.trim()) return;
    
    setIsSearchingLocation(true);
    try {
      const response = await fetch(
        `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(locationSearch)}&count=1&language=${language === 'en' ? 'en' : 'en'}`
      );
      const data = await response.json();
      if (data.results && data.results.length > 0) {
        const result = data.results[0];
        setLocationSearch('');
        setShowLocationSearch(false);
        fetchWeather(result.latitude, result.longitude);
      } else {
        setToast({ message: t.locationNotFound, type: 'error' });
      }
    } catch (error) {
      setToast({ message: t.somethingWentWrong, type: 'error' });
    } finally {
      setIsSearchingLocation(false);
    }
  };

  const getWeatherIcon = (code: number, size = 24) => {
    if (code === 0) return <Sun className="text-amber-400" size={size} />;
    if (code >= 1 && code <= 2) return <CloudSun className="text-yellow-400" size={size} />;
    if (code === 3) return <Cloud className="text-stone-400" size={size} />;
    if (code === 45 || code === 48) return <CloudOff className="text-stone-200" size={size} />;
    if (code >= 51 && code <= 57) return <CloudDrizzle className="text-blue-300" size={size} />;
    if ((code >= 61 && code <= 63) || (code >= 80 && code <= 81)) return <CloudRain className="text-blue-400" size={size} />;
    if (code === 65 || code === 66 || code === 67 || code === 82) return <CloudRain className="text-blue-600" size={size} />;
    if (code >= 71 && code <= 77 || (code >= 85 && code <= 86)) return <Snowflake className="text-blue-100" size={size} />;
    if (code >= 95) return <CloudLightning className="text-purple-500" size={size} />;
    return <Cloud className="text-stone-400" size={size} />;
  };

  const getWeatherText = (code: number) => {
    if (code === undefined || code === null) return t.dataNotAvailable;
    if (code === 0) return t.sunny;
    if (code === 1) return t.mainlyClear;
    if (code === 2) return t.partlyCloudy;
    if (code === 3) return t.cloudy;
    if (code === 45 || code === 48) return t.fog;
    if (code >= 51 && code <= 57) return t.drizzle;
    if ((code >= 61 && code <= 63) || (code >= 80 && code <= 81)) return t.rain;
    if (code === 65 || code === 66 || code === 67 || code === 82) return t.heavyRain;
    if (code >= 71 && code <= 77 || (code >= 85 && code <= 86)) return t.snow;
    if (code >= 95) return t.thunderstorm;
    return t.cloudy;
  };

  const getUVText = (uv: number) => {
    if (uv <= 2) return t.lowUV;
    if (uv <= 5) return t.moderateUV;
    if (uv <= 7) return t.highUV;
    if (uv <= 10) return t.veryHighUV;
    return t.extremeUV;
  };

  const getWeeklyPractices = () => {
    if (!profile?.followedCrops || !cropSchedules.length) return [];
    
    const now = new Date();
    const practices: { practice: Practice, cropName: string, cropId: string }[] = [];

    profile.followedCrops.forEach(followed => {
      const schedule = cropSchedules.find(s => s.id === followed.cropId);
      if (!schedule || !schedule.practices) return;

      const sowing = new Date(followed.sowingDate);
      const diffTime = now.getTime() - sowing.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      const currentWeek = Math.floor(diffDays / 7);

      schedule.practices.forEach(p => {
        const practiceWeek = Math.floor(p.dayOffset / 7);
        const isCompleted = followed.completedPractices?.includes(`${p.id}_${practiceWeek}`);
        
        if (practiceWeek === currentWeek && !isCompleted && diffDays >= p.dayOffset) {
          const localizedCropName = (schedule[`name_${language}` as keyof CropSchedule] as string)?.trim() || followed.cropName;
          practices.push({ practice: p, cropName: localizedCropName, cropId: followed.cropId });
        }
      });
    });

    return practices;
  };

  const markPracticeComplete = async (cropId: string, practiceId: string, dayOffset: number) => {
    if (!profile) return;
    const week = Math.floor(dayOffset / 7);
    const practiceKey = `${practiceId}_${week}`;
    
    const updatedFollowed = (profile.followedCrops || []).map(followed => {
      if (followed.cropId === cropId) {
        return {
          ...followed,
          completedPractices: [...(followed.completedPractices || []), practiceKey]
        };
      }
      return followed;
    });

    try {
      await onUpdateProfile({ followedCrops: updatedFollowed });
      setToast({ message: t.practiceCompleted || "Practice marked as complete!", type: 'success' });
    } catch (error) {
      console.error("Error marking practice as complete:", error);
    }
  };

  const weeklyPractices = getWeeklyPractices();

  const getUVColor = (uv: number) => {
    if (uv <= 2) return 'text-emerald-500';
    if (uv <= 5) return 'text-amber-500';
    if (uv <= 7) return 'text-orange-500';
    if (uv <= 10) return 'text-rose-500';
    return 'text-purple-600';
  };

  const handleShare = async (calc: Calculator) => {
    const element = document.getElementById(`calc-${calc.id}`);
    if (!element) return;

    const total = (acres * calc.baseRate).toFixed(1);
    let shareText = `*${getLocalized(calc, 'title', language)}*\n`;
    shareText += `${t.acresCount} ${acres}\n`;
    shareText += `${t.total}: ${total} kg\n\n`;
    
    calc.rows.forEach(row => {
      const amount = (acres * row.ratio).toFixed(2);
      shareText += `• ${getLocalized(row, 'label', language)}: ${amount} kg\n`;
    });
    shareText += `\n${t.sharedVia}`;

    try {
      // Create a canvas from the element
      const canvas = await html2canvas(element, {
        scale: 2, // Higher quality
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        onclone: (clonedDoc) => {
          // Fix for Tailwind v4 oklch/oklab colors which html2canvas doesn't support
          const clonedElement = clonedDoc.getElementById(`calc-${calc.id}`);
          if (!clonedElement) return;

          const allOriginalElements = [element, ...Array.from(element.getElementsByTagName('*'))];
          const allClonedElements = [clonedElement, ...Array.from(clonedElement.getElementsByTagName('*'))];
          
          if (allOriginalElements.length !== allClonedElements.length) return;

          const tempDiv = document.createElement('div');
          clonedDoc.body.appendChild(tempDiv);
          
          allOriginalElements.forEach((originalEl, i) => {
            const clonedEl = allClonedElements[i] as HTMLElement;
            const style = window.getComputedStyle(originalEl);
            
            ['color', 'backgroundColor', 'borderColor', 'outlineColor', 'fill', 'stroke'].forEach(prop => {
              const val = style.getPropertyValue(prop);
              if (val && (val.includes('oklch') || val.includes('oklab'))) {
                clonedEl.style.setProperty(prop, 'black', 'important');
              }
            });
          });
          clonedDoc.body.removeChild(tempDiv);
        }
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: [Math.floor(canvas.width / 2), Math.floor(canvas.height / 2)]
      });

      pdf.addImage(imgData, 'PNG', 0, 0, Math.floor(canvas.width / 2), Math.floor(canvas.height / 2));
      
      const pdfBlob = pdf.output('blob');
      const fileName = `${calc.title.replace(/\s+/g, '_')}_Report.pdf`;
      const file = new File([pdfBlob], fileName, { type: 'application/pdf' });

      // Try sharing PDF if supported
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: calc.title,
          text: shareText,
        });
      } else if (navigator.share) {
        // Fallback to sharing text if file sharing is not supported
        await navigator.share({
          title: calc.title,
          text: shareText,
        });
        // Also download PDF as it couldn't be shared
        const link = document.createElement('a');
        link.href = URL.createObjectURL(pdfBlob);
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(link.href);
      } else {
        // Desktop fallback: Download PDF and Copy Text
        const link = document.createElement('a');
        link.href = URL.createObjectURL(pdfBlob);
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(link.href);

        await navigator.clipboard.writeText(shareText);
      }
    } catch (err) {
      console.error('Error generating/sharing PDF:', err);
      // Final fallback: just share text if PDF generation fails
      if (navigator.share) {
        try {
          await navigator.share({
            title: calc.title,
            text: shareText,
          });
        } catch (shareErr) {
          await navigator.clipboard.writeText(shareText);
          setToast({ message: t.detailsCopied || "Details copied to clipboard!", type: 'success' });
        }
      } else {
        await navigator.clipboard.writeText(shareText);
        setToast({ message: t.detailsCopied || "Details copied to clipboard!", type: 'success' });
      }
    }
  };

  return (
    <div className="p-4 space-y-6">
      {/* Hero Section */}
      <section className="bg-[#1b7d36] text-white p-8 rounded-[40px] shadow-lg relative overflow-hidden">
        <div className="relative z-10">
          <h2 className="text-[26px] font-bold mb-3 leading-tight">
            {user?.displayName ? `${t.hello} ${user.displayName}!` : t.helloFarmer}
          </h2>
          <p className="opacity-90 mb-6 text-sm leading-relaxed max-w-[280px]">
            {t.tagline}
          </p>
          <button 
            onClick={() => onNavigate('advisor')}
            className="bg-white text-[#1b7d36] px-6 py-2.5 rounded-full font-bold text-sm shadow-md hover:bg-stone-50 transition-all active:scale-95 flex items-center gap-2 w-fit"
          >
            <Bot size={18} />
            {t.askAI}
          </button>
        </div>
        <div className="absolute -bottom-10 -right-10 opacity-10">
          <Sprout size={200} />
        </div>
      </section>


      {/* Weather Section */}
      <div className="space-y-4">
        <h3 className="text-base font-bold text-stone-800 px-2">{t.dailyWeather}</h3>
        <div className="bg-white p-6 rounded-[32px] border border-black/5 shadow-sm flex flex-col gap-4">
          <div className="flex items-center justify-between">
            {loadingWeather ? (
              <div className="w-full flex justify-center py-4">
                <div className="w-6 h-6 border-2 border-[#1b7d36] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    {locationName && (
                      <div className="flex items-center gap-1 text-[#1b7d36]">
                        <span className="text-[18px] font-bold">{locationName}</span>
                      </div>
                    )}
                    <button 
                      onClick={() => setShowLocationSearch(!showLocationSearch)}
                      className="p-1.5 bg-stone-100 rounded-full text-stone-500 hover:text-[#1b7d36] transition-colors"
                    >
                      <MapPin size={14} />
                    </button>
                  </div>
                  <div className="flex items-baseline gap-1">
                    {weather ? (
                      <div className="flex flex-col">
                        <div className="flex items-baseline gap-2">
                          <span className="text-3xl font-bold text-stone-800">{weather.temperature_2m}°C</span>
                          <span className="text-xs text-stone-400 font-medium">{t.realFeel} {weather.apparent_temperature}°C</span>
                        </div>
                        {forecast.length > 0 && (
                          <div className="flex gap-2 text-xs font-semibold mt-1">
                            <span className="text-orange-600">{forecast[0].max}°</span>
                            <span className="text-stone-300">/</span>
                            <span className="text-blue-600">{forecast[0].min}°</span>
                            <div className="ml-2 flex items-center gap-1">
                              <span className="text-stone-400 font-normal">{t.uvIndex}:</span>
                              <span className={`font-bold ${getUVColor(weather.uv_index)}`}>{weather.uv_index} ({getUVText(weather.uv_index)})</span>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-stone-400">{t.dataNotAvailable}</span>
                    )}
                  </div>
                  <p className="text-[#1b7d36] font-bold text-sm">
                    {weather ? getWeatherText(weather.weather_code) : (locationName ? t.weatherLoadFailed : t.locationRequired)}
                  </p>
                </div>
                <div className="flex flex-col items-center">
                  {weather ? getWeatherIcon(weather.weather_code, 56) : <CloudOff className="text-stone-300" size={56} />}
                </div>
                <div className="text-right space-y-1">
                  {weather ? (
                    <>
                      <p className="text-xs text-stone-500 font-medium">{t.humidity}: {weather.relative_humidity_2m}%</p>
                      <p className="text-xs text-stone-500 font-medium">{t.wind}: {weather.wind_speed_10m} km/h</p>
                      {(() => {
                        const isSuitable = weather.temperature_2m >= 10 && 
                                         weather.temperature_2m <= 40 && 
                                         weather.weather_code < 51 && 
                                         weather.wind_speed_10m < 25;
                        return (
                          <p className={`text-xs font-bold mt-1 px-2 py-0.5 rounded-full inline-block ${isSuitable ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-amber-50 text-amber-700 border border-amber-100'}`}>
                            {isSuitable ? t.suitableForNaturalFarming : t.notSuitableForFarming}
                          </p>
                        );
                      })()}
                      {weather.summary && (
                        <p className="text-[11px] text-stone-500 mt-1 leading-tight italic max-w-[150px] ml-auto">
                          {weather.summary}
                        </p>
                      )}
                      <button 
                        onClick={() => setShowForecast(true)}
                        className="text-xs text-[#1b7d36] font-medium underline mt-1"
                      >
                        {t.clickForForecast}
                      </button>
                      {weather.lastUpdated && (
                        <p className="text-[10px] text-stone-300 mt-1">
                          {t.lastUpdated}: {new Date(weather.lastUpdated).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-stone-400">{t.humidity}: --%</p>
                  )}
                </div>
              </>
            )}
          </div>

          <AnimatePresence>
            {showLocationSearch && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <form 
                  onSubmit={handleLocationSearch}
                  className="flex gap-2 items-center pt-2 border-t border-stone-100"
                >
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                    <input
                      type="text"
                      className="w-full bg-stone-50 border border-stone-200 rounded-xl py-2 pl-9 pr-4 text-sm focus:outline-none focus:border-[#1b7d36]"
                      placeholder={t.searchLocation}
                      value={locationSearch}
                      onChange={(e) => setLocationSearch(e.target.value)}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isSearchingLocation}
                    className="bg-[#1b7d36] text-white p-2 rounded-xl disabled:opacity-50"
                  >
                    {isSearchingLocation ? <Loader2 size={16} className="animate-spin" /> : <ChevronRight size={16} />}
                  </button>
                  <button
                    type="button"
                    onClick={detectLocation}
                    className="p-2 bg-emerald-50 text-[#1b7d36] rounded-xl hover:bg-emerald-100 transition-colors"
                    title={t.detectLocation}
                  >
                    <RefreshCcw size={16} />
                  </button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Weekly Practices Section */}
      {weeklyPractices.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-base font-bold text-stone-800 px-2">{t.weeklyPractices || 'This Week\'s Practices'}</h3>
          <div className="space-y-3">
            {weeklyPractices.map(({ practice, cropName, cropId }) => (
              <motion.div 
                key={`${cropId}_${practice.id}`}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white p-5 rounded-[32px] border border-black/5 shadow-sm space-y-3"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="bg-emerald-50 text-[#1b7d36] text-[10px] font-black uppercase px-2 py-0.5 rounded-full tracking-wider">
                        {cropName}
                      </span>
                      <span className="text-[10px] text-stone-400 font-bold uppercase tracking-widest">
                        {language === 'te' ? `${practice.dayOffset}వ రోజు` : language === 'hi' ? `दिन ${practice.dayOffset}` : `Day ${practice.dayOffset}`}
                      </span>
                    </div>
                    <h4 className="font-bold text-stone-800">{getLocalized(practice, 'title', language)}</h4>
                    <p className="text-sm text-stone-500 leading-relaxed mt-1">
                      {getLocalized(practice, 'details', language)}
                    </p>
                  </div>
                  <div className="bg-stone-50 p-3 rounded-2xl">
                    <ListChecks className="text-[#1b7d36]" size={20} />
                  </div>
                </div>
                <button 
                  onClick={() => markPracticeComplete(cropId, practice.id, practice.dayOffset)}
                  className="w-full py-3 bg-[#1b7d36] text-white rounded-2xl font-bold text-sm shadow-md shadow-[#1b7d36]/20 active:scale-95 transition-all flex items-center justify-center gap-2"
                >
                  <Check size={18} />
                  {t.markAsComplete || 'Mark as Complete'}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Videos Section */}
      <VideoSection videos={videos} placement="home" t={t} />

      {/* PMDS Seed Calculator */}
      {calculators.filter(c => c.title.includes('PMDS')).map(calc => (
        <div key={calc.id} id={`calc-${calc.id}`} className="bg-white p-6 rounded-[32px] border border-black/5 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold text-stone-800">{getLocalized(calc, 'title', language)}</h3>
            <button 
              onClick={() => handleShare(calc)}
              className="p-2 text-[#1b7d36] hover:bg-emerald-50 rounded-full transition-colors"
            >
              <Share2 size={20} />
            </button>
          </div>
          
          <div className="flex items-center gap-3 bg-emerald-50 p-4 rounded-2xl">
            <div className="flex-1">
              <label className="text-xs font-bold text-emerald-800 block mb-1">{t.acresCount}</label>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setAcres(Math.max(0.5, acres - 0.5))}
                  className="w-8 h-8 rounded-full bg-white text-emerald-600 flex items-center justify-center font-bold shadow-sm"
                >-</button>
                <span className="font-bold text-lg text-emerald-900 w-12 text-center">{acres}</span>
                <button 
                  onClick={() => setAcres(acres + 0.5)}
                  className="w-8 h-8 rounded-full bg-[#1b7d36] text-white flex items-center justify-center font-bold shadow-sm"
                >+</button>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs font-bold text-emerald-800 mb-1">{t.total}</p>
              <p className="text-xl font-black text-[#1b7d36]">{(acres * calc.baseRate).toFixed(1)} <span className="text-sm font-bold">kg</span></p>
            </div>
          </div>

          <div className="space-y-2">
            {calc.rows.map(row => (
              <div key={row.id} className="flex items-center justify-between p-3 bg-stone-50 rounded-2xl border border-black/5">
                <div>
                  <p className="font-bold text-stone-800 text-sm">{getLocalized(row, 'label', language)}</p>
                  <p className="text-[10px] text-stone-500 line-clamp-1">{getLocalized(row, 'sublabel', language)}</p>
                </div>
                <div className="text-right font-bold text-[#1b7d36]">
                  {(acres * row.ratio).toFixed(2)} <span className="text-xs">kg</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Quick Access Grid */}
      <div className="grid grid-cols-2 gap-4">
        <button 
          onClick={() => onNavigate('marketplace')}
          className="bg-white p-6 rounded-[32px] border border-black/5 shadow-sm flex flex-col items-center text-center gap-3 hover:border-[#1b7d36]/30 transition-all group"
        >
          <div className="w-12 h-12 bg-orange-50 rounded-2xl flex items-center justify-center text-orange-600 group-hover:scale-110 transition-transform">
            <ShoppingBag size={24} />
          </div>
          <div>
            <h4 className="font-bold text-stone-900 text-sm">{t.marketplace}</h4>
            <p className="text-[10px] text-stone-400">{t.buySellProduce}</p>
          </div>
        </button>

        <button 
          onClick={() => onNavigate('advisor')}
          className="bg-white p-6 rounded-[32px] border border-black/5 shadow-sm flex flex-col items-center text-center gap-3 hover:border-[#1b7d36]/30 transition-all group"
        >
          <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600 group-hover:scale-110 transition-transform">
            <Bot size={24} />
          </div>
          <div>
            <h4 className="font-bold text-stone-900 text-sm">{t.advisor}</h4>
            <p className="text-[10px] text-stone-400">{t.aiAdvisor}</p>
          </div>
        </button>

        <button 
          onClick={() => onNavigate('plantDoctor')}
          className="bg-white p-6 rounded-[32px] border border-black/5 shadow-sm flex flex-col items-center text-center gap-3 hover:border-[#1b7d36]/30 transition-all group"
        >
          <div className="w-12 h-12 bg-rose-50 rounded-2xl flex items-center justify-center text-rose-600 group-hover:scale-110 transition-transform">
            <Bug size={24} />
          </div>
          <div>
            <h4 className="font-bold text-stone-900 text-sm">{t.plantDoctor}</h4>
            <p className="text-[10px] text-stone-400">Diagnose Pests</p>
          </div>
        </button>

        <button 
          onClick={() => onNavigate('expertConnect')}
          className="bg-white p-6 rounded-[32px] border border-black/5 shadow-sm flex flex-col items-center text-center gap-3 hover:border-[#1b7d36]/30 transition-all group"
        >
          <div className="w-12 h-12 bg-amber-50 rounded-2xl flex items-center justify-center text-amber-600 group-hover:scale-110 transition-transform">
            <UserCircle size={24} />
          </div>
          <div>
            <h4 className="font-bold text-stone-900 text-sm">{t.expertConnect}</h4>
            <p className="text-[10px] text-stone-400">Talk to Experts</p>
          </div>
        </button>

        <button 
          onClick={() => onNavigate('cropCalendar')}
          className="bg-white p-6 rounded-[32px] border border-black/5 shadow-sm flex flex-col items-center text-center gap-3 hover:border-[#1b7d36]/30 transition-all group"
        >
          <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600 group-hover:scale-110 transition-transform">
            <Calendar size={24} />
          </div>
          <div>
            <h4 className="font-bold text-stone-900 text-sm">{t.cropCalendar}</h4>
            <p className="text-[10px] text-stone-400">{t.recommendedCrops}</p>
          </div>
        </button>

        <button 
          onClick={() => onNavigate('forum')}
          className="bg-white p-6 rounded-[32px] border border-black/5 shadow-sm flex flex-col items-center text-center gap-3 hover:border-[#1b7d36]/30 transition-all group"
        >
          <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600 group-hover:scale-110 transition-transform">
            <MessageSquare size={24} />
          </div>
          <div>
            <h4 className="font-bold text-stone-900 text-sm">{t.communityForum}</h4>
            <p className="text-[10px] text-stone-400">{t.shareExperience}</p>
          </div>
        </button>

      </div>

      {/* Forecast Modal */}
      <AnimatePresence>
        {showForecast && (
          <Modal title={t.forecast10Days} onClose={() => setShowForecast(false)}>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              {forecast.map((day, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-stone-50 rounded-2xl border border-black/5">
                  <div className="w-24">
                    <p className="text-sm font-bold text-stone-800">
                      {i === 0 ? t.today : day.date.toLocaleDateString(language === 'te' ? 'te-IN' : language === 'hi' ? 'hi-IN' : 'en-US', { weekday: 'short', day: 'numeric' })}
                    </p>
                  </div>
                  <div className="flex flex-col items-center gap-1 flex-1 justify-center">
                    <div className="flex items-center gap-2">
                      {getWeatherIcon(day.code, 20)}
                      <span className="text-xs text-stone-500 truncate max-w-[120px]">{getWeatherText(day.code)}</span>
                    </div>
                    <div className="flex gap-3">
                      {day.rainChance > 10 && (
                        <div className="flex items-center gap-1">
                          <CloudRain size={10} className="text-blue-400" />
                          <span className="text-[10px] text-blue-500 font-medium">{day.rainChance}%</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <Sun size={10} className={getUVColor(day.uv)} />
                        <span className={`text-[10px] font-bold ${getUVColor(day.uv)}`}>UV {day.uv}</span>
                      </div>
                    </div>
                  </div>
                  <div className="w-24 text-right">
                    <span className="text-sm font-bold text-stone-800">{day.max}°</span>
                    <span className="text-xs text-stone-400 ml-1">/ {day.min}°</span>
                  </div>
                </div>
              ))}
            </div>
          </Modal>
        )}
      </AnimatePresence>

      {/* Add Market Price Modal */}
      <AnimatePresence>
        {showAddMarketPrice && (
          <Modal title={t.add || 'Add Market Price'} onClose={() => setShowAddMarketPrice(false)}>
            <form onSubmit={handleAddMarketPrice} className="space-y-4">
              <div className="space-y-1">
                <label className="text-sm font-bold text-stone-600 ml-1">{t.cropName}</label>
                <input 
                  type="text" 
                  name="cropName"
                  className="w-full bg-[#f8f9fa] border border-stone-200 rounded-2xl px-5 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
                  required
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-bold text-stone-600 ml-1">{t.price} (₹/{t.quintal})</label>
                <input 
                  type="number" 
                  name="price"
                  min="0"
                  className="w-full bg-[#f8f9fa] border border-stone-200 rounded-2xl px-5 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
                  required
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-bold text-stone-600 ml-1">{t.trend || 'Trend'}</label>
                <select 
                  name="trend"
                  className="w-full bg-[#f8f9fa] border border-stone-200 rounded-2xl px-5 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
                  required
                >
                  <option value="stable">{t.stable || 'Stable'}</option>
                  <option value="up">{t.rising || 'Rising'}</option>
                  <option value="down">{t.falling || 'Falling'}</option>
                </select>
              </div>
              <button 
                type="submit"
                className="w-full bg-[#1b7d36] text-white py-4 rounded-2xl font-bold text-lg shadow-lg hover:bg-[#16652b] transition-colors mt-4"
              >
                {t.submit || 'Submit'}
              </button>
            </form>
          </Modal>
        )}
      </AnimatePresence>

      {/* Important Note Section */}
      <section className="bg-[#fff7ed] p-8 rounded-[40px] border border-orange-100 shadow-sm relative mb-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-orange-100 p-2 rounded-xl">
            <AlertTriangle className="text-orange-600 w-5 h-5" />
          </div>
          <h3 className="text-base font-bold text-stone-800">{t.importantNote}</h3>
        </div>
        <p className="text-stone-700 text-sm leading-relaxed pr-16 text-justify">
          {t.naturalFarmingBenefit}
        </p>
        <div className="absolute top-8 right-8 bg-[#f26522] p-3 rounded-full shadow-lg">
          <Megaphone className="text-white w-5 h-5" />
        </div>
      </section>
    </div>
  );
}

function HomeCard({ icon, title, desc, onClick }: { icon: React.ReactNode, title: string, desc: string, onClick: () => void }) {
  return (
    <button 
      onClick={onClick}
      className="bg-white p-4 rounded-2xl border border-black/5 shadow-sm hover:shadow-md transition-all flex flex-col items-center text-center gap-2 group"
    >
      <div className="p-3 bg-[#f0fdf4] rounded-full group-hover:scale-110 transition-transform">
        {icon}
      </div>
      <div>
        <h4 className="font-bold text-stone-900 text-sm">{title}</h4>
        <p className="text-sm text-stone-500">{desc}</p>
      </div>
    </button>
  );
}

function AdminScreen(props: { 
  onNavigate: (s: Screen) => void,
  isAdmin: boolean,
  isExpert: boolean,
  user: User | null,
  onLogout: () => void, 
  categories: HandbookCategory[],
  setCategories: React.Dispatch<React.SetStateAction<HandbookCategory[]>>,
  calculators: Calculator[],
  setCalculators: React.Dispatch<React.SetStateAction<Calculator[]>>,
  videos: VideoItem[],
  setVideos: React.Dispatch<React.SetStateAction<VideoItem[]>>,
  pages: PageContent[],
  marketPrices: MarketPrice[],
  setMarketPrices: React.Dispatch<React.SetStateAction<MarketPrice[]>>,
  menuItems: MenuItem[],
  setMenuItems: React.Dispatch<React.SetStateAction<MenuItem[]>>,
  marketplaceConfig: { 
    availableCertifications: string[],
    farmingMethods: string[],
    productCategories: string[],
    visibleFields: {
      location: boolean;
      contact: boolean;
      certificate: boolean;
      farmingMethods: boolean;
    }
  },
  branding: { logoUrl: string },
  admobConfig: { bannerId: string, interstitialId: string, rewardedId: string, isTesting: boolean },
  setAdmobConfig: React.Dispatch<React.SetStateAction<{ bannerId: string, interstitialId: string, rewardedId: string, isTesting: boolean }>>,
  isAdMobSaving: boolean,
  setIsAdMobSaving: (s: boolean) => void,
  language: Language,
  t: any,
  setToast: (toast: {message: string, type: 'success' | 'error' | 'info'} | null) => void,
  setConfirmDialog: (dialog: {isOpen: boolean, title: string, message: string, onConfirm: () => void, type?: 'danger' | 'info'} | null) => void
}) {
  const { 
    onNavigate,
    isAdmin,
    isExpert,
    user,
    onLogout, 
    categories, 
    setCategories, 
    calculators, 
    setCalculators, 
    videos, 
    setVideos,
    pages,
    marketPrices,
    setMarketPrices,
    menuItems,
    setMenuItems,
    marketplaceConfig,
    branding,
    admobConfig,
    setAdmobConfig,
    isAdMobSaving,
    setIsAdMobSaving,
    language,
    t,
    setToast,
    setConfirmDialog
  } = props;
  const [activeTab, setActiveTab] = useState<'handbook' | 'calculators' | 'videos' | 'pages' | 'menu' | 'marketplace' | 'experts' | 'market_prices' | 'app_branding' | 'ad_config' | 'users' | 'crop_calendar'>(isAdmin ? 'handbook' : 'experts');
  const [localLogoUrl, setLocalLogoUrl] = useState(branding.logoUrl);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingItem, setEditingItem] = useState<{ catId: string, item: HandbookItem } | null>(null);
  const [editingCategory, setEditingCategory] = useState<HandbookCategory | null>(null);
  const [addingToCategory, setAddingToCategory] = useState<string | null>(null);
  const [newItemName, setNewItemName] = useState('');
  const [editingCalculator, setEditingCalculator] = useState<Calculator | null>(null);
  const [editingPage, setEditingPage] = useState<PageContent | null>(null);
  const [isAddingPage, setIsAddingPage] = useState(false);

  // Market Price management state
  const [isAddingMarketPrice, setIsAddingMarketPrice] = useState(false);
  const [editingMarketPrice, setEditingMarketPrice] = useState<MarketPrice | null>(null);
  const [newMarketPrice, setNewMarketPrice] = useState<Partial<MarketPrice>>({
    cropName: '',
    price: 0,
    trend: 'stable'
  });
  
  // Marketplace management state
  const [marketplaceListings, setMarketplaceListings] = useState<Listing[]>([]);
  const [loadingMarketplace, setLoadingMarketplace] = useState(false);
  const [editingMarketplaceListing, setEditingMarketplaceListing] = useState<Listing | null>(null);
  const [isAddingMarketplaceListing, setIsAddingMarketplaceListing] = useState(false);

  // Expert management state
  const [experts, setExperts] = useState<Expert[]>([]);
  const [loadingExperts, setLoadingExperts] = useState(false);
  const [editingExpert, setEditingExpert] = useState<Expert | null>(null);
  const [isAddingExpert, setIsAddingExpert] = useState(false);

  // Expert Messages state
  const [expertMessages, setExpertMessages] = useState<ExpertMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [viewingMessages, setViewingMessages] = useState(isExpert && !isAdmin);
  const [replyingTo, setReplyingTo] = useState<ExpertMessage | null>(null);
  const [replyText, setReplyText] = useState('');
  const [isSendingReply, setIsSendingReply] = useState(false);

  // Users state for export
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    setLocalLogoUrl(branding.logoUrl);
  }, [branding.logoUrl]);

  useEffect(() => {
    if (activeTab === 'marketplace') {
      setLoadingMarketplace(true);
      const q = collection(db, 'marketplace_listings');
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as any));
        // Sort client-side to be robust against missing timestamps
        docs.sort((a, b) => {
          const timeA = a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || 0;
          const timeB = b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || 0;
          return timeB - timeA;
        });
        setMarketplaceListings(docs);
        setLoadingMarketplace(false);
      }, (error) => {
        handleFirestoreError(error, OperationType.LIST, 'marketplace_listings');
      });
      return () => unsubscribe();
    }
    
    if (activeTab === 'experts') {
      setLoadingExperts(true);
      const qExperts = collection(db, 'experts');
      const unsubscribeExperts = onSnapshot(qExperts, (snapshot) => {
        const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Expert));
        setExperts(docs);
        setLoadingExperts(false);
      }, (error) => {
        handleFirestoreError(error, OperationType.LIST, 'experts');
        setLoadingExperts(false);
      });

      setLoadingMessages(true);
      // Filter messages if expert but not admin
      let qMessages = query(collection(db, 'expert_messages'), orderBy('timestamp', 'desc'));
      if (isExpert && !isAdmin && user?.email) {
        const email = user.email.toLowerCase().trim();
        qMessages = query(collection(db, 'expert_messages'), where('expertEmail', '==', email), orderBy('timestamp', 'desc'));
      }
      
      const unsubscribeMessages = onSnapshot(qMessages, (snapshot) => {
        const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ExpertMessage));
        setExpertMessages(docs);
        setLoadingMessages(false);
      }, (error) => {
        handleFirestoreError(error, OperationType.LIST, 'expert_messages');
        setLoadingMessages(false);
      });

      return () => {
        unsubscribeExperts();
        unsubscribeMessages();
      };
    }

    if (activeTab === 'users' && isAdmin) {
      const q = collection(db, 'users');
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const usersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllUsers(usersData);
      });
      return () => unsubscribe();
    }
  }, [activeTab, isExpert, isAdmin, user]);

  const handleExportUsers = () => {
    setIsExporting(true);
    try {
      const dataToExport = allUsers.map(u => ({
        UID: u.uid || '',
        Name: u.displayName || '',
        Email: u.email || '',
        Phone: u.phone || '',
        Location: u.location || '',
        Role: u.role || 'user',
        CreatedAt: u.createdAt?.toDate?.()?.toLocaleString() || u.createdAt?.seconds ? new Date(u.createdAt.seconds * 1000).toLocaleString() : ''
      }));
      exportToExcel(dataToExport, 'Prakruthi_Mithra_Users');
      setToast({ message: "Users exported successfully", type: 'success' });
    } catch (error) {
      console.error("Export error:", error);
      setToast({ message: "Export failed", type: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyingTo || !replyText.trim()) return;
    setIsSendingReply(true);
    try {
      await updateDoc(doc(db, 'expert_messages', replyingTo.id), {
        reply: replyText.trim(),
        replyTimestamp: serverTimestamp(),
        status: 'responded'
      });
      setReplyingTo(null);
      setReplyText('');
      setToast({ message: "Reply sent successfully", type: 'success' });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `expert_messages/${replyingTo.id}`);
    } finally {
      setIsSendingReply(false);
    }
  };

  const handleSaveExpert = async (expertData: any) => {
    try {
      const sanitizedData = {
        ...expertData,
        email: expertData.email?.toLowerCase()?.trim()
      };
      if (editingExpert) {
        await setDoc(doc(db, 'experts', editingExpert.id), sanitizedData, { merge: true });
        setToast({ message: "Expert updated successfully", type: 'success' });
      } else {
        await addDoc(collection(db, 'experts'), sanitizedData);
        setToast({ message: "Expert added successfully", type: 'success' });
      }
      setEditingExpert(null);
      setIsAddingExpert(false);
    } catch (error) {
      handleFirestoreError(error, editingExpert ? OperationType.UPDATE : OperationType.CREATE, 'experts');
    }
  };

  const handleDeleteExpert = async (id: string) => {
    setConfirmDialog({
      isOpen: true,
      title: "Delete Expert?",
      message: "Are you sure you want to delete this expert? This action cannot be undone.",
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, 'experts', id));
          setToast({ message: "Expert deleted successfully", type: 'success' });
        } catch (error) {
          handleFirestoreError(error, OperationType.DELETE, `experts/${id}`);
        }
        setConfirmDialog(null);
      },
      type: 'danger'
    });
  };

  const handleSaveMarketplaceListing = async (listingData: any) => {
    try {
      if (editingMarketplaceListing) {
        await setDoc(doc(db, 'marketplace_listings', editingMarketplaceListing.id), listingData, { merge: true });
        setToast({ message: "Listing updated successfully", type: 'success' });
      } else {
        const newListing = {
          ...listingData,
          authorId: auth.currentUser?.uid,
          authorName: 'Admin',
          isVerified: true,
          createdAt: serverTimestamp()
        };
        await addDoc(collection(db, 'marketplace_listings'), newListing);
        setToast({ message: "Listing added successfully", type: 'success' });
      }
      setEditingMarketplaceListing(null);
      setIsAddingMarketplaceListing(false);
    } catch (error) {
      handleFirestoreError(error, editingMarketplaceListing ? OperationType.UPDATE : OperationType.CREATE, 'marketplace_listings');
    }
  };

  const handleDeleteMarketplaceListing = async (id: string) => {
    setConfirmDialog({
      isOpen: true,
      title: t.deleteConfirm || "Are you sure?",
      message: "This listing will be permanently removed from the marketplace.",
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, 'marketplace_listings', id));
          setToast({ message: "Listing deleted successfully", type: 'success' });
        } catch (error) {
          handleFirestoreError(error, OperationType.DELETE, `marketplace_listings/${id}`);
        }
        setConfirmDialog(null);
      },
      type: 'danger'
    });
  };
  
  // Video management state
  const [newVideoTitle, setNewVideoTitle] = useState('');
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [newVideoThumbnail, setNewVideoThumbnail] = useState('');
  const [newVideoPlacements, setNewVideoPlacements] = useState<string[]>(['videos']);
  const [editingVideo, setEditingVideo] = useState<VideoItem | null>(null);
  const [isResetting, setIsResetting] = useState(false);

  // Menu management state
  const [editingMenuItem, setEditingMenuItem] = useState<MenuItem | null>(null);
  const [itemToDelete, setItemToDelete] = useState<MenuItem | null>(null);
  const [isAddingMenuItem, setIsAddingMenuItem] = useState(false);
  const [newMenuItem, setNewMenuItem] = useState<Partial<MenuItem>>({
    screen: 'home',
    labelKey: '',
    iconName: 'Home',
    visible: true,
    isAdminOnly: false
  });

  const handleSeedMarketPrices = async () => {
    setConfirmDialog({
      isOpen: true,
      title: "Seed Common Market Prices?",
      message: "This will add a set of common crop prices (Paddy, Chilli, Cotton, etc.) to the verified local prices section.",
      onConfirm: async () => {
        const commonPrices = [
          { cropName: 'వరి (Paddy)', price: 1800, trend: 'stable', lastUpdated: new Date().toISOString() },
          { cropName: 'మిర్చి (Chilli)', price: 15500, trend: 'up', lastUpdated: new Date().toISOString() },
          { cropName: 'పత్తి (Cotton)', price: 7200, trend: 'down', lastUpdated: new Date().toISOString() },
          { cropName: 'శనగ (Bengal Gram)', price: 5400, trend: 'up', lastUpdated: new Date().toISOString() },
          { cropName: 'మినుములు (Black Gram)', price: 8200, trend: 'stable', lastUpdated: new Date().toISOString() },
          { cropName: 'కందులు (Red Gram)', price: 10500, trend: 'up', lastUpdated: new Date().toISOString() },
          { cropName: 'పెసలు (Green Gram)', price: 7800, trend: 'stable', lastUpdated: new Date().toISOString() },
          { cropName: 'మొక్కజొన్న (Maize)', price: 2150, trend: 'down', lastUpdated: new Date().toISOString() },
          { cropName: 'వేరుశనగ (Groundnut)', price: 6800, trend: 'up', lastUpdated: new Date().toISOString() },
          { cropName: 'పసుపు (Turmeric)', price: 12500, trend: 'up', lastUpdated: new Date().toISOString() },
        ];

        try {
          const batch = writeBatch(db);
          commonPrices.forEach(price => {
            const docRef = doc(collection(db, 'market_prices'));
            batch.set(docRef, { ...price, authorId: user?.uid || 'admin' });
          });
          await batch.commit();
          setToast({ message: "Successfully seeded market prices!", type: 'success' });
        } catch (error) {
          handleFirestoreError(error, OperationType.WRITE, 'market_prices');
        }
        setConfirmDialog(null);
      }
    });
  };

  const handleSaveMarketPrice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMarketPrice.cropName || !newMarketPrice.price) return;
    
    try {
      if (editingMarketPrice) {
        await updateDoc(doc(db, 'market_prices', editingMarketPrice.id), {
          ...newMarketPrice,
          lastUpdated: new Date().toISOString()
        });
        setToast({ message: "Market price updated successfully", type: 'success' });
      } else {
        await addDoc(collection(db, 'market_prices'), {
          ...newMarketPrice,
          lastUpdated: new Date().toISOString(),
          authorId: user?.uid || 'admin'
        });
        setToast({ message: "Market price added successfully", type: 'success' });
      }
      setNewMarketPrice({ cropName: '', price: 0, trend: 'stable' });
      setEditingMarketPrice(null);
      setIsAddingMarketPrice(false);
    } catch (error) {
      handleFirestoreError(error, editingMarketPrice ? OperationType.UPDATE : OperationType.CREATE, 'market_prices');
    }
  };

  const handleEditMarketPrice = (price: MarketPrice) => {
    setEditingMarketPrice(price);
    setNewMarketPrice({
      cropName: price.cropName,
      price: price.price,
      trend: price.trend
    });
    setIsAddingMarketPrice(true);
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteMarketPrice = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'market_prices', id));
      setToast({ message: "Price deleted successully", type: 'success' });
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, `market_prices/${id}`);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, callback: (url: string) => void) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const resized = await resizeImage(reader.result as string);
        callback(resized);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleReset = async () => {
    setConfirmDialog({
      isOpen: true,
      title: "Reset All Data?",
      message: t.resetConfirmation || "Are you sure you want to reset all data? This will clear Firestore and re-seed with initial data.",
      onConfirm: async () => {
        setIsResetting(true);
        try {
          // Clear and re-seed Handbook
          for (const cat of INITIAL_HANDBOOK) {
            try {
              await setDoc(doc(db, 'handbook', cat.id), cat);
            } catch (error) {
              handleFirestoreError(error, OperationType.WRITE, `handbook/${cat.id}`);
            }
          }
          // Clear and re-seed Calculators
          for (const calc of INITIAL_CALCULATORS) {
            try {
              await setDoc(doc(db, 'calculators', calc.id), calc);
            } catch (error) {
              handleFirestoreError(error, OperationType.WRITE, `calculators/${calc.id}`);
            }
          }
          // Clear and re-seed Videos
          for (const video of INITIAL_VIDEOS) {
            try {
              await setDoc(doc(db, 'videos', video.id), video);
            } catch (error) {
              handleFirestoreError(error, OperationType.WRITE, `videos/${video.id}`);
            }
          }
          // Clear and re-seed Pages
          try {
            await setDoc(doc(db, 'pages', 'privacy'), { title: t.privacyPolicy, content: '' });
          } catch (error) {
            handleFirestoreError(error, OperationType.WRITE, 'pages/privacy');
          }
          try {
            await setDoc(doc(db, 'pages', 'terms'), { title: t.termsAndConditions, content: '' });
          } catch (error) {
            handleFirestoreError(error, OperationType.WRITE, 'pages/terms');
          }
          
          setToast({ message: t.resetSuccess || "Data reset successfully!", type: 'success' });
        } catch (error) {
          console.error("Reset error", error);
          setToast({ message: t.resetError || "Error resetting data.", type: 'error' });
        } finally {
          setIsResetting(false);
          setConfirmDialog(null);
        }
      },
      type: 'danger'
    });
  };

  const handleAddVideo = async () => {
    if (!newVideoTitle.trim() || !newVideoUrl.trim()) return;
    const id = Date.now().toString();
    const newVideo: VideoItem = {
      id,
      title: newVideoTitle,
      url: getYouTubeEmbedUrl(newVideoUrl),
      thumbnail: newVideoThumbnail.trim() || `https://picsum.photos/seed/${id}/800/400`,
      placement: newVideoPlacements,
      order: videos.length
    };
    try {
      await setDoc(doc(db, 'videos', id), newVideo);
      setNewVideoTitle('');
      setNewVideoUrl('');
      setNewVideoThumbnail('');
      setNewVideoPlacements(['videos']);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, `videos/${id}`);
    }
  };

  const handleDeleteVideo = async (id: string) => {
    setConfirmDialog({
      isOpen: true,
      title: "Delete Video?",
      message: t.deleteVideoConfirm || "Are you sure you want to delete this video?",
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, 'videos', id));
          setToast({ message: "Video deleted successfully", type: 'success' });
        } catch (error) {
          handleFirestoreError(error, OperationType.DELETE, `videos/${id}`);
        }
        setConfirmDialog(null);
      },
      type: 'danger'
    });
  };

  const handleSaveVideo = async () => {
    if (!editingVideo || !editingVideo.title.trim() || !editingVideo.url.trim()) return;
    const updatedVideo = { 
      ...editingVideo, 
      url: getYouTubeEmbedUrl(editingVideo.url),
      placement: editingVideo.placement || ['videos']
    };
    try {
      await setDoc(doc(db, 'videos', updatedVideo.id), updatedVideo);
      setEditingVideo(null);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, `videos/${updatedVideo.id}`);
    }
  };

  const handleAddCalculator = async () => {
    const id = Date.now().toString();
    const newCalc: Calculator = {
      id,
      title: t.newCalculator,
      baseRate: 10,
      rows: []
    };
    try {
      await setDoc(doc(db, 'calculators', id), newCalc);
      setEditingCalculator(newCalc);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, `calculators/${id}`);
    }
  };

  const handleDeleteCalculator = async (id: string) => {
    setConfirmDialog({
      isOpen: true,
      title: "Delete Calculator?",
      message: t.deleteCalculatorConfirm || "Are you sure you want to delete this calculator?",
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, 'calculators', id));
          setToast({ message: "Calculator deleted successfully", type: 'success' });
        } catch (error) {
          handleFirestoreError(error, OperationType.DELETE, `calculators/${id}`);
        }
        setConfirmDialog(null);
      },
      type: 'danger'
    });
  };

  const handleSaveCalculator = async (updated: Calculator) => {
    try {
      await setDoc(doc(db, 'calculators', updated.id), updated);
      setEditingCalculator(null);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, `calculators/${updated.id}`);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    const id = Date.now().toString();
    const newCat: HandbookCategory = {
      id,
      name: newCategoryName,
      items: [],
      order: categories.length
    };
    try {
      await setDoc(doc(db, 'handbook', id), newCat);
      setNewCategoryName('');
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, `handbook/${id}`);
    }
  };

  const handleMoveCategory = async (id: string, direction: 'up' | 'down') => {
    const index = categories.findIndex(c => c.id === id);
    if (index === -1) return;
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === categories.length - 1) return;

    const newCategories = [...categories];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newCategories[index], newCategories[targetIndex]] = [newCategories[targetIndex], newCategories[index]];

    // Update all categories with their new order in Firestore
    for (let i = 0; i < newCategories.length; i++) {
      try {
        await setDoc(doc(db, 'handbook', newCategories[i].id), { ...newCategories[i], order: i });
      } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, `handbook/${newCategories[i].id}`);
      }
    }
  };

  const handleMoveVideo = async (id: string, direction: 'up' | 'down') => {
    const index = videos.findIndex(v => v.id === id);
    if (index === -1) return;
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === videos.length - 1) return;

    const newVideos = [...videos];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newVideos[index], newVideos[targetIndex]] = [newVideos[targetIndex], newVideos[index]];

    // Update all videos with their new order in Firestore
    for (let i = 0; i < newVideos.length; i++) {
      try {
        await setDoc(doc(db, 'videos', newVideos[i].id), { ...newVideos[i], order: i });
      } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, `videos/${newVideos[i].id}`);
      }
    }
  };

  const handleMoveMenuItem = async (id: string, direction: 'up' | 'down') => {
    const index = menuItems.findIndex(m => m.id === id);
    if (index === -1) return;
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === menuItems.length - 1) return;

    const newMenuItems = [...menuItems];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newMenuItems[index], newMenuItems[targetIndex]] = [newMenuItems[targetIndex], newMenuItems[index]];

    // Optimistic update
    setMenuItems(newMenuItems.map((item, i) => ({ ...item, order: i + 1 })));

    const batch = writeBatch(db);
    for (let i = 0; i < newMenuItems.length; i++) {
      const ref = doc(db, 'menu_items', newMenuItems[i].id);
      batch.set(ref, { order: i + 1 }, { merge: true });
    }
    try {
      await batch.commit();
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'menu_items');
      // Revert if batch fails
      setMenuItems([...menuItems]);
    }
  };

  const handleToggleMenuItemVisibility = async (id: string, currentVisible: boolean) => {
    try {
      await updateDoc(doc(db, 'menu_items', id), { visible: !currentVisible });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `menu_items/${id}`);
    }
  };

  const handleAddMenuItem = async () => {
    if (!newMenuItem.labelKey) return;
    const id = Date.now().toString();
    const item: MenuItem = {
      id,
      screen: newMenuItem.screen as Screen || 'home',
      labelKey: newMenuItem.labelKey,
      iconName: newMenuItem.iconName || 'Home',
      order: menuItems.length,
      visible: newMenuItem.visible ?? true,
      isAdminOnly: newMenuItem.isAdminOnly ?? false
    };
    try {
      await setDoc(doc(db, 'menu_items', id), item);
      setIsAddingMenuItem(false);
      setNewMenuItem({ screen: 'home', labelKey: '', iconName: 'Home', visible: true, isAdminOnly: false });
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, `menu_items/${id}`);
    }
  };

  const handleSaveMenuItem = async () => {
    if (!editingMenuItem) return;
    try {
      await setDoc(doc(db, 'menu_items', editingMenuItem.id), editingMenuItem);
      setEditingMenuItem(null);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, `menu_items/${editingMenuItem.id}`);
    }
  };

  const handleDeleteMenuItem = (item: MenuItem) => {
    setItemToDelete(item);
  };

  const handleDeleteConfirmed = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'menu_items', id));
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, `menu_items/${id}`);
    }
  };


  const handleUpdateCategoryName = async () => {
    if (!editingCategory || !editingCategory.name.trim()) return;
    try {
      const compressedCat = await compressCategory(editingCategory);
      await setDoc(doc(db, 'handbook', compressedCat.id), compressedCat);
      setEditingCategory(null);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, `handbook/${editingCategory.id}`);
    }
  };

  const handleDeleteCategory = async (id: string) => {
    setConfirmDialog({
      isOpen: true,
      title: "Delete Category?",
      message: t.deleteCategoryConfirm || "Are you sure you want to delete this category and all its items?",
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, 'handbook', id));
          setToast({ message: "Category deleted successfully", type: 'success' });
        } catch (error) {
          handleFirestoreError(error, OperationType.DELETE, `handbook/${id}`);
        }
        setConfirmDialog(null);
      },
      type: 'danger'
    });
  };

  const handleAddItem = async (catId: string) => {
    if (!newItemName.trim()) return;
    const cat = categories.find(c => c.id === catId);
    if (!cat) return;

    const newItem: HandbookItem = { 
      id: Date.now().toString(), 
      name: newItemName, 
      screen: 'handbook',
      sections: [],
      image: 'https://picsum.photos/seed/' + Date.now() + '/800/400'
    };

    const updatedCat = {
      ...cat,
      items: [...cat.items, newItem]
    };
    
    try {
      const compressedCat = await compressCategory(updatedCat);
      await setDoc(doc(db, 'handbook', catId), compressedCat);
      setNewItemName('');
      setAddingToCategory(null);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, `handbook/${catId}`);
    }
  };

  const handleDeleteItem = async (catId: string, itemId: string) => {
    setConfirmDialog({
      isOpen: true,
      title: "Delete Item?",
      message: t.deleteSubcategoryConfirm || "Are you sure you want to delete this item?",
      onConfirm: async () => {
        const cat = categories.find(c => c.id === catId);
        if (!cat) return;

        const updatedCat = {
          ...cat,
          items: cat.items.filter(i => i.id !== itemId)
        };
        try {
          const compressedCat = await compressCategory(updatedCat);
          await setDoc(doc(db, 'handbook', catId), compressedCat);
          setToast({ message: "Item deleted successfully", type: 'success' });
        } catch (error) {
          handleFirestoreError(error, OperationType.WRITE, `handbook/${catId}`);
        }
        setConfirmDialog(null);
      },
      type: 'danger'
    });
  };

  const handleSaveItem = async (catId: string, updatedItem: HandbookItem) => {
    const cat = categories.find(c => c.id === catId);
    if (!cat) return;

    const updatedCat = {
      ...cat,
      items: cat.items.map(i => i.id === updatedItem.id ? updatedItem : i)
    };
    try {
      const compressedCat = await compressCategory(updatedCat);
      await setDoc(doc(db, 'handbook', catId), compressedCat);
      setEditingItem(null);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, `handbook/${catId}`);
    }
  };

  if (editingItem) {
    return (
      <ItemEditor 
        catId={editingItem.catId} 
        item={editingItem.item} 
        onSave={handleSaveItem} 
        onCancel={() => setEditingItem(null)} 
        t={t}
      />
    );
  }

  if (editingCalculator) {
    return (
      <CalculatorEditor 
        calculator={editingCalculator}
        onSave={handleSaveCalculator}
        onCancel={() => setEditingCalculator(null)}
        t={t}
      />
    );
  }

  return (
    <div className="p-4 space-y-6 pb-20">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-[#1b7d36]">{t.adminPanel}</h2>
        <div className="flex gap-4 items-center">
          {isAdmin && (
            <button 
              onClick={handleReset} 
              disabled={isResetting}
              className={`text-xs transition-colors ${isResetting ? 'text-stone-300' : 'text-stone-400 hover:text-rose-500'}`}
              title={t.resetAppData}
            >
              {isResetting ? t.resetting : t.resetData}
            </button>
          )}
          <button onClick={onLogout} className="text-rose-500 font-bold hover:opacity-80 transition-opacity">
            {t.logout}
          </button>
        </div>
      </div>

      <div className="flex gap-2 bg-stone-100 p-1 rounded-2xl overflow-x-auto no-scrollbar">
        {isAdmin && (
          <>
            <button 
              onClick={() => setActiveTab('handbook')}
              className={`whitespace-nowrap px-4 py-2 rounded-xl font-bold transition-all ${activeTab === 'handbook' ? 'bg-white text-[#1b7d36] shadow-sm' : 'text-stone-400'}`}
            >
              {t.handbook}
            </button>
            <button 
              onClick={() => setActiveTab('calculators')}
              className={`whitespace-nowrap px-4 py-2 rounded-xl font-bold transition-all ${activeTab === 'calculators' ? 'bg-white text-[#1b7d36] shadow-sm' : 'text-stone-400'}`}
            >
              {t.calculators}
            </button>
            <button 
              onClick={() => setActiveTab('videos')}
              className={`whitespace-nowrap px-4 py-2 rounded-xl font-bold transition-all ${activeTab === 'videos' ? 'bg-white text-[#1b7d36] shadow-sm' : 'text-stone-400'}`}
            >
              {t.videos}
            </button>
            <button 
              onClick={() => setActiveTab('pages')}
              className={`whitespace-nowrap px-4 py-2 rounded-xl font-bold transition-all ${activeTab === 'pages' ? 'bg-white text-[#1b7d36] shadow-sm' : 'text-stone-400'}`}
            >
              {t.pages}
            </button>
            <button 
              onClick={() => setActiveTab('menu')}
              className={`whitespace-nowrap px-4 py-2 rounded-xl font-bold transition-all ${activeTab === 'menu' ? 'bg-white text-[#1b7d36] shadow-sm' : 'text-stone-400'}`}
            >
              {t.menu || 'Menu'}
            </button>
            <button 
              onClick={() => setActiveTab('marketplace')}
              className={`whitespace-nowrap px-4 py-2 rounded-xl font-bold transition-all ${activeTab === 'marketplace' ? 'bg-white text-[#1b7d36] shadow-sm' : 'text-stone-400'}`}
            >
              {t.marketplace || 'Marketplace'}
            </button>
            <button 
              onClick={() => setActiveTab('market_prices')}
              className={`whitespace-nowrap px-4 py-2 rounded-xl font-bold transition-all ${activeTab === 'market_prices' ? 'bg-white text-[#1b7d36] shadow-sm' : 'text-stone-400'}`}
            >
              {t.marketPrices || 'Market Prices'}
            </button>
            <button 
              onClick={() => setActiveTab('crop_calendar')}
              className={`whitespace-nowrap px-4 py-2 rounded-xl font-bold transition-all ${activeTab === 'crop_calendar' ? 'bg-white text-[#1b7d36] shadow-sm' : 'text-stone-400'}`}
            >
              {t.cropCalendar || 'Crop Calendar'}
            </button>
            <button 
              onClick={() => setActiveTab('app_branding')}
              className={`whitespace-nowrap px-4 py-2 rounded-xl font-bold transition-all ${activeTab === 'app_branding' ? 'bg-white text-[#1b7d36] shadow-sm' : 'text-stone-400'}`}
            >
              {t.branding || 'Branding'}
            </button>
            <button 
              onClick={() => setActiveTab('ad_config')}
              className={`whitespace-nowrap px-4 py-2 rounded-xl font-bold transition-all ${activeTab === 'ad_config' ? 'bg-white text-[#1b7d36] shadow-sm' : 'text-stone-400'}`}
            >
              {t.adMobConfig || 'Ads'}
            </button>
            <button 
              onClick={() => setActiveTab('users')}
              className={`whitespace-nowrap px-4 py-2 rounded-xl font-bold transition-all ${activeTab === 'users' ? 'bg-white text-[#1b7d36] shadow-sm' : 'text-stone-400'}`}
            >
              {language === 'te' ? 'వినియోగదారులు' : 'Users'}
            </button>
          </>
        )}
        <button 
          onClick={() => setActiveTab('experts')}
          className={`whitespace-nowrap px-4 py-2 rounded-xl font-bold transition-all ${activeTab === 'experts' ? 'bg-white text-[#1b7d36] shadow-sm' : 'text-stone-400'}`}
        >
          {t.experts || 'Experts'}
        </button>
      </div>

      {activeTab === 'handbook' ? (
        <>
          {/* Category Editor Modal-like section */}
          {editingCategory && (
            <div className="bg-amber-50 p-6 rounded-[32px] border border-amber-200 shadow-sm space-y-4">
              <h3 className="text-amber-800 font-bold">{t.editCategoryName}</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-bold text-amber-700 block mb-1">English Name</label>
                  <input 
                    type="text" 
                    value={editingCategory.name_en || editingCategory.name}
                    onChange={e => setEditingCategory({ ...editingCategory, name_en: e.target.value, name: e.target.value })}
                    className="w-full bg-white border border-amber-200 rounded-2xl px-5 py-3 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-amber-700 block mb-1">Telugu Name (తెలుగు)</label>
                  <input 
                    type="text" 
                    value={editingCategory.name_te || ''}
                    onChange={e => setEditingCategory({ ...editingCategory, name_te: e.target.value })}
                    className="w-full bg-white border border-amber-200 rounded-2xl px-5 py-3 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-amber-700 block mb-1">Hindi Name (हिंदी)</label>
                  <input 
                    type="text" 
                    value={editingCategory.name_hi || ''}
                    onChange={e => setEditingCategory({ ...editingCategory, name_hi: e.target.value })}
                    className="w-full bg-white border border-amber-200 rounded-2xl px-5 py-3 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                  />
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={handleUpdateCategoryName}
                    className="flex-1 bg-amber-600 text-white px-6 py-3 rounded-2xl font-bold shadow-md"
                  >
                    {t.save}
                  </button>
                  <button 
                    onClick={() => setEditingCategory(null)}
                    className="bg-stone-200 text-stone-600 px-6 py-3 rounded-2xl font-bold"
                  >
                    {t.cancel}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Add Category Section */}
          {isAdmin && !editingCategory && (
            <div className="bg-white p-6 rounded-[32px] border border-black/5 shadow-sm space-y-4">
              <h3 className="text-[#1b7d36] font-bold">{t.addNewCategory}</h3>
              <div className="flex gap-2">
                <input 
                  type="text" 
                  value={newCategoryName}
                  onChange={e => setNewCategoryName(e.target.value)}
                  placeholder={t.categoryName}
                  className="flex-1 bg-[#f8f9fa] border border-stone-200 rounded-2xl px-5 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
                />
                <button 
                  onClick={handleAddCategory}
                  className="bg-[#1b7d36] text-white p-4 rounded-2xl shadow-md hover:scale-105 transition-transform"
                >
                  <Plus size={24} />
                </button>
              </div>
            </div>
          )}

          {/* Categories List */}
          <div className="space-y-8">
            {categories.map((cat, index) => (
              <div key={`${cat.id}-${index}`} className="space-y-4 bg-stone-50/50 p-4 rounded-[40px] border border-black/5">
                <div className="flex flex-col gap-2 px-2">
                  <div className="flex justify-between items-center">
                    <h4 className="text-base font-bold text-[#1b7d36]">{cat.name}</h4>
                    {isAdmin && (
                      <div className="flex gap-4 items-center">
                        <div className="flex gap-1 mr-2">
                          <button 
                            onClick={() => handleMoveCategory(cat.id, 'up')}
                            disabled={categories.indexOf(cat) === 0}
                            className="p-1 text-[#1b7d36] disabled:opacity-20 hover:bg-[#1b7d36]/10 rounded"
                            title={t.moveUp}
                          >
                            <ArrowUp size={16} />
                          </button>
                          <button 
                            onClick={() => handleMoveCategory(cat.id, 'down')}
                            disabled={categories.indexOf(cat) === categories.length - 1}
                            className="p-1 text-[#1b7d36] disabled:opacity-20 hover:bg-[#1b7d36]/10 rounded"
                            title={t.moveDown}
                          >
                            <ArrowDown size={16} />
                          </button>
                        </div>
                        <button 
                          onClick={() => setEditingCategory(cat)}
                          className="text-blue-500 text-sm font-bold"
                        >
                          {t.edit}
                        </button>
                        <button 
                          onClick={() => handleDeleteCategory(cat.id)}
                          className="text-rose-500 text-sm font-bold"
                        >
                          {t.delete}
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {addingToCategory === cat.id ? (
                    <div className="flex gap-2 bg-white p-2 rounded-xl border border-emerald-200">
                      <input 
                        type="text" 
                        autoFocus
                        value={newItemName}
                        onChange={e => setNewItemName(e.target.value)}
                        placeholder={t.subcategoryName}
                        className="flex-1 px-3 py-1 text-sm focus:outline-none"
                        onKeyPress={e => e.key === 'Enter' && handleAddItem(cat.id)}
                      />
                      <button 
                        onClick={() => handleAddItem(cat.id)}
                        className="bg-[#1b7d36] text-white px-3 py-1 rounded-lg text-xs font-bold"
                      >
                        {t.add}
                      </button>
                      <button 
                        onClick={() => { setAddingToCategory(null); setNewItemName(''); }}
                        className="text-stone-400 px-2"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <button 
                      onClick={() => setAddingToCategory(cat.id)}
                      className="w-full bg-emerald-50 text-[#1b7d36] py-2 rounded-xl text-sm font-bold border border-emerald-100 hover:bg-emerald-100 transition-colors"
                    >
                      + {t.addNewSubcategory}
                    </button>
                  )}
                </div>

                <div className="space-y-2">
                  {cat.items.map((item, index) => (
                    <div key={`${item.id}-${index}`} className="bg-white p-4 rounded-2xl border border-black/5 shadow-sm flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {item.image && <img src={item.image} className="w-10 h-10 rounded-lg object-cover" referrerPolicy="no-referrer" />}
                        <span className="font-bold text-stone-700">{item.name}</span>
                      </div>
                      <div className="flex gap-4">
                        <button 
                          onClick={() => setEditingItem({ catId: cat.id, item })}
                          className="text-blue-500 font-bold text-sm"
                        >
                          {t.edit}
                        </button>
                        <button 
                          onClick={() => handleDeleteItem(cat.id, item.id)}
                          className="text-rose-500 font-bold text-sm"
                        >
                          {t.delete}
                        </button>
                      </div>
                    </div>
                  ))}
                  {cat.items.length === 0 && (
                    <p className="text-center text-stone-400 text-xs py-2 italic">{t.noSubcategoriesYet}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : activeTab === 'pages' ? (
        <div className="space-y-6">
          {isAdmin && (
            <button 
              onClick={() => {
                setEditingPage({ id: '', title: '', content: '', title_en: '', content_en: '', title_te: '', content_te: '', title_hi: '', content_hi: '' });
                setIsAddingPage(true);
              }}
              className="w-full bg-[#1b7d36] text-white py-4 rounded-2xl font-bold shadow-md flex items-center justify-center gap-2"
            >
              <Plus size={20} /> {t.addNewPage}
            </button>
          )}

          <div className="space-y-4">
            {pages.map((page, index) => (
              <div key={`${page.id}-${index}`} className="bg-white p-6 rounded-[32px] border border-black/5 shadow-sm flex items-center justify-between">
                <div className="space-y-1">
                  <h4 className="font-bold text-stone-800">{page.title_en || page.title}</h4>
                  <p className="text-xs text-stone-400">/{page.id}</p>
                </div>
                {isAdmin && (
                  <div className="flex gap-4">
                    <button 
                      onClick={() => setEditingPage(page)}
                      className="text-blue-500 font-bold text-sm"
                    >
                      {t.edit}
                    </button>
                    <button 
                      onClick={() => {
                        setConfirmDialog({
                          isOpen: true,
                          title: t.deleteConfirm,
                          message: t.deletePageConfirm,
                          onConfirm: async () => {
                            try {
                              await deleteDoc(doc(db, 'pages', page.id));
                              setToast({ message: "Page deleted successfully", type: 'success' });
                            } catch (e) {
                              handleFirestoreError(e, OperationType.DELETE, `pages/${page.id}`);
                            }
                            setConfirmDialog(null);
                          },
                          type: 'danger'
                        });
                      }}
                      className="text-rose-500 font-bold text-sm"
                    >
                      {t.delete}
                    </button>
                  </div>
                )}
              </div>
            ))}
            {pages.length === 0 && (
              <p className="text-center text-stone-400 text-xs py-10 italic">{t.noPagesYet}</p>
            )}
          </div>
        </div>
      ) : activeTab === 'calculators' ? (
        <div className="space-y-6">
          {isAdmin && (
            <button 
              onClick={handleAddCalculator}
              className="w-full bg-[#1b7d36] text-white py-4 rounded-2xl font-bold shadow-md flex items-center justify-center gap-2"
            >
              <Plus size={20} /> {t.addNewCalculator}
            </button>
          )}

          <div className="space-y-4">
            {calculators.map((calc, index) => (
              <div key={`${calc.id}-${index}`} className="bg-white p-6 rounded-[32px] border border-black/5 shadow-sm flex items-center justify-between">
                <div className="space-y-1">
                  <h4 className="font-bold text-stone-800">{calc.title}</h4>
                  <p className="text-xs text-stone-400">{calc.rows.length} rows • {calc.baseRate} kg/acre</p>
                </div>
                {isAdmin && (
                  <div className="flex gap-4">
                    <button 
                      onClick={() => setEditingCalculator(calc)}
                      className="text-blue-500 font-bold text-sm"
                    >
                      {t.edit}
                    </button>
                    <button 
                      onClick={() => handleDeleteCalculator(calc.id)}
                      className="text-rose-500 font-bold text-sm"
                    >
                      {t.delete}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : activeTab === 'videos' ? (
        <div className="space-y-6">
          {/* Video Editor Modal-like section */}
          {editingVideo && (
            <div className="bg-blue-50 p-6 rounded-[32px] border border-blue-200 shadow-sm space-y-4">
              <h3 className="text-blue-800 font-bold">{t.editVideo}</h3>
              <div className="space-y-3">
                <input 
                  type="text" 
                  value={editingVideo.title}
                  onChange={e => setEditingVideo({ ...editingVideo, title: e.target.value })}
                  placeholder={t.videoTitle}
                  className="w-full bg-white border border-blue-200 rounded-2xl px-5 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
                <input 
                  type="text" 
                  value={editingVideo.url}
                  onChange={e => setEditingVideo({ ...editingVideo, url: e.target.value })}
                  placeholder={t.videoUrl}
                  className="w-full bg-white border border-blue-200 rounded-2xl px-5 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />

                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">Placement Locations</label>
                  <div className="flex flex-wrap gap-2">
                    {['home', 'videos', 'handbook', 'advisor', 'forum'].map(loc => (
                      <label key={loc} className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-xl border border-stone-200 cursor-pointer hover:border-blue-500 transition-colors">
                        <input 
                          type="checkbox" 
                          checked={(editingVideo.placement || []).includes(loc)}
                          onChange={e => {
                            const current = editingVideo.placement || [];
                            const updated = e.target.checked 
                              ? [...current, loc] 
                              : current.filter(l => l !== loc);
                            setEditingVideo({ ...editingVideo, placement: updated });
                          }}
                          className="w-3 h-3 text-blue-600 rounded"
                        />
                        <span className="text-[10px] font-bold capitalize text-stone-600">{loc}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-blue-400 uppercase">{t.thumbnailImage}</label>
                  <div className="flex items-center gap-4">
                    {editingVideo.thumbnail && (
                      <img src={editingVideo.thumbnail} className="w-20 h-12 object-cover rounded-lg border border-blue-200" referrerPolicy="no-referrer" />
                    )}
                    <label className="flex-1 cursor-pointer bg-white border-2 border-dashed border-blue-200 rounded-2xl p-4 flex flex-col items-center justify-center hover:border-blue-400 transition-colors">
                      <Upload size={20} className="text-blue-400 mb-1" />
                      <span className="text-xs font-bold text-blue-500">{t.uploadThumbnail}</span>
                      <input 
                        type="file" 
                        accept="image/*" 
                        className="hidden" 
                        onChange={e => handleFileUpload(e, (base64) => setEditingVideo({ ...editingVideo, thumbnail: base64 }))} 
                      />
                    </label>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={handleSaveVideo}
                    className="flex-1 bg-blue-600 text-white py-3 rounded-2xl font-bold shadow-md"
                  >
                    {t.save}
                  </button>
                  <button 
                    onClick={() => setEditingVideo(null)}
                    className="flex-1 bg-stone-200 text-stone-600 py-3 rounded-2xl font-bold"
                  >
                    {t.cancel}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Add Video Section */}
          {isAdmin && !editingVideo && (
            <div className="bg-white p-6 rounded-[32px] border border-black/5 shadow-sm space-y-4">
              <h3 className="text-[#1b7d36] font-bold">{t.addNewVideo}</h3>
              <div className="space-y-3">
                <input 
                  type="text" 
                  value={newVideoTitle}
                  onChange={e => setNewVideoTitle(e.target.value)}
                  placeholder={t.videoTitle}
                  className="w-full bg-[#f8f9fa] border border-stone-200 rounded-2xl px-5 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
                />
                <input 
                  type="text" 
                  value={newVideoUrl}
                  onChange={e => setNewVideoUrl(e.target.value)}
                  placeholder={t.videoUrl}
                  className="w-full bg-[#f8f9fa] border border-stone-200 rounded-2xl px-5 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
                />

                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">Placement Locations</label>
                  <div className="flex flex-wrap gap-2">
                    {['home', 'videos', 'handbook', 'advisor', 'forum'].map(loc => (
                      <label key={loc} className="flex items-center gap-2 bg-[#f8f9fa] px-3 py-1.5 rounded-xl border border-stone-200 cursor-pointer hover:border-[#1b7d36] transition-colors">
                        <input 
                          type="checkbox" 
                          checked={newVideoPlacements.includes(loc)}
                          onChange={e => {
                            const updated = e.target.checked 
                              ? [...newVideoPlacements, loc] 
                              : newVideoPlacements.filter(l => l !== loc);
                            setNewVideoPlacements(updated);
                          }}
                          className="w-3 h-3 text-[#1b7d36] rounded"
                        />
                        <span className="text-[10px] font-bold capitalize text-stone-600">{loc}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-stone-400 uppercase">{t.thumbnailImage}</label>
                  <div className="flex items-center gap-4">
                    {newVideoThumbnail && (
                      <img src={newVideoThumbnail} className="w-20 h-12 object-cover rounded-lg border border-stone-200" referrerPolicy="no-referrer" />
                    )}
                    <label className="flex-1 cursor-pointer bg-[#f8f9fa] border-2 border-dashed border-stone-200 rounded-2xl p-4 flex flex-col items-center justify-center hover:border-[#1b7d36] transition-colors">
                      <Upload size={20} className="text-stone-400 mb-1" />
                      <span className="text-xs font-bold text-stone-500">{t.uploadThumbnail}</span>
                      <input 
                        type="file" 
                        accept="image/*" 
                        className="hidden" 
                        onChange={e => handleFileUpload(e, (base64) => setNewVideoThumbnail(base64))} 
                      />
                    </label>
                  </div>
                </div>
                <button 
                  onClick={handleAddVideo}
                  className="w-full bg-[#1b7d36] text-white py-3 rounded-2xl font-bold shadow-md flex items-center justify-center gap-2"
                >
                  <Plus size={20} /> {t.addNewVideo}
                </button>
              </div>
            </div>
          )}

          {/* Videos List */}
          <div className="space-y-4">
            {videos.map((video, index) => (
              <div key={`${video.id}-${index}`} className="bg-white p-4 rounded-2xl border border-black/5 shadow-sm flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex flex-col gap-1 mr-2">
                    <button 
                      onClick={() => handleMoveVideo(video.id, 'up')}
                      disabled={index === 0}
                      className={`p-1 rounded-md hover:bg-stone-100 ${index === 0 ? 'text-stone-200 cursor-not-allowed' : 'text-stone-400'}`}
                    >
                      <ChevronUp size={14} />
                    </button>
                    <button 
                      onClick={() => handleMoveVideo(video.id, 'down')}
                      disabled={index === videos.length - 1}
                      className={`p-1 rounded-md hover:bg-stone-100 ${index === videos.length - 1 ? 'text-stone-200 cursor-not-allowed' : 'text-stone-400'}`}
                    >
                      <ChevronDown size={14} />
                    </button>
                  </div>
                  <div className="w-12 h-8 bg-stone-100 rounded flex items-center justify-center">
                    <Video size={16} className="text-stone-400" />
                  </div>
                  <span className="font-bold text-stone-700 text-sm line-clamp-1">{video.title}</span>
                </div>
                {isAdmin && (
                  <div className="flex gap-4">
                    <button 
                      onClick={() => setEditingVideo(video)}
                      className="text-blue-500 font-bold text-sm"
                    >
                      {t.edit}
                    </button>
                    <button 
                      onClick={() => handleDeleteVideo(video.id)}
                      className="text-rose-500 font-bold text-sm"
                    >
                      {t.delete}
                    </button>
                  </div>
                )}
              </div>
            ))}
            {videos.length === 0 && (
              <p className="text-center text-stone-400 text-xs py-10 italic">{t.noVideosYet}</p>
            )}
          </div>
        </div>
      ) : activeTab === 'menu' ? (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-[32px] border border-black/5 shadow-sm space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-[#1b7d36] font-bold">{t.menuManagement}</h3>
              {isAdmin && (
                <button 
                  onClick={() => setIsAddingMenuItem(true)}
                  className="bg-[#1b7d36] text-white p-2 rounded-xl shadow-md"
                >
                  <Plus size={20} />
                </button>
              )}
            </div>

            {isAddingMenuItem && (
              <div className="bg-stone-50 p-4 rounded-2xl border border-stone-200 space-y-3">
                <input 
                  type="text" 
                  value={newMenuItem.labelKey}
                  onChange={e => setNewMenuItem({ ...newMenuItem, labelKey: e.target.value })}
                  placeholder={t.labelKey}
                  className="w-full bg-white border border-stone-200 rounded-xl px-4 py-2 text-sm"
                />
                <select 
                  value={newMenuItem.screen}
                  onChange={e => setNewMenuItem({ ...newMenuItem, screen: e.target.value as any })}
                  className="w-full bg-white border border-stone-200 rounded-xl px-4 py-2 text-sm"
                >
                  <option value="home">Home</option>
                  <option value="forum">Forum</option>
                  <option value="videos">Videos</option>
                  <option value="handbook">Handbook</option>
                  <option value="advisor">AI Advisor</option>
                  <option value="marketplace">Marketplace</option>
                  <option value="plantDoctor">Plant Doctor</option>
                  <option value="expertConnect">Expert Connect</option>
                  <option value="crops">Crops</option>
                  <option value="inputs">Inputs</option>
                  <option value="settings">Settings</option>
                  <option value="privacy">Privacy</option>
                  <option value="terms">Terms</option>
                  <option value="admin">Admin</option>
                </select>
                <input 
                  type="text" 
                  value={newMenuItem.iconName}
                  onChange={e => setNewMenuItem({ ...newMenuItem, iconName: e.target.value })}
                  placeholder="Icon Name (Lucide icon name)"
                  className="w-full bg-white border border-stone-200 rounded-xl px-4 py-2 text-sm"
                />
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm text-stone-600">
                    <input 
                      type="checkbox" 
                      checked={newMenuItem.visible}
                      onChange={e => setNewMenuItem({ ...newMenuItem, visible: e.target.checked })}
                    />
                    {t.visible}
                  </label>
                  <label className="flex items-center gap-2 text-sm text-stone-600">
                    <input 
                      type="checkbox" 
                      checked={newMenuItem.isAdminOnly}
                      onChange={e => setNewMenuItem({ ...newMenuItem, isAdminOnly: e.target.checked })}
                    />
                    {t.adminOnly}
                  </label>
                </div>
                <div className="flex gap-2">
                  <button onClick={handleAddMenuItem} className="flex-1 bg-[#1b7d36] text-white py-2 rounded-xl text-sm font-bold">Add</button>
                  <button onClick={() => setIsAddingMenuItem(false)} className="flex-1 bg-stone-200 text-stone-600 py-2 rounded-xl text-sm font-bold">Cancel</button>
                </div>
              </div>
            )}

            {editingMenuItem && (
              <div className="bg-amber-50 p-4 rounded-2xl border border-amber-200 space-y-3">
                <input 
                  type="text" 
                  value={editingMenuItem.labelKey}
                  onChange={e => setEditingMenuItem({ ...editingMenuItem, labelKey: e.target.value })}
                  placeholder={t.labelKey}
                  className="w-full bg-white border border-amber-200 rounded-xl px-4 py-2 text-sm"
                />
                <select 
                  value={editingMenuItem.screen}
                  onChange={e => setEditingMenuItem({ ...editingMenuItem, screen: e.target.value as any })}
                  className="w-full bg-white border border-amber-200 rounded-xl px-4 py-2 text-sm"
                >
                  <option value="home">Home</option>
                  <option value="forum">Forum</option>
                  <option value="videos">Videos</option>
                  <option value="handbook">Handbook</option>
                  <option value="advisor">AI Advisor</option>
                  <option value="marketplace">Marketplace</option>
                  <option value="plantDoctor">Plant Doctor</option>
                  <option value="expertConnect">Expert Connect</option>
                  <option value="crops">Crops</option>
                  <option value="inputs">Inputs</option>
                  <option value="settings">Settings</option>
                  <option value="privacy">Privacy</option>
                  <option value="terms">Terms</option>
                  <option value="admin">Admin</option>
                </select>
                <input 
                  type="text" 
                  value={editingMenuItem.iconName}
                  onChange={e => setEditingMenuItem({ ...editingMenuItem, iconName: e.target.value })}
                  placeholder="Icon Name"
                  className="w-full bg-white border border-amber-200 rounded-xl px-4 py-2 text-sm"
                />
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm text-stone-600">
                    <input 
                      type="checkbox" 
                      checked={editingMenuItem.visible}
                      onChange={e => setEditingMenuItem({ ...editingMenuItem, visible: e.target.checked })}
                    />
                    {t.visible}
                  </label>
                  <label className="flex items-center gap-2 text-sm text-stone-600">
                    <input 
                      type="checkbox" 
                      checked={editingMenuItem.isAdminOnly}
                      onChange={e => setEditingMenuItem({ ...editingMenuItem, isAdminOnly: e.target.checked })}
                    />
                    {t.adminOnly}
                  </label>
                </div>
                <div className="flex gap-2">
                  <button onClick={handleSaveMenuItem} className="flex-1 bg-amber-600 text-white py-2 rounded-xl text-sm font-bold">Save</button>
                  <button onClick={() => setEditingMenuItem(null)} className="flex-1 bg-stone-200 text-stone-600 py-2 rounded-xl text-sm font-bold">Cancel</button>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {menuItems.map((item, index) => (
                <div key={`${item.id}-${index}`} className="bg-white p-4 rounded-2xl border border-black/5 flex items-center justify-between shadow-sm">
                  <div className="flex items-center gap-4">
                    <div className="text-stone-400">
                      {getIcon(item.iconName)}
                    </div>
                    <div>
                      <h4 className="font-bold text-stone-800">{t[item.labelKey] || item.labelKey}</h4>
                      <p className="text-xs text-stone-400 uppercase tracking-wider">{item.screen} {item.isAdminOnly && `(${t.adminOnly})`}</p>
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => setEditingMenuItem(item)}
                        className="p-2 text-blue-500 hover:bg-blue-50 rounded-xl transition-colors"
                      >
                        <Plus size={20} className="rotate-45" />
                      </button>
                      <button 
                        onClick={() => handleDeleteMenuItem(item)}
                        className="p-2 text-rose-500 hover:bg-rose-50 rounded-xl transition-colors"
                      >
                        <X size={20} />
                      </button>
                      <button 
                        disabled={index === 0}
                        onClick={() => handleMoveMenuItem(item.id, 'up')}
                        className="p-2 text-stone-400 hover:bg-stone-50 rounded-xl transition-colors disabled:opacity-20"
                      >
                        <ArrowUp size={20} />
                      </button>
                      <button 
                        disabled={index === menuItems.length - 1}
                        onClick={() => handleMoveMenuItem(item.id, 'down')}
                        className="p-2 text-stone-400 hover:bg-stone-50 rounded-xl transition-colors disabled:opacity-20"
                      >
                        <ArrowDown size={20} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : activeTab === 'marketplace' ? (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-[32px] border border-black/5 shadow-sm space-y-4">
            <h3 className="text-[#1b7d36] font-bold flex items-center gap-2">
              <UserCircle size={20} /> Farmer Profile Configuration
            </h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs font-bold text-stone-400 uppercase ml-1">Available Certifications</label>
                <div className="flex flex-wrap gap-2">
                  {marketplaceConfig.availableCertifications.map(cert => (
                    <div key={cert} className="bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-2 border border-emerald-100">
                      {cert}
                      {isAdmin && (
                        <button 
                          onClick={async () => {
                            const updated = marketplaceConfig.availableCertifications.filter(c => c !== cert);
                            try {
                              await setDoc(doc(db, 'marketplace_config', 'farmer_profile'), { ...marketplaceConfig, availableCertifications: updated });
                              setToast({ message: "Certification removed", type: 'success' });
                            } catch (error) {
                              handleFirestoreError(error, OperationType.WRITE, 'marketplace_config/farmer_profile');
                            }
                          }}
                          className="hover:text-rose-500 transition-colors"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {isAdmin && (
                  <div className="flex gap-2 mt-2">
                    <input 
                      type="text" 
                      id="newCertInput"
                      placeholder="Add new certification..."
                      className="flex-1 bg-stone-50 border border-stone-200 px-4 py-2 rounded-xl text-sm outline-none focus:ring-2 focus:ring-emerald-500"
                      onKeyDown={async (e) => {
                        if (e.key === 'Enter') {
                          const val = (e.target as HTMLInputElement).value.trim();
                          if (val && !(marketplaceConfig.availableCertifications || []).includes(val)) {
                            const updated = [...(marketplaceConfig.availableCertifications || []), val];
                            try {
                              await setDoc(doc(db, 'marketplace_config', 'farmer_profile'), { ...marketplaceConfig, availableCertifications: updated });
                              (e.target as HTMLInputElement).value = '';
                              setToast({ message: "Certification added", type: 'success' });
                            } catch (error) {
                              handleFirestoreError(error, OperationType.WRITE, 'marketplace_config/farmer_profile');
                            }
                          }
                        }
                      }}
                    />
                    <button 
                      onClick={async () => {
                        const input = document.getElementById('newCertInput') as HTMLInputElement;
                        const val = input.value.trim();
                        if (val && !(marketplaceConfig.availableCertifications || []).includes(val)) {
                          const updated = [...(marketplaceConfig.availableCertifications || []), val];
                          try {
                            await setDoc(doc(db, 'marketplace_config', 'farmer_profile'), { ...marketplaceConfig, availableCertifications: updated });
                            input.value = '';
                            setToast({ message: "Certification added", type: 'success' });
                          } catch (error) {
                            handleFirestoreError(error, OperationType.WRITE, 'marketplace_config/farmer_profile');
                          }
                        }
                      }}
                      className="bg-emerald-600 text-white p-2 rounded-xl shadow-sm hover:bg-emerald-700 transition-colors"
                    >
                      <Plus size={20} />
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-2 pt-4 border-t border-stone-100">
                <label className="text-xs font-bold text-stone-400 uppercase ml-1">Farming Methods</label>
                <div className="flex flex-wrap gap-2">
                  {(marketplaceConfig.farmingMethods || []).map(method => (
                    <div key={method} className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-2 border border-blue-100">
                      {method}
                      {isAdmin && (
                        <button 
                          onClick={async () => {
                            const updated = marketplaceConfig.farmingMethods.filter(m => m !== method);
                            try {
                              await setDoc(doc(db, 'marketplace_config', 'farmer_profile'), { ...marketplaceConfig, farmingMethods: updated });
                              setToast({ message: "Farming method removed", type: 'success' });
                            } catch (error) {
                              handleFirestoreError(error, OperationType.WRITE, 'marketplace_config/farmer_profile');
                            }
                          }}
                          className="hover:text-rose-500 transition-colors"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {isAdmin && (
                  <div className="flex gap-2 mt-2">
                    <input 
                      type="text" 
                      id="newMethodInput"
                      placeholder="Add new farming method..."
                      className="flex-1 bg-stone-50 border border-stone-200 px-4 py-2 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500"
                      onKeyDown={async (e) => {
                        if (e.key === 'Enter') {
                          const val = (e.target as HTMLInputElement).value.trim();
                          if (val && !(marketplaceConfig.farmingMethods || []).includes(val)) {
                            const updated = [...(marketplaceConfig.farmingMethods || []), val];
                            try {
                              await setDoc(doc(db, 'marketplace_config', 'farmer_profile'), { ...marketplaceConfig, farmingMethods: updated });
                              (e.target as HTMLInputElement).value = '';
                              setToast({ message: "Farming method added", type: 'success' });
                            } catch (error) {
                              handleFirestoreError(error, OperationType.WRITE, 'marketplace_config/farmer_profile');
                            }
                          }
                        }
                      }}
                    />
                    <button 
                      onClick={async () => {
                        const input = document.getElementById('newMethodInput') as HTMLInputElement;
                        const val = input.value.trim();
                        if (val && !(marketplaceConfig.farmingMethods || []).includes(val)) {
                          const updated = [...(marketplaceConfig.farmingMethods || []), val];
                          try {
                            await setDoc(doc(db, 'marketplace_config', 'farmer_profile'), { ...marketplaceConfig, farmingMethods: updated });
                            input.value = '';
                            setToast({ message: "Farming method added", type: 'success' });
                          } catch (error) {
                            handleFirestoreError(error, OperationType.WRITE, 'marketplace_config/farmer_profile');
                          }
                        }
                      }}
                      className="bg-blue-600 text-white p-2 rounded-xl shadow-sm hover:bg-blue-700 transition-colors"
                    >
                      <Plus size={20} />
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-2 pt-4 border-t border-stone-100">
                <label className="text-xs font-bold text-stone-400 uppercase ml-1">Product Categories</label>
                <div className="flex flex-wrap gap-2">
                  {(marketplaceConfig.productCategories || []).map(category => (
                    <div key={category} className="bg-orange-50 text-orange-700 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-2 border border-orange-100">
                      {category}
                      {isAdmin && (
                        <button 
                          onClick={async () => {
                            const updated = marketplaceConfig.productCategories.filter(c => c !== category);
                            try {
                              await setDoc(doc(db, 'marketplace_config', 'farmer_profile'), { ...marketplaceConfig, productCategories: updated });
                              setToast({ message: "Category removed", type: 'success' });
                            } catch (error) {
                              handleFirestoreError(error, OperationType.WRITE, 'marketplace_config/farmer_profile');
                            }
                          }}
                          className="hover:text-rose-500 transition-colors"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {isAdmin && (
                  <div className="flex gap-2 mt-2">
                    <input 
                      type="text" 
                      id="newCategoryInput"
                      placeholder="Add new category (e.g. Vegetables)..."
                      className="flex-1 bg-stone-50 border border-stone-200 px-4 py-2 rounded-xl text-sm outline-none focus:ring-2 focus:ring-orange-500"
                      onKeyDown={async (e) => {
                        if (e.key === 'Enter') {
                          const val = (e.target as HTMLInputElement).value.trim();
                          if (val && !(marketplaceConfig.productCategories || []).includes(val)) {
                            const updated = [...(marketplaceConfig.productCategories || []), val];
                            try {
                              await setDoc(doc(db, 'marketplace_config', 'farmer_profile'), { ...marketplaceConfig, productCategories: updated });
                              (e.target as HTMLInputElement).value = '';
                              setToast({ message: "Category added", type: 'success' });
                            } catch (error) {
                              handleFirestoreError(error, OperationType.WRITE, 'marketplace_config/farmer_profile');
                            }
                          }
                        }
                      }}
                    />
                    <button 
                      onClick={async () => {
                        const input = document.getElementById('newCategoryInput') as HTMLInputElement;
                        const val = input.value.trim();
                        if (val && !(marketplaceConfig.productCategories || []).includes(val)) {
                          const updated = [...(marketplaceConfig.productCategories || []), val];
                          try {
                            await setDoc(doc(db, 'marketplace_config', 'farmer_profile'), { ...marketplaceConfig, productCategories: updated });
                            input.value = '';
                            setToast({ message: "Category added", type: 'success' });
                          } catch (error) {
                            handleFirestoreError(error, OperationType.WRITE, 'marketplace_config/farmer_profile');
                          }
                        }
                      }}
                      className="bg-orange-600 text-white p-2 rounded-xl shadow-sm hover:bg-orange-700 transition-colors"
                    >
                      <Plus size={20} />
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-2 pt-4 border-t border-stone-100">
                <label className="text-xs font-bold text-stone-400 uppercase ml-1">Visible Profile Fields</label>
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(marketplaceConfig.visibleFields || {}).map(([key, visible]) => (
                    <label key={key} className="flex items-center gap-3 bg-stone-50 p-3 rounded-2xl border border-stone-100 cursor-pointer hover:bg-stone-100 transition-colors">
                      <input 
                        type="checkbox" 
                        checked={visible}
                        onChange={async (e) => {
                          const updatedFields = { ...marketplaceConfig.visibleFields, [key]: e.target.checked };
                          try {
                            await setDoc(doc(db, 'marketplace_config', 'farmer_profile'), { ...marketplaceConfig, visibleFields: updatedFields });
                            setToast({ message: `Field visibility updated`, type: 'success' });
                          } catch (error) {
                            handleFirestoreError(error, OperationType.WRITE, 'marketplace_config/farmer_profile');
                          }
                        }}
                        className="w-5 h-5 rounded-lg text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="text-sm font-bold text-stone-700 capitalize">{key}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-[32px] border border-black/5 shadow-sm space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-[#1b7d36] font-bold flex items-center gap-2">
                <ShoppingBag size={20} /> {t.marketplace} Management
              </h3>
              <button 
                onClick={() => setIsAddingMarketplaceListing(true)}
                className="text-[#1b7d36] font-bold text-sm flex items-center gap-1"
              >
                <Plus size={16} /> Add Listing
              </button>
            </div>
            {loadingMarketplace ? (
              <div className="flex justify-center py-10">
                <Loader2 className="animate-spin text-emerald-600" size={32} />
              </div>
            ) : marketplaceListings.length === 0 ? (
              <p className="text-center text-stone-400 py-10 italic">No listings found.</p>
            ) : (
              <div className="space-y-4">
                {marketplaceListings.map((listing) => (
                  <div key={listing.id} className="bg-stone-50 p-4 rounded-2xl border border-stone-100 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <img src={listing.photoUrl || 'https://picsum.photos/seed/farm/100/100'} className="w-12 h-12 rounded-lg object-cover" referrerPolicy="no-referrer" />
                      <div>
                        <h4 className="font-bold text-stone-800 text-sm">{listing.productName}</h4>
                        <p className="text-[10px] text-stone-500">{listing.authorName} • ₹{listing.price}/{listing.unit}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isAdmin && (
                        <>
                          <button 
                            onClick={() => setEditingMarketplaceListing(listing)}
                            className="p-2 text-[#1b7d36] hover:bg-[#1b7d36]/5 rounded-xl transition-colors"
                          >
                            <Settings size={18} />
                          </button>
                          <button 
                            onClick={() => handleDeleteMarketplaceListing(listing.id)}
                            className="p-2 text-rose-500 hover:bg-rose-50 rounded-xl transition-colors"
                          >
                            <Trash2 size={18} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : activeTab === 'ad_config' ? (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-[32px] border border-black/5 shadow-sm space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-[#f0fdf4] rounded-xl">
                <ShieldCheck className="text-[#1b7d36]" size={20} />
              </div>
              <h3 className="text-[#1b7d36] font-bold">{t.adMobConfig}</h3>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-bold text-stone-500 uppercase tracking-wider">{t.bannerAdId}</label>
                <input 
                  type="text" 
                  value={admobConfig.bannerId}
                  onChange={(e) => setAdmobConfig({...admobConfig, bannerId: e.target.value})}
                  className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#1b7d36]"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-stone-500 uppercase tracking-wider">{t.interstitialAdId}</label>
                <input 
                  type="text" 
                  value={admobConfig.interstitialId}
                  onChange={(e) => setAdmobConfig({...admobConfig, interstitialId: e.target.value})}
                  className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#1b7d36]"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-stone-500 uppercase tracking-wider">{t.rewardedAdId}</label>
                <input 
                  type="text" 
                  value={admobConfig.rewardedId}
                  onChange={(e) => setAdmobConfig({...admobConfig, rewardedId: e.target.value})}
                  className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#1b7d36]"
                />
              </div>
              <div className="flex items-center justify-between p-4 bg-stone-50 rounded-2xl border border-stone-100">
                <div className="space-y-0.5">
                  <label htmlFor="test-mode" className="text-sm font-bold text-stone-700">{t.testMode}</label>
                  <p className="text-[10px] text-stone-500 font-medium">
                    {admobConfig.isTesting ? 'App is using Google Test Ad IDs' : 'App is using Your Production Ad IDs'}
                  </p>
                </div>
                <div 
                  onClick={() => setAdmobConfig({...admobConfig, isTesting: !admobConfig.isTesting})}
                  className={`w-14 h-8 flex items-center rounded-full p-1 cursor-pointer transition-all ${admobConfig.isTesting ? 'bg-amber-500' : 'bg-[#1b7d36]'}`}
                >
                  <motion.div 
                    layout
                    className="bg-white w-6 h-6 rounded-full shadow-md"
                    animate={{ x: admobConfig.isTesting ? 24 : 0 }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2">
                <button 
                  onClick={() => AdMobService.showInterstitial()}
                  className="bg-stone-100 text-stone-700 py-2 rounded-xl text-xs font-bold hover:bg-stone-200"
                >
                  {t.showInterstitialAd}
                </button>
                <button 
                  onClick={() => AdMobService.showRewardedAd((reward) => setToast({message: `${t.adRewardSuccess} (${reward.amount} ${reward.type})`, type: 'success'}))}
                  className="bg-stone-100 text-stone-700 py-2 rounded-xl text-xs font-bold hover:bg-stone-200"
                >
                  {t.showRewardedAd}
                </button>
              </div>
            </div>

            <button 
              onClick={async () => {
                setIsAdMobSaving(true);
                try {
                  await setDoc(doc(db, 'app_config', 'admob'), admobConfig);
                  setToast({ message: t.adMobSaved, type: 'success' });
                } catch (error) {
                  handleFirestoreError(error, OperationType.UPDATE, 'app_config/admob');
                } finally {
                  setIsAdMobSaving(false);
                }
              }}
              disabled={isAdMobSaving}
              className="w-full bg-[#1b7d36] text-white py-4 rounded-3xl font-bold shadow-md hover:bg-[#16652b] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isAdMobSaving && <Loader2 size={20} className="animate-spin" />}
              {isAdMobSaving ? (language === 'te' ? 'సేవ్ అవుతోంది...' : 'Saving...') : t.saveAdMob}
            </button>
          </div>
        </div>
      ) : activeTab === 'crop_calendar' ? (
        <CropCalendarAdmin language={language} t={t} />
      ) : activeTab === 'market_prices' ? (
        <div className="space-y-6">
          <div className="flex justify-between items-center bg-emerald-50 p-6 rounded-[32px] border border-emerald-100 shadow-sm">
            <div>
              <h3 className="text-[#1b7d36] font-bold">{t.localMarketPrices}</h3>
              <p className="text-xs text-stone-600">{t.manageVerifiedPrices}</p>
            </div>
            <div className="flex gap-2">
               <button 
                onClick={handleSeedMarketPrices}
                className="bg-emerald-600 text-white px-4 py-2 rounded-xl text-xs font-bold shadow-md hover:bg-emerald-700 flex items-center gap-2 transition-all hover:scale-105 active:scale-95"
              >
                <Leaf size={14} />
                {t.seedCommonPrices}
              </button>
              <button 
                onClick={() => setIsAddingMarketPrice(!isAddingMarketPrice)}
                className="bg-stone-900 text-white px-4 py-2 rounded-xl text-xs font-bold shadow-md flex items-center gap-2 transition-all"
              >
                {isAddingMarketPrice ? t.cancel : `+ ${t.addNewPrice}`}
              </button>
            </div>
          </div>

          {isAddingMarketPrice && (
            <motion.form 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              onSubmit={handleSaveMarketPrice} 
              className="bg-white p-6 rounded-[32px] border border-black/5 shadow-sm space-y-4"
            >
              <div className="flex justify-between items-center mb-2">
                <h4 className="text-stone-800 font-bold">{editingMarketPrice ? t.editPrice || 'Edit Price' : t.addNewPrice}</h4>
                {editingMarketPrice && (
                  <button 
                    type="button"
                    onClick={() => {
                      setEditingMarketPrice(null);
                      setIsAddingMarketPrice(false);
                      setNewMarketPrice({ cropName: '', price: 0, trend: 'stable' });
                    }}
                    className="text-stone-400 hover:text-stone-600"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-stone-500 mb-1 block">{t.cropName}</label>
                  <input 
                    type="text" 
                    required
                    value={newMarketPrice.cropName}
                    onChange={e => setNewMarketPrice({ ...newMarketPrice, cropName: e.target.value })}
                    className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
                    placeholder="e.g. వరి (Paddy)"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-stone-500 mb-1 block">{t.pricePerQuintal}</label>
                  <input 
                    type="number" 
                    required
                    value={newMarketPrice.price || ''}
                    onChange={e => setNewMarketPrice({ ...newMarketPrice, price: parseFloat(e.target.value) })}
                    className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
                    placeholder="e.g. 1800"
                  />
                </div>
              </div>
              <div className="flex items-center gap-4">
                <label className="text-xs font-bold text-stone-500">{t.trend}:</label>
                <div className="flex gap-2">
                  {['up', 'down', 'stable'].map(type => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setNewMarketPrice({ ...newMarketPrice, trend: type as any })}
                      className={`px-4 py-2 rounded-xl text-xs font-bold capitalize transition-all ${newMarketPrice.trend === type ? 'bg-stone-900 text-white shadow-md' : 'bg-stone-100 text-stone-500 hover:bg-stone-200'}`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
                <button type="submit" className="ml-auto bg-[#1b7d36] text-white px-8 py-2 rounded-xl font-bold shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all">
                  {t.savePrice}
                </button>
              </div>
            </motion.form>
          )}

          <div className="grid gap-3">
            <AnimatePresence mode="popLayout">
              {marketPrices.map(price => (
                <motion.div 
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  key={price.id} 
                  className="bg-white p-4 rounded-2xl border border-black/5 flex items-center justify-between shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${price.trend === 'up' ? 'bg-emerald-100 text-emerald-600' : price.trend === 'down' ? 'bg-rose-100 text-stone-600' : 'bg-stone-100 text-stone-400'}`}>
                      {price.trend === 'up' ? <TrendingUp size={20} /> : price.trend === 'down' ? <TrendingDown size={20} /> : <Minus size={20} />}
                    </div>
                    <div>
                      <div className="font-bold text-stone-800">{price.cropName}</div>
                      <div className="text-[10px] text-stone-500 font-bold uppercase tracking-wider">
                        {t.lastUpdated}: {price.lastUpdated ? new Date(price.lastUpdated).toLocaleDateString() : 'Just now'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <div className="font-black text-[#1b7d36] text-lg">₹{price.price?.toLocaleString()}</div>
                      <div className="text-[10px] text-stone-400 font-bold uppercase">/ quintal</div>
                    </div>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => handleEditMarketPrice(price)} 
                        className="text-stone-400 hover:text-emerald-500 hover:bg-emerald-50 p-2 rounded-lg transition-all"
                        title={t.edit || 'Edit'}
                      >
                        <Settings size={18} />
                      </button>
                      <button 
                        onClick={() => handleDeleteMarketPrice(price.id)} 
                        className="text-stone-400 hover:text-rose-500 hover:bg-rose-50 p-2 rounded-lg transition-all"
                        title={t.delete || 'Delete'}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
            {marketPrices.length === 0 && (
              <div className="text-center py-20 bg-stone-50/50 rounded-[40px] border border-dashed border-stone-200">
                <div className="flex justify-center mb-2"><Store size={32} className="text-stone-300" /></div>
                <div className="text-stone-400 italic text-sm">{t.noVerifiedPrices}</div>
                <div className="text-xs text-stone-300 mt-1">{t.seedPricesInstructions}</div>
              </div>
            )}
          </div>
        </div>
      ) : activeTab === 'users' && isAdmin ? (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-emerald-50 p-6 rounded-[32px] border border-emerald-100 shadow-sm">
            <div>
              <h3 className="text-[#1b7d36] font-bold text-xl">{language === 'te' ? 'వినియోగదారుల నిర్వహణ' : 'User Management'}</h3>
              <p className="text-xs text-stone-600">{language === 'te' ? 'వినియోగదారుల వివరాలను చూడండి మరియు ఎగుమతి చేయండి' : 'View and export user details'}</p>
            </div>
            <button
              onClick={handleExportUsers}
              disabled={isExporting || allUsers.length === 0}
              className="px-6 py-3 bg-emerald-600 text-white rounded-2xl font-bold shadow-lg shadow-emerald-600/20 hover:bg-emerald-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <FileText size={20} />
              {isExporting ? (language === 'te' ? 'ఎగుమతి అవుతోంది...' : 'Exporting...') : `${language === 'te' ? 'ఎక్సెల్ కి ఎగుమతి చేయండి' : 'Export to Excel'} (${allUsers.length})`}
            </button>
          </div>

          <div className="bg-white rounded-[40px] shadow-sm border border-stone-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left min-w-[600px]">
                <thead>
                  <tr className="bg-stone-50 border-b border-stone-100">
                    <th className="px-6 py-4 font-bold text-stone-600 text-sm">User</th>
                    <th className="px-6 py-4 font-bold text-stone-600 text-sm">Contact</th>
                    <th className="px-6 py-4 font-bold text-stone-600 text-sm">Location</th>
                    <th className="px-6 py-4 font-bold text-stone-600 text-sm">Role</th>
                    <th className="px-6 py-4 font-bold text-stone-600 text-sm">Joined</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-50">
                  {allUsers.map((u) => (
                    <tr key={u.id} className="hover:bg-stone-50/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {u.photoURL ? (
                            <img src={u.photoURL} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-emerald-100 shadow-sm" referrerPolicy="no-referrer" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-bold text-sm border-2 border-emerald-50">
                              {(u.displayName || u.email || '?')[0].toUpperCase()}
                            </div>
                          )}
                          <div>
                            <div className="font-bold text-stone-800 text-sm">{u.displayName || 'Unnamed User'}</div>
                            <div className="text-[10px] text-stone-400 font-mono tracking-tighter">{u.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-stone-600 flex flex-col">
                          <span className="flex items-center gap-1"><Mail size={12} className="text-stone-300" /> {u.email}</span>
                          {u.phone && <span className="flex items-center gap-1 text-stone-400 mt-1"><Phone size={12} /> {u.phone}</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-stone-600 flex items-center gap-1">
                          <MapPin size={14} className="text-stone-300" /> {u.location || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${u.role === 'admin' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                          {u.role || 'user'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-xs text-stone-500 font-medium">
                        {u.createdAt?.toDate?.()?.toLocaleDateString() || u.createdAt?.seconds ? new Date(u.createdAt.seconds * 1000).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))}
                  {allUsers.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-6 py-20 text-center text-stone-400 italic">No users found in database.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : activeTab === 'experts' ? (
        <div className="space-y-6">
          <div className="flex gap-2 bg-stone-100 p-1 rounded-xl mb-4">
            <button 
              onClick={() => setViewingMessages(false)}
              className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${!viewingMessages ? 'bg-white text-[#1b7d36] shadow-sm' : 'text-stone-400'}`}
            >
              {t.manageExperts || 'Manage Experts'}
            </button>
            <button 
              onClick={() => setViewingMessages(true)}
              className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${viewingMessages ? 'bg-white text-[#1b7d36] shadow-sm' : 'text-stone-400'}`}
            >
              {t.viewMessages || 'User Messages'} {expertMessages.length > 0 && `(${expertMessages.filter(m => m.status === 'pending').length})`}
            </button>
          </div>

          {!viewingMessages ? (
            <>
              <div className="flex justify-between items-center px-2">
                <h3 className="font-bold text-stone-800">{t.experts || 'Experts'}</h3>
                <button 
                  onClick={() => setIsAddingExpert(true)}
                  className="bg-[#1b7d36] text-white px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 shadow-md hover:scale-105 transition-transform"
                >
                  <Plus size={18} /> {t.addExpert || 'Add Expert'}
                </button>
              </div>

              {loadingExperts ? (
                <div className="flex justify-center py-20">
                  <Loader2 className="animate-spin text-[#1b7d36]" size={40} />
                </div>
              ) : (
                <div className="space-y-4">
                  {experts.map((expert) => (
                    <div key={expert.id} className="bg-white p-4 rounded-2xl border border-black/5 shadow-sm flex items-center gap-4">
                      <div className="w-16 h-16 rounded-xl bg-stone-100 overflow-hidden flex-shrink-0">
                        {expert.photoUrl ? (
                          <img src={expert.photoUrl} alt={expert.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-stone-300">
                            <UserCircle size={32} />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-stone-800 truncate">{expert.name}</h4>
                        <p className="text-xs text-stone-500 font-medium truncate">{expert.specialty}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Star size={12} className="text-amber-400 fill-amber-400" />
                          <span className="text-[10px] font-bold text-stone-600">{expert.rating || 0}</span>
                          <span className="text-[10px] text-stone-400">•</span>
                          <span className="text-[10px] font-bold text-stone-600">{expert.experience}</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => setEditingExpert(expert)}
                          className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                          title={t.edit}
                        >
                          <Settings size={18} />
                        </button>
                        <button 
                          onClick={() => handleDeleteExpert(expert.id)}
                          className="p-2 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                          title={t.delete}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                  {experts.length === 0 && (
                    <div className="text-center py-20 text-stone-400 italic">
                      {t.noExperts || 'No experts found. Add your first expert!'}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="space-y-4">
              <h3 className="font-bold text-stone-800 px-2">{t.expertMessages || 'User Inquiries'}</h3>
              {loadingMessages ? (
                <div className="flex justify-center py-20">
                  <Loader2 className="animate-spin text-[#1b7d36]" size={40} />
                </div>
              ) : (
                <div className="space-y-4">
                  {expertMessages.map((msg) => (
                    <div key={msg.id} className={`p-5 rounded-3xl border shadow-sm transition-all ${msg.status === 'pending' ? 'bg-white border-emerald-100' : 'bg-stone-50 border-black/5 opacity-70'}`}>
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-bold text-stone-800 flex items-center gap-2">
                            {msg.userName} 
                            {msg.status === 'pending' && <span className="w-2 h-2 bg-[#1b7d36] rounded-full animate-pulse" />}
                          </h4>
                          <p className="text-[10px] text-stone-400 font-bold uppercase tracking-wider">
                            Sent to: {msg.expertName} • {msg.timestamp?.toDate?.()?.toLocaleString() || 'Just now'}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button 
                            onClick={() => { setReplyingTo(msg); setReplyText(msg.reply || ''); }}
                            className="text-xs font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-full border border-blue-100"
                          >
                            {msg.reply ? 'Edit Reply' : 'Reply'}
                          </button>
                          {msg.status === 'pending' && (
                            <button 
                              onClick={async () => {
                                try {
                                  await updateDoc(doc(db, 'expert_messages', msg.id), { status: 'responded' });
                                } catch (error) {
                                  handleFirestoreError(error, OperationType.UPDATE, `expert_messages/${msg.id}`);
                                }
                              }}
                              className="text-xs font-bold text-[#1b7d36] bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100"
                            >
                              Mark Done
                            </button>
                          )}
                          <button 
                            onClick={() => {
                              setConfirmDialog({
                                isOpen: true,
                                title: "Delete Message?",
                                message: "Remove this inquiry from the list?",
                                onConfirm: async () => {
                                  try {
                                    await deleteDoc(doc(db, 'expert_messages', msg.id));
                                  } catch (error) {
                                    handleFirestoreError(error, OperationType.DELETE, `expert_messages/${msg.id}`);
                                  }
                                  setConfirmDialog(null);
                                },
                                type: 'danger'
                              });
                            }}
                            className="p-1 text-rose-500 hover:bg-rose-50 rounded-lg"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <p className="text-sm text-stone-600 bg-stone-100/50 p-3 rounded-2xl border border-black/5 italic">
                          "{msg.message}"
                        </p>
                        {msg.reply && (
                          <div className="bg-[#1b7d36]/5 p-3 rounded-2xl border border-[#1b7d36]/10">
                            <p className="text-[10px] font-bold text-[#1b7d36] uppercase tracking-wider mb-1">Your Response:</p>
                            <p className="text-sm text-stone-700">{msg.reply}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {expertMessages.length === 0 && (
                    <div className="text-center py-20 text-stone-400 italic">
                      {t.noMessages || 'No inquiries from users yet.'}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      ) : activeTab === 'app_branding' ? (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-[32px] border border-black/5 shadow-sm space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-[#f0fdf4] rounded-xl">
                <Leaf className="text-[#1b7d36]" size={20} />
              </div>
              <h3 className="text-[#1b7d36] font-bold">{t.brandingSettings}</h3>
            </div>
            
            <div className="space-y-6">
              <div className="space-y-4">
                <label className="text-xs font-bold text-stone-500 uppercase tracking-wider">{t.appLogo}</label>
                
                <div className="flex flex-col md:flex-row items-center gap-8 p-8 bg-stone-50 rounded-[40px] border border-stone-100 shadow-inner">
                  <div className="relative group">
                    <div className="w-32 h-32 bg-white rounded-full flex items-center justify-center shadow-xl overflow-hidden border-4 border-white transition-transform group-hover:scale-105">
                      {branding.logoUrl ? (
                        <img src={branding.logoUrl} alt="Logo" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                      ) : (
                        <div className="bg-stone-50 w-full h-full flex items-center justify-center">
                          <UserCircle size={64} className="text-stone-200" />
                        </div>
                      )}
                    </div>
                    {branding.logoUrl && (
                      <button 
                        onClick={async () => {
                          try {
                            await setDoc(doc(db, 'app_config', 'branding'), { logoUrl: '' }, { merge: true });
                            setToast({ message: t.removeSuccess || "Logo removed", type: 'info' });
                          } catch (err) {
                            handleFirestoreError(err, OperationType.WRITE, 'app_config/branding');
                          }
                        }}
                        className="absolute -top-2 -right-2 bg-rose-500 text-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                        title={t.remove}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>

                  <div className="flex-1 space-y-4 w-full">
                    <p className="text-xs text-stone-500 leading-relaxed font-medium">
                      {t.logoUploadInstructions}
                    </p>
                    
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <label className="flex-1 cursor-pointer bg-[#1b7d36] text-white px-6 py-3 rounded-2xl text-sm font-bold shadow-lg hover:bg-[#16652b] transition-all flex items-center justify-center gap-2">
                          <Upload size={18} />
                          {t.uploadLogo}
                          <input 
                            type="file" 
                            accept="image/*" 
                            className="hidden" 
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              try {
                                const base64 = await new Promise<string>((resolve) => {
                                  const reader = new FileReader();
                                  reader.onload = () => resolve(reader.result as string);
                                  reader.readAsDataURL(file);
                                });
                                await setDoc(doc(db, 'app_config', 'branding'), { logoUrl: base64 }, { merge: true });
                                setToast({ message: t.updateSuccess, type: 'success' });
                              } catch (err) {
                                handleFirestoreError(err, OperationType.WRITE, 'app_config/branding');
                              }
                            }}
                          />
                        </label>
                      </div>

                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <Share2 size={14} className="text-stone-400" />
                          </div>
                          <input 
                            type="text"
                            placeholder={t.enterLogoUrl}
                            value={localLogoUrl.startsWith('data:') ? '' : localLogoUrl}
                            onChange={(e) => setLocalLogoUrl(e.target.value)}
                            className="w-full bg-white border border-stone-200 rounded-2xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20 transition-all"
                          />
                        </div>
                        {localLogoUrl && !localLogoUrl.startsWith('data:') && (
                          <button 
                            onClick={async () => {
                              try {
                                await setDoc(doc(db, 'app_config', 'branding'), { logoUrl: localLogoUrl }, { merge: true });
                                setToast({ message: t.updateSuccess, type: 'success' });
                              } catch (err) {
                                handleFirestoreError(err, OperationType.WRITE, 'app_config/branding');
                              }
                            }}
                            className="bg-[#1b7d36] text-white px-6 py-3 rounded-2xl font-bold shadow-md hover:bg-[#16652b] transition-all"
                          >
                            {t.save}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <AnimatePresence>
        {replyingTo && (
          <Modal title={`Reply to ${replyingTo.userName}`} onClose={() => setReplyingTo(null)}>
            <div className="space-y-4">
              <div className="bg-stone-50 p-4 rounded-2xl border border-black/5 italic text-sm text-stone-600">
                "{replyingTo.message}"
              </div>
              <textarea 
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                placeholder="Type your response here..."
                className="w-full h-40 bg-white border border-stone-200 rounded-2xl p-4 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20 transition-all text-sm"
              />
              <div className="flex gap-2">
                <button 
                  onClick={handleSendReply}
                  disabled={isSendingReply || !replyText.trim()}
                  className="flex-1 bg-[#1b7d36] text-white py-4 rounded-2xl font-bold shadow-md hover:bg-[#16652b] disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                >
                  {isSendingReply && <Loader2 size={18} className="animate-spin" />}
                  Send Response
                </button>
                <button onClick={() => setReplyingTo(null)} className="flex-1 bg-stone-100 text-stone-600 py-4 rounded-2xl font-bold">
                  Cancel
                </button>
              </div>
            </div>
          </Modal>
        )}
        {(isAddingExpert || editingExpert) && (
          <Modal 
            title={editingExpert ? (t.editExpert || 'Edit Expert') : (t.addExpert || 'Add Expert')} 
            onClose={() => { setEditingExpert(null); setIsAddingExpert(false); }}
          >
            <ExpertEditor 
              expert={editingExpert || {}} 
              onSave={handleSaveExpert} 
              onCancel={() => { setEditingExpert(null); setIsAddingExpert(false); }} 
              t={t}
            />
          </Modal>
        )}
        {(isAddingPage || editingPage) && (
          <Modal 
            title={editingPage?.id ? t.editPage : t.addNewPage} 
            onClose={() => { setEditingPage(null); setIsAddingPage(false); }}
          >
            <PageEditor 
              page={editingPage || { id: '', title: '', content: '' }} 
              onSave={async (p) => {
                try {
                  await setDoc(doc(db, 'pages', p.id), {
                    ...p,
                    updatedAt: serverTimestamp()
                  });
                  setToast({ message: "Page saved successfully", type: 'success' });
                  setEditingPage(null);
                  setIsAddingPage(false);
                } catch (e) {
                  handleFirestoreError(e, OperationType.WRITE, `pages/${p.id}`);
                }
              }} 
              onCancel={() => { setEditingPage(null); setIsAddingPage(false); }} 
              t={t}
            />
          </Modal>
        )}
      </AnimatePresence>
      {itemToDelete && (
        <Modal title={t.deleteConfirm || 'Are you sure?'} onClose={() => setItemToDelete(null)}>
          <div className="space-y-4">
            <p>{t.deleteConfirmMessage || 'Are you sure you want to delete this menu item?'}</p>
            <div className="flex gap-2">
              <button onClick={() => { handleDeleteConfirmed(itemToDelete.id); setItemToDelete(null); }} className="flex-1 bg-rose-600 text-white py-2 rounded-xl font-bold">Delete</button>
              <button onClick={() => setItemToDelete(null)} className="flex-1 bg-stone-200 text-stone-600 py-2 rounded-xl font-bold">Cancel</button>
            </div>
          </div>
        </Modal>
      )}
      {(isAddingMarketplaceListing || editingMarketplaceListing) && (
        <Modal 
          title={editingMarketplaceListing ? "Edit Listing" : "Add New Listing"} 
          onClose={() => { setEditingMarketplaceListing(null); setIsAddingMarketplaceListing(false); }}
        >
          <MarketplaceListingEditor 
            listing={editingMarketplaceListing || {}} 
            onSave={handleSaveMarketplaceListing}
            onCancel={() => { setEditingMarketplaceListing(null); setIsAddingMarketplaceListing(false); }}
            t={t}
          />
        </Modal>
      )}
    </div>
  );
}

function PageEditor({ page, onSave, onCancel, t }: { 
  page: PageContent, 
  onSave: (page: PageContent) => void,
  onCancel: () => void,
  t: any
}) {
  const [editedPage, setEditedPage] = useState<PageContent>({ ...page });

  return (
    <div className="p-4 space-y-6 pb-20 overflow-y-auto max-h-[80vh]">
      <div className="space-y-4">
        <div>
          <label className="text-xs font-bold text-stone-400 block mb-1">{t.pageSlug}</label>
          <input 
            type="text" 
            value={editedPage.id}
            onChange={e => setEditedPage({ ...editedPage, id: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
            disabled={!!page.id}
            className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20 disabled:bg-stone-50"
            placeholder="e.g. privacy-policy"
          />
        </div>

        <div>
          <label className="text-xs font-bold text-stone-400 block mb-1">{t.pageTitle}</label>
          <div className="space-y-4">
            <input 
              type="text" 
              value={editedPage.title_en || editedPage.title}
              onChange={e => setEditedPage({ ...editedPage, title_en: e.target.value, title: e.target.value })}
              className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
              placeholder="English Title"
            />
            <input 
              type="text" 
              value={editedPage.title_te || ''}
              onChange={e => setEditedPage({ ...editedPage, title_te: e.target.value })}
              className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
              placeholder="Telugu Title (తెలుగు)"
            />
            <input 
              type="text" 
              value={editedPage.title_hi || ''}
              onChange={e => setEditedPage({ ...editedPage, title_hi: e.target.value })}
              className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
              placeholder="Hindi Title (हिंदी)"
            />
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <label className="text-xs font-bold text-stone-400 block mb-1">{t.contentInEnglish}</label>
            <textarea 
              value={editedPage.content_en || editedPage.content}
              onChange={e => setEditedPage({ ...editedPage, content_en: e.target.value, content: e.target.value })}
              className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20 h-32 font-mono text-sm"
              placeholder="English Content (Markdown)"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-stone-400 block mb-1">{t.contentInTelugu}</label>
            <textarea 
              value={editedPage.content_te || ''}
              onChange={e => setEditedPage({ ...editedPage, content_te: e.target.value })}
              className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20 h-32 font-mono text-sm"
              placeholder="Telugu Content (తెలుగు - Markdown)"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-stone-400 block mb-1">{t.contentInHindi}</label>
            <textarea 
              value={editedPage.content_hi || ''}
              onChange={e => setEditedPage({ ...editedPage, content_hi: e.target.value })}
              className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20 h-32 font-mono text-sm"
              placeholder="Hindi Content (हिंदी - Markdown)"
            />
          </div>
        </div>
      </div>

      <div className="flex gap-4">
        <button 
          onClick={() => onSave(editedPage)}
          disabled={!editedPage.id || !editedPage.title}
          className="flex-1 bg-[#1b7d36] text-white py-4 rounded-xl font-bold shadow-md hover:bg-[#16652b] disabled:opacity-50 transition-all"
        >
          {t.savePage}
        </button>
        <button onClick={onCancel} className="flex-1 bg-stone-100 text-stone-600 py-4 rounded-xl font-bold">
          {t.cancel}
        </button>
      </div>
    </div>
  );
}

function ItemEditor({ catId, item, onSave, onCancel, t }: { 
  catId: string, 
  item: HandbookItem, 
  onSave: (catId: string, item: HandbookItem) => void,
  onCancel: () => void,
  t: any
}) {
  const [editedItem, setEditedItem] = useState<HandbookItem>({ ...item });

  const handleAddSection = () => {
    const newSection: HandbookSection = {
      id: Date.now().toString(),
      title: '',
      content: ''
    };
    setEditedItem({ ...editedItem, sections: [...editedItem.sections, newSection] });
  };

  const handleUpdateSection = (id: string, field: string, value: string) => {
    setEditedItem({
      ...editedItem,
      sections: editedItem.sections.map(s => s.id === id ? { ...s, [field]: value } : s)
    });
  };

  const handleDeleteSection = (id: string) => {
    setEditedItem({
      ...editedItem,
      sections: editedItem.sections.filter(s => s.id !== id)
    });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const resized = await resizeImage(reader.result as string);
        setEditedItem({ ...editedItem, image: resized });
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="p-4 space-y-6 pb-20">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-[#1b7d36]">{t.edit} {item.name}</h2>
        <button onClick={onCancel} className="p-2 hover:bg-black/5 rounded-full">
          <X size={24} />
        </button>
      </div>

      <div className="bg-white p-6 rounded-[32px] border border-black/5 shadow-lg space-y-6">
        <div className="space-y-4">
          <label className="text-[#1b7d36] font-bold text-sm block">{t.itemName}</label>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold text-stone-400 block mb-1">English Name</label>
              <input 
                type="text" 
                value={editedItem.name_en || editedItem.name}
                onChange={e => setEditedItem({ ...editedItem, name_en: e.target.value, name: e.target.value })}
                className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
                placeholder="English Name"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-stone-400 block mb-1">Telugu Name (తెలుగు)</label>
              <input 
                type="text" 
                value={editedItem.name_te || ''}
                onChange={e => setEditedItem({ ...editedItem, name_te: e.target.value })}
                className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
                placeholder="Telugu Name"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-stone-400 block mb-1">Hindi Name (हिंदी)</label>
              <input 
                type="text" 
                value={editedItem.name_hi || ''}
                onChange={e => setEditedItem({ ...editedItem, name_hi: e.target.value })}
                className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
                placeholder="Hindi Name"
              />
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-[#1b7d36] font-bold text-sm block">{t.thumbnailImage}</label>
          <div className="flex items-center gap-4 mb-2">
            <label className="flex-1 cursor-pointer bg-[#f8f9fa] border-2 border-dashed border-stone-200 rounded-2xl p-4 flex flex-col items-center justify-center hover:border-[#1b7d36] transition-colors">
              <Upload size={24} className="text-stone-400 mb-1" />
              <span className="text-sm font-bold text-stone-500">{t.uploadImage}</span>
              <input 
                type="file" 
                accept="image/*" 
                className="hidden" 
                onChange={handleFileUpload} 
              />
            </label>
          </div>
          <div className="relative rounded-2xl overflow-hidden border border-stone-200 aspect-video bg-stone-100">
            {editedItem.image ? (
              <img src={editedItem.image} alt="Preview" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-stone-400">{t.thumbnailImage}</div>
            )}
          </div>
          {editedItem.image && (
            <button 
              onClick={() => setEditedItem({ ...editedItem, image: undefined })}
              className="text-rose-500 text-xs font-bold mt-2"
            >
              {t.delete}
            </button>
          )}
        </div>

        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <label className="text-[#1b7d36] font-bold text-sm">{t.sections}</label>
            <button 
              onClick={handleAddSection}
              className="text-[#1b7d36] text-sm font-bold flex items-center gap-1 bg-emerald-50 px-3 py-1 rounded-lg"
            >
              + {t.addSection}
            </button>
          </div>

          <div className="space-y-4">
            {editedItem.sections.map((section, idx) => (
              <div key={section.id} className="bg-[#f8f9fa] rounded-2xl p-4 border border-black/5 relative space-y-4">
                <button 
                  onClick={() => handleDeleteSection(section.id)}
                  className="absolute top-2 right-2 bg-rose-100 text-rose-500 p-1 rounded-full hover:bg-rose-200"
                >
                  <X size={12} />
                </button>
                <div className="space-y-4">
                  <label className="text-stone-900 font-bold text-sm block">{t.title}</label>
                  <div className="grid grid-cols-1 gap-3">
                    <input 
                      type="text" 
                      value={section.title_en || section.title}
                      onChange={e => handleUpdateSection(section.id, 'title_en', e.target.value)}
                      className="w-full bg-white border border-stone-200 rounded-lg px-3 py-2 text-sm" 
                      placeholder="English Title"
                    />
                    <input 
                      type="text" 
                      value={section.title_te || ''}
                      onChange={e => handleUpdateSection(section.id, 'title_te', e.target.value)}
                      className="w-full bg-white border border-stone-200 rounded-lg px-3 py-2 text-sm" 
                      placeholder="Telugu Title (తెలుగు)"
                    />
                    <input 
                      type="text" 
                      value={section.title_hi || ''}
                      onChange={e => handleUpdateSection(section.id, 'title_hi', e.target.value)}
                      className="w-full bg-white border border-stone-200 rounded-lg px-3 py-2 text-sm" 
                      placeholder="Hindi Title (हिंदी)"
                    />
                  </div>
                </div>
                <div className="space-y-4">
                  <label className="text-stone-400 text-sm block">{t.content}</label>
                  <div className="grid grid-cols-1 gap-3">
                    <textarea 
                      value={section.content_en || section.content}
                      onChange={e => handleUpdateSection(section.id, 'content_en', e.target.value)}
                      className="w-full bg-white border border-stone-200 rounded-lg px-3 py-2 text-sm h-24 resize-none" 
                      placeholder="English Content"
                    />
                    <textarea 
                      value={section.content_te || ''}
                      onChange={e => handleUpdateSection(section.id, 'content_te', e.target.value)}
                      className="w-full bg-white border border-stone-200 rounded-lg px-3 py-2 text-sm h-24 resize-none" 
                      placeholder="Telugu Content (తెలుగు)"
                    />
                    <textarea 
                      value={section.content_hi || ''}
                      onChange={e => handleUpdateSection(section.id, 'content_hi', e.target.value)}
                      className="w-full bg-white border border-stone-200 rounded-lg px-3 py-2 text-sm h-24 resize-none" 
                      placeholder="Hindi Content (हिंदी)"
                    />
                  </div>
                </div>
              </div>
            ))}
            {editedItem.sections.length === 0 && (
              <p className="text-center text-stone-400 text-sm py-4 italic">{t.noSections}</p>
            )}
          </div>
        </div>

        <button 
          onClick={() => onSave(catId, editedItem)}
          className="w-full bg-[#1b7d36] text-white py-4 rounded-2xl font-bold text-lg shadow-lg hover:bg-[#16652b] transition-colors"
        >
          {t.saveAndSync}
        </button>
      </div>
    </div>
  );
}



function LoginScreen({ 
  onLogin, 
  signUpFields, 
  verificationEmail, 
  setVerificationEmail,
  branding
}: { 
  onLogin: () => void, 
  signUpFields: SignUpField[],
  verificationEmail: string,
  setVerificationEmail: (email: string) => void,
  branding: { logoUrl: string }
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Exclusively use English for Login/Signup
  const en = translations.en;

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    try {
      googleProvider.setCustomParameters({
        prompt: 'select_account'
      });
      
      if (Capacitor.isNativePlatform()) {
        // Use redirect for better compatibility with Android/iOS WebViews
        await signInWithRedirect(auth, googleProvider);
      } else {
        const result = await signInWithPopup(auth, googleProvider);
        const user = result.user;
        
        // Check if user document exists, if not create it
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (!userDoc.exists()) {
          await setDoc(doc(db, 'users', user.uid), {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL,
            role: user.email === "prakruthimithra2026@gmail.com" ? "admin" : "user",
            createdAt: serverTimestamp()
          });
        }
        onLogin();
      }
    } catch (err: any) {
      console.error("Google Auth error", err);
      setError(en.somethingWentWrong);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (isForgotPassword) {
        await sendPasswordResetEmail(auth, email);
        setSuccess(en.passwordResetSent);
        setIsForgotPassword(false);
      } else if (isSignUp) {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        
        // Save user details to Firestore
        try {
          await setDoc(doc(db, 'users', userCredential.user.uid), {
            uid: userCredential.user.uid,
            email: email,
            role: email === "prakruthimithra2026@gmail.com" ? "admin" : "user",
            ...formData,
            createdAt: serverTimestamp()
          });
        } catch (error) {
          handleFirestoreError(error, OperationType.WRITE, `users/${userCredential.user.uid}`);
        }

        // Send verification email (optional, don't block)
        try {
          await sendEmailVerification(userCredential.user);
        } catch (e) {
          console.error("Failed to send verification email", e);
        }
        
        onLogin();
      } else {
        await signInWithEmailAndPassword(auth, email, password);
        onLogin();
      }
    } catch (err: any) {
      console.error("Auth error", err);
      const errorCode = err.code;
      
      if (errorCode === 'auth/email-already-in-use') {
        setError(en.userAlreadyExists);
      } else if (errorCode === 'auth/user-not-found' || errorCode === 'auth/invalid-credential' || errorCode === 'auth/wrong-password') {
        setError(en.incorrectEmailPassword);
      } else if (errorCode === 'auth/weak-password') {
        setError(en.passwordTooShort);
      } else if (errorCode === 'auth/invalid-email') {
        setError(en.invalidEmail);
      } else {
        setError(en.somethingWentWrong);
      }
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    if (isForgotPassword) return en.forgotPassword;
    return isSignUp ? en.signup : en.login;
  };

  if (verificationEmail) {
    return (
      <div className="p-4 flex flex-col items-center justify-center min-h-[60vh] py-10">
        <div className="bg-white p-8 rounded-[40px] border border-black/5 shadow-lg w-full max-w-md space-y-6 text-center">
          <div className="bg-[#f0fdf4] w-20 h-20 rounded-2xl flex items-center justify-center mx-auto overflow-hidden shadow-sm">
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt="Logo" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
            ) : (
              <div className="bg-[#1b7d36]/10 w-full h-full flex items-center justify-center">
                <Leaf className="text-[#1b7d36]" size={32} />
              </div>
            )}
          </div>
          <div className="bg-blue-50 w-12 h-12 rounded-full flex items-center justify-center mx-auto translate-y-[-24px] border-4 border-white shadow-sm">
            <Mail className="text-blue-600" size={24} />
          </div>
          <div className="-mt-4">
            <h2 className="text-xl font-bold text-stone-800">{en.verifyEmail}</h2>
            <p className="text-stone-600 leading-relaxed mt-2">
              {en.verificationSent} <span className="font-bold text-[#1b7d36]">{verificationEmail}</span>. {en.pleaseVerify}
            </p>
          </div>
          <button 
            onClick={() => {
              setVerificationEmail('');
              setIsSignUp(false);
            }}
            className="w-full bg-[#1b7d36] text-white py-4 rounded-2xl font-bold text-lg shadow-lg hover:bg-[#16652b] transition-colors"
          >
            {en.login}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 flex flex-col items-center justify-center min-h-[60vh] py-10">
      <div className="bg-white p-8 rounded-[40px] border border-black/5 shadow-lg w-full max-w-md space-y-6">
        <div className="text-center space-y-3">
          <div className="bg-[#f0fdf4] w-24 h-24 rounded-3xl flex items-center justify-center mx-auto overflow-hidden shadow-sm border border-[#1b7d36]/10">
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt="Logo" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
            ) : (
              <div className="bg-[#1b7d36]/10 w-full h-full flex items-center justify-center">
                <Leaf size={48} className="text-[#1b7d36]" />
              </div>
            )}
          </div>
          <h2 className="text-2xl font-bold text-stone-800">{getTitle()}</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <label className="text-sm font-bold text-stone-600 ml-1">{en.email}</label>
            <input 
              type="email" 
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full bg-[#f8f9fa] border border-stone-200 rounded-2xl px-5 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
              placeholder={en.emailPlaceholder}
              required
            />
          </div>
          {!isForgotPassword && (
            <div className="space-y-1">
              <label className="text-sm font-bold text-stone-600 ml-1">{en.password}</label>
              <input 
                type="password" 
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full bg-[#f8f9fa] border border-stone-200 rounded-2xl px-5 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
                placeholder={en.passwordPlaceholder}
                required={!isForgotPassword}
              />
            </div>
          )}
          
          {isSignUp && !isForgotPassword && signUpFields.map((field, index) => {
            const fieldLabel = field.label_en || field.label;
            const fieldPlaceholder = field.placeholder_en || field.placeholder;
            
            if (field.id === 'farmerId' && formData.role !== 'farmer') return null;
            
            return (
              <div key={`${field.id}-${index}`} className="space-y-1">
                <label className="text-sm font-bold text-stone-600 ml-1">{fieldLabel}</label>
                {field.type === 'select' ? (
                  <select
                    value={formData[field.id] || ''}
                    onChange={e => setFormData({ ...formData, [field.id]: e.target.value })}
                    className="w-full bg-[#f8f9fa] border border-stone-200 rounded-2xl px-5 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
                    required={field.required}
                  >
                    <option value="">{fieldPlaceholder}</option>
                    {field.options?.map(opt => (
                      <option key={opt.value} value={opt.value}>{translations.en[opt.value] || opt.label}</option>
                    ))}
                  </select>
                ) : (
                  <input 
                    type={field.type}
                    value={formData[field.id] || ''}
                    onChange={e => setFormData({ ...formData, [field.id]: e.target.value })}
                    className="w-full bg-[#f8f9fa] border border-stone-200 rounded-2xl px-5 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
                    placeholder={fieldPlaceholder}
                    required={field.required}
                  />
                )}
              </div>
            );
          })}

          {error && <p className="text-rose-500 text-xs font-bold text-center">{error}</p>}
          {success && <p className="text-[#1b7d36] text-xs font-bold text-center">{success}</p>}
          <button 
            type="submit"
            disabled={loading}
            className="w-full bg-[#1b7d36] text-white py-4 rounded-2xl font-bold text-lg shadow-lg hover:bg-[#16652b] transition-colors disabled:opacity-50"
          >
            {loading ? en.processing : (isForgotPassword ? en.sendResetLink : (isSignUp ? en.signup : en.login))}
          </button>
        </form>

        {!isForgotPassword && (
          <>
            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-stone-200"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-stone-400 font-bold">{en.or}</span>
              </div>
            </div>

            <button 
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full bg-white border border-stone-200 text-stone-700 py-3.5 rounded-2xl font-bold flex items-center justify-center gap-3 hover:bg-stone-50 transition-colors shadow-sm disabled:opacity-50"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.66l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              {en.signInWithGoogle}
            </button>
          </>
        )}

        <div className="flex flex-col gap-3 text-center">
          {!isForgotPassword && !isSignUp && (
            <button 
              onClick={() => { setIsForgotPassword(true); setError(''); setSuccess(''); }}
              className="text-xs text-stone-400 font-bold hover:text-[#1b7d36] transition-colors"
            >
              {en.forgotPassword}
            </button>
          )}
          
          <button 
            onClick={() => {
              if (isForgotPassword) {
                setIsForgotPassword(false);
              } else {
                setIsSignUp(!isSignUp);
              }
              setError('');
              setSuccess('');
            }}
            className="text-sm text-[#1b7d36] font-bold hover:underline"
          >
            {isForgotPassword ? en.backToLogin : (isSignUp ? en.alreadyHaveAccount : en.dontHaveAccount)}
          </button>
        </div>
      </div>
    </div>
  );
}

function CropsScreen({ categories, language, t, onNavigate }: { categories: HandbookCategory[], language: Language, t: any, onNavigate: (s: Screen) => void }) {
  const [selectedItem, setSelectedItem] = useState<HandbookItem | null>(null);
  
  const cropItems = categories.flatMap(cat => cat.items).filter(item => item.screen === 'crops');

  if (selectedItem) {
    return <HandbookItemDetail item={selectedItem} onBack={() => setSelectedItem(null)} onNavigate={onNavigate} language={language} t={t} />;
  }

  return (
    <div className="p-4 space-y-6 pb-20">
      <h2 className="text-xl font-bold text-[#1b7d36] mb-8">{t.crops}</h2>
      <div className="grid grid-cols-2 gap-4">
        {cropItems.map(item => (
          <button 
            key={item.id}
            onClick={() => setSelectedItem(item)}
            className="bg-white rounded-[32px] border border-black/5 shadow-sm overflow-hidden flex flex-col group hover:shadow-md transition-all text-center"
          >
            <div className="aspect-[4/3] bg-stone-100 relative overflow-hidden">
              {item.image ? (
                <img 
                  src={item.image} 
                  alt={(item as any)[`name_${language}`] || item.name} 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" 
                  referrerPolicy="no-referrer" 
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-stone-300 font-bold text-sm">
                  <Sprout size={24} />
                </div>
              )}
            </div>
            <div className="p-4">
              <span className="text-sm font-bold text-[#1b7d36] line-clamp-2">{(item as any)[`name_${language}`] || item.name}</span>
            </div>
          </button>
        ))}
        {cropItems.length === 0 && (
          <div className="col-span-2 text-center text-stone-400 py-10 italic">
            {t.noCropsFound}
          </div>
        )}
      </div>
    </div>
  );
}

function InputsScreen({ categories, language, t, onNavigate }: { categories: HandbookCategory[], language: Language, t: any, onNavigate: (s: Screen) => void }) {
  const [selectedItem, setSelectedItem] = useState<HandbookItem | null>(null);
  
  const inputItems = categories.flatMap(cat => cat.items).filter(item => item.screen === 'inputs');

  if (selectedItem) {
    return <HandbookItemDetail item={selectedItem} onBack={() => setSelectedItem(null)} onNavigate={onNavigate} language={language} t={t} />;
  }

  return (
    <div className="p-4 space-y-6 pb-20">
      <h2 className="text-xl font-bold text-[#1b7d36] mb-8">{t.naturalKashayams}</h2>
      <div className="grid grid-cols-2 gap-4">
        {inputItems.map(item => (
          <button 
            key={item.id}
            onClick={() => setSelectedItem(item)}
            className="bg-white rounded-[32px] border border-black/5 shadow-sm overflow-hidden flex flex-col group hover:shadow-md transition-all text-center"
          >
            <div className="aspect-[4/3] bg-stone-100 relative overflow-hidden">
              {item.image ? (
                <img 
                  src={item.image} 
                  alt={(item as any)[`name_${language}`] || item.name} 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" 
                  referrerPolicy="no-referrer" 
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-stone-300 font-bold text-sm">
                  <Droplets size={24} />
                </div>
              )}
            </div>
            <div className="p-4">
              <span className="text-sm font-bold text-[#1b7d36] line-clamp-2">{(item as any)[`name_${language}`] || item.name}</span>
            </div>
          </button>
        ))}
        {inputItems.length === 0 && (
          <div className="col-span-2 text-center text-stone-400 py-10 italic">
            {t.noInputsFound}
          </div>
        )}
      </div>
    </div>
  );
}

function HandbookScreen({ onNavigate, categories, language, t, initialCategoryId, onCategoryChange, videos }: { 
  onNavigate: (s: Screen) => void, 
  categories: HandbookCategory[], 
  language: Language, 
  t: any,
  initialCategoryId?: string | null,
  onCategoryChange?: (id: string | null) => void,
  videos: VideoItem[]
}) {
  const [selectedItem, setSelectedItem] = useState<HandbookItem | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<HandbookCategory | null>(null);

  useEffect(() => {
    if (initialCategoryId) {
      const cat = categories.find(c => c.id === initialCategoryId);
      if (cat) setSelectedCategory(cat);
    } else {
      setSelectedCategory(null);
    }
  }, [initialCategoryId, categories]);

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    if (onCategoryChange) onCategoryChange(null);
  };

  const handleSelectCategory = (cat: HandbookCategory) => {
    setSelectedCategory(cat);
    if (onCategoryChange) onCategoryChange(cat.id);
  };

  if (selectedItem) {
    return <HandbookItemDetail item={selectedItem} onBack={() => setSelectedItem(null)} onNavigate={onNavigate} language={language} t={t} />;
  }

  if (selectedCategory) {
    return (
      <div className="p-4 space-y-6 pb-20">
        <button 
          onClick={handleBackToCategories}
          className="flex items-center gap-2 text-[#1b7d36] font-bold mb-4"
        >
          <ChevronLeft size={20} /> {t.back}
        </button>
        
        <div className="flex items-center justify-between gap-4 mb-8">
          <h2 className="text-xl font-bold text-[#1b7d36]">{(selectedCategory as any)[`name_${language}`] || selectedCategory.name}</h2>

        </div>
        
        <div className="grid grid-cols-2 gap-4">
          {selectedCategory.items.map((item) => (
            <div key={item.id} className="relative bg-white rounded-[32px] border border-black/5 shadow-sm overflow-hidden flex flex-col group hover:shadow-md transition-all text-center">
              <div className="flex-grow flex flex-col">
                <button 
                  onClick={() => setSelectedItem(item)}
                  className="aspect-[4/3] bg-stone-100 relative overflow-hidden block w-full"
                >
                  {item.image ? (
                    <img 
                      src={item.image} 
                      alt={(item as any)[`name_${language}`] || item.name} 
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" 
                      referrerPolicy="no-referrer" 
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-stone-300 font-bold text-sm">
                      {t.noMedia}
                    </div>
                  )}
                </button>
                <div className="p-4 flex items-start justify-between gap-2">
                  <button onClick={() => setSelectedItem(item)} className="text-left flex-grow">
                    <span className="text-sm font-bold text-[#1b7d36] line-clamp-2">{(item as any)[`name_${language}`] || item.name}</span>
                  </button>

                </div>
              </div>
            </div>
          ))}
          {selectedCategory.items.length === 0 && (
            <div className="col-span-2 text-center text-stone-400 py-10 italic">
              {t.noSubcategories}
            </div>
          )}
        </div>
        
        <VideoSection videos={videos} placement="handbook" t={t} />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6 pb-20">
      <h2 className="text-xl font-bold text-[#1b7d36] mb-8">{t.handbook}</h2>
      <div className="space-y-4">
        {categories.map((cat) => (
          <button 
            key={cat.id}
            onClick={() => handleSelectCategory(cat)}
            className="w-full bg-white p-6 rounded-[32px] border border-black/5 shadow-sm flex items-center justify-between group hover:shadow-md transition-all"
          >
            <span className="text-xl font-bold text-stone-800">{(cat as any)[`name_${language}`] || cat.name}</span>
            <ChevronRight className="text-[#1b7d36] group-hover:translate-x-1 transition-transform" />
          </button>
        ))}
      </div>
    </div>
  );
}

function VideosScreen({ videos, t }: { videos: VideoItem[], t: any }) {
  const [playingVideo, setPlayingVideo] = useState<VideoItem | null>(null);
  const filteredVideos = videos.filter(v => (v.placement || ['videos']).includes('videos'));

  return (
    <div className="p-4 space-y-6 pb-20">
      <h2 className="text-xl font-bold text-[#1b7d36] mb-8">{t.videos}</h2>
      
      <div className="grid grid-cols-1 gap-6">
        {filteredVideos.map((video) => (
          <div key={video.id} className="bg-white rounded-[32px] border border-black/5 shadow-sm overflow-hidden flex flex-col group">
            <div 
              className="aspect-video bg-stone-100 relative overflow-hidden cursor-pointer"
              onClick={() => setPlayingVideo(video)}
            >
              {video.thumbnail ? (
                <img 
                  src={video.thumbnail} 
                  alt={video.title} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                  referrerPolicy="no-referrer" 
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-stone-200">
                  <Video size={48} className="text-stone-400" />
                </div>
              )}
              <div className="absolute inset-0 bg-black/20 flex items-center justify-center group-hover:bg-black/30 transition-colors">
                <div className="bg-white/90 p-4 rounded-full shadow-lg scale-90 group-hover:scale-100 transition-transform">
                  <Play size={24} className="text-[#1b7d36] fill-[#1b7d36]" />
                </div>
              </div>
            </div>
            <div className="p-5 flex justify-between items-center">
              <h4 className="font-bold text-stone-800 text-sm">{video.title}</h4>
              <button 
                onClick={() => setPlayingVideo(video)}
                className="text-xs font-bold text-[#1b7d36] bg-emerald-50 px-4 py-2 rounded-full"
              >
                {t.play}
              </button>
            </div>
          </div>
        ))}
        {filteredVideos.length === 0 && (
          <div className="text-center text-stone-400 py-20 italic">
            {t.noVideos}
          </div>
        )}
      </div>

      <AnimatePresence>
        {playingVideo && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setPlayingVideo(null)}
              className="absolute inset-0 bg-black/90 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative w-full max-w-4xl aspect-video bg-black rounded-3xl overflow-hidden shadow-2xl"
            >
              <button 
                onClick={() => setPlayingVideo(null)}
                className="absolute top-4 right-4 z-10 bg-white/10 hover:bg-white/20 p-2 rounded-full text-white transition-colors"
              >
                <X size={24} />
              </button>
              <iframe 
                src={getYouTubeEmbedUrl(playingVideo.url)} 
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowFullScreen
              />
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

function CalculatorEditor({ calculator, onSave, onCancel, t }: {
  calculator: Calculator,
  onSave: (calc: Calculator) => void,
  onCancel: () => void,
  t: any
}) {
  const [edited, setEdited] = useState<Calculator>({ ...calculator });

  const handleAddRow = () => {
    const newRow: CalculatorRow = {
      id: Date.now().toString(),
      label: '',
      sublabel: '',
      ratio: 0
    };
    setEdited({ ...edited, rows: [...edited.rows, newRow] });
  };

  const handleUpdateRow = (id: string, field: keyof CalculatorRow, value: any) => {
    setEdited({
      ...edited,
      rows: edited.rows.map(r => r.id === id ? { ...r, [field]: value } : r)
    });
  };

  const handleDeleteRow = (id: string) => {
    setEdited({
      ...edited,
      rows: edited.rows.filter(r => r.id !== id)
    });
  };

  return (
    <div className="p-4 space-y-6 pb-20">
      <div className="flex items-center justify-between">
        <button onClick={onCancel} className="p-2 hover:bg-black/5 rounded-full">
          <ChevronLeft size={24} />
        </button>
        <h2 className="text-xl font-bold text-stone-800">{t.editCalculator}</h2>
        <div className="w-10" />
      </div>

      <div className="bg-white p-6 rounded-[32px] border border-black/5 shadow-sm space-y-4">
        <div className="space-y-4">
          <label className="text-xs font-bold text-stone-400 uppercase">{t.title}</label>
          <div className="space-y-3">
            <input 
              type="text" 
              value={edited.title_en || edited.title}
              onChange={e => setEdited({ ...edited, title_en: e.target.value, title: e.target.value })}
              className="w-full bg-[#f8f9fa] border border-stone-200 rounded-2xl px-5 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
              placeholder="English Title"
            />
            <input 
              type="text" 
              value={edited.title_te || ''}
              onChange={e => setEdited({ ...edited, title_te: e.target.value })}
              className="w-full bg-[#f8f9fa] border border-stone-200 rounded-2xl px-5 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
              placeholder="Telugu Title (తెలుగు)"
            />
            <input 
              type="text" 
              value={edited.title_hi || ''}
              onChange={e => setEdited({ ...edited, title_hi: e.target.value })}
              className="w-full bg-[#f8f9fa] border border-stone-200 rounded-2xl px-5 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
              placeholder="Hindi Title (हिंदी)"
            />
          </div>
        </div>
        <div className="space-y-2">
          <label className="text-xs font-bold text-stone-400 uppercase">{t.baseRate}</label>
          <input 
            type="number" 
            value={edited.baseRate}
            onChange={e => setEdited({ ...edited, baseRate: parseFloat(e.target.value) || 0 })}
            className="w-full bg-[#f8f9fa] border border-stone-200 rounded-2xl px-5 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
          />
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex justify-between items-center px-2">
          <h3 className="font-bold text-stone-800">{t.rows}</h3>
          <button 
            onClick={handleAddRow}
            className="text-[#1b7d36] font-bold text-sm flex items-center gap-1"
          >
            <Plus size={16} /> {t.addRow}
          </button>
        </div>

        <div className="space-y-3">
          {edited.rows.map(row => (
            <div key={row.id} className="bg-white p-4 rounded-2xl border border-black/5 shadow-sm space-y-4">
              <div className="flex justify-between items-start">
                <div className="flex-1 space-y-3">
                  <div>
                    <label className="text-[10px] font-bold text-stone-400 uppercase mb-1 block">Label</label>
                    <div className="space-y-2">
                      <input 
                        type="text" 
                        placeholder="English Label"
                        value={row.label_en || row.label}
                        onChange={e => handleUpdateRow(row.id, 'label_en', e.target.value)}
                        className="w-full bg-[#f8f9fa] border border-stone-100 rounded-xl px-3 py-2 text-sm"
                      />
                      <input 
                        type="text" 
                        placeholder="Telugu Label (తెలుగు)"
                        value={row.label_te || ''}
                        onChange={e => handleUpdateRow(row.id, 'label_te', e.target.value)}
                        className="w-full bg-[#f8f9fa] border border-stone-100 rounded-xl px-3 py-2 text-sm"
                      />
                      <input 
                        type="text" 
                        placeholder="Hindi Label (हिंदी)"
                        value={row.label_hi || ''}
                        onChange={e => handleUpdateRow(row.id, 'label_hi', e.target.value)}
                        className="w-full bg-[#f8f9fa] border border-stone-100 rounded-xl px-3 py-2 text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-stone-400 uppercase mb-1 block">Sublabel</label>
                    <div className="space-y-2">
                      <input 
                        type="text" 
                        placeholder="English Sublabel"
                        value={row.sublabel_en || row.sublabel}
                        onChange={e => handleUpdateRow(row.id, 'sublabel_en', e.target.value)}
                        className="w-full bg-[#f8f9fa] border border-stone-100 rounded-xl px-3 py-2 text-xs"
                      />
                      <input 
                        type="text" 
                        placeholder="Telugu Sublabel (తెలుగు)"
                        value={row.sublabel_te || ''}
                        onChange={e => handleUpdateRow(row.id, 'sublabel_te', e.target.value)}
                        className="w-full bg-[#f8f9fa] border border-stone-100 rounded-xl px-3 py-2 text-xs"
                      />
                      <input 
                        type="text" 
                        placeholder="Hindi Sublabel (हिंदी)"
                        value={row.sublabel_hi || ''}
                        onChange={e => handleUpdateRow(row.id, 'sublabel_hi', e.target.value)}
                        className="w-full bg-[#f8f9fa] border border-stone-100 rounded-xl px-3 py-2 text-xs"
                      />
                    </div>
                  </div>
                </div>
                <button 
                  onClick={() => handleDeleteRow(row.id)}
                  className="text-rose-500 p-2 ml-2"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="flex items-center gap-2 pt-2 border-t border-stone-50">
                <label className="text-[10px] font-bold text-stone-400 uppercase">{t.ratio}</label>
                <input 
                  type="number" 
                  step="0.1"
                  value={row.ratio}
                  onChange={e => handleUpdateRow(row.id, 'ratio', parseFloat(e.target.value) || 0)}
                  className="w-24 bg-[#f8f9fa] border border-stone-100 rounded-xl px-3 py-2 text-sm"
                />
                <span className="text-xs text-stone-400">{t.kgPerAcre}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <button 
        onClick={() => onSave(edited)}
        className="w-full bg-[#1b7d36] text-white py-4 rounded-2xl font-bold shadow-md hover:bg-[#16652b] transition-colors"
      >
        {t.saveCalculator}
      </button>
    </div>
  );
}



function Modal({ title, children, onClose }: { title: string, children: React.ReactNode, onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <motion.div 
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="bg-white w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden"
      >
        <div className="p-6 border-b border-black/5 flex items-center justify-between bg-[#f8f9fa]">
          <h3 className="text-2xl font-bold text-[#1b7d36]">{title}</h3>
          <button onClick={onClose} className="p-2 hover:bg-black/5 rounded-full">
            <X size={24} />
          </button>
        </div>
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {children}
        </div>
      </motion.div>
    </div>
  );
}

function MarketplaceListingEditor({ 
  listing, 
  onSave, 
  onCancel, 
  t 
}: { 
  listing: Partial<Listing>, 
  onSave: (listing: any) => void,
  onCancel: () => void,
  t: any
}) {
  const [edited, setEdited] = useState<Partial<Listing>>({ 
    category: 'Vegetables',
    productName: '',
    price: 0,
    unit: 'kg',
    harvestDate: new Date().toISOString().split('T')[0],
    shelfLife: '',
    description: '',
    photoUrl: 'https://picsum.photos/seed/farm/400/300',
    ...listing 
  });

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const resized = await resizeImage(reader.result as string);
        setEdited({ ...edited, photoUrl: resized });
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div>
          <label className="text-xs font-bold text-stone-400 block mb-1">Product Name</label>
          <input 
            type="text" 
            value={edited.productName || ''}
            onChange={e => setEdited({ ...edited, productName: e.target.value })}
            className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
            placeholder="Product Name"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold text-stone-400 block mb-1">Price</label>
            <input 
              type="number" 
              value={edited.price || ''}
              onChange={e => setEdited({ ...edited, price: parseFloat(e.target.value) || 0 })}
              className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
              placeholder="Price"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-stone-400 block mb-1">Unit</label>
            <select 
              value={edited.unit || 'kg'}
              onChange={e => setEdited({ ...edited, unit: e.target.value })}
              className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
            >
              <option value="kg">kg</option>
              <option value="quintal">quintal</option>
              <option value="ton">ton</option>
              <option value="piece">piece</option>
              <option value="liter">liter</option>
            </select>
          </div>
        </div>
        <div>
          <label className="text-xs font-bold text-stone-400 block mb-1">Category</label>
          <select 
            value={edited.category || 'Vegetables'}
            onChange={e => setEdited({ ...edited, category: e.target.value as any })}
            className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
          >
            <option value="Vegetables">Vegetables</option>
            <option value="Fruits">Fruits</option>
            <option value="Grains">Grains</option>
            <option value="Millets">Millets</option>
            <option value="Inputs">Inputs</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-bold text-stone-400 block mb-1">Description</label>
          <textarea 
            value={edited.description || ''}
            onChange={e => setEdited({ ...edited, description: e.target.value })}
            className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20 min-h-[100px]"
            placeholder="Description"
          />
        </div>
        <div>
          <label className="text-xs font-bold text-stone-400 block mb-1">Photo</label>
          <div className="flex items-center gap-4">
            <img src={edited.photoUrl || 'https://picsum.photos/seed/farm/100/100'} className="w-20 h-20 rounded-xl object-cover" referrerPolicy="no-referrer" />
            <label className="flex-1 cursor-pointer">
              <div className="border-2 border-dashed border-stone-200 rounded-xl p-4 text-center hover:border-[#1b7d36] transition-colors">
                <span className="text-xs text-stone-500">Change Photo</span>
              </div>
              <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
            </label>
          </div>
        </div>
      </div>
      <div className="flex gap-3">
        <button 
          onClick={() => onSave(edited)}
          className="flex-1 bg-[#1b7d36] text-white py-4 rounded-2xl font-bold shadow-md hover:bg-[#16652b] transition-colors"
        >
          Save Listing
        </button>
        <button 
          onClick={onCancel}
          className="flex-1 bg-stone-100 text-stone-600 py-4 rounded-2xl font-bold hover:bg-stone-200 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function ExpertEditor({ expert, onSave, onCancel, t }: {
  expert: Partial<Expert>,
  onSave: (expert: any) => void,
  onCancel: () => void,
  t: any
}) {
  const [edited, setEdited] = useState<Partial<Expert>>({
    name: '',
    specialty: '',
    location: '',
    rating: 5,
    experience: '',
    bio: '',
    photoUrl: '',
    phone: '',
    ...expert
  });

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const resized = await resizeImage(reader.result as string);
        setEdited({ ...edited, photoUrl: resized });
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4">
        <div>
          <label className="text-xs font-bold text-stone-400 block mb-1">Expert Name</label>
          <input 
            type="text" 
            value={edited.name || ''}
            onChange={e => setEdited({ ...edited, name: e.target.value })}
            className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
            placeholder="e.g. Dr. Ramesh"
          />
        </div>

        <div>
          <label className="text-xs font-bold text-stone-400 block mb-1">Expert Registered Email</label>
          <input 
            type="email" 
            value={edited.email || ''}
            onChange={e => setEdited({ ...edited, email: e.target.value })}
            className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
            placeholder="expert@example.com"
          />
          <p className="text-[10px] text-stone-400 mt-1 italic">Must match the email they use to log in to the app.</p>
        </div>
        
        <div>
          <label className="text-xs font-bold text-stone-400 block mb-1">Specialty</label>
          <input 
            type="text" 
            value={edited.specialty || ''}
            onChange={e => setEdited({ ...edited, specialty: e.target.value })}
            className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
            placeholder="e.g. Natural Farming Specialist"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold text-stone-400 block mb-1">Experience</label>
            <input 
              type="text" 
              value={edited.experience || ''}
              onChange={e => setEdited({ ...edited, experience: e.target.value })}
              className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
              placeholder="e.g. 10 Years"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-stone-400 block mb-1">Rating (1-5)</label>
            <input 
              type="number" 
              min="0"
              max="5"
              step="0.1"
              value={edited.rating || 5}
              onChange={e => setEdited({ ...edited, rating: parseFloat(e.target.value) || 0 })}
              className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
            />
          </div>
        </div>

        <div>
          <label className="text-xs font-bold text-stone-400 block mb-1">Location</label>
          <input 
            type="text" 
            value={edited.location || ''}
            onChange={e => setEdited({ ...edited, location: e.target.value })}
            className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
            placeholder="e.g. Guntur, AP"
          />
        </div>

        <div>
          <label className="text-xs font-bold text-stone-400 block mb-1">Phone Number (Optional)</label>
          <input 
            type="tel" 
            value={edited.phone || ''}
            onChange={e => setEdited({ ...edited, phone: e.target.value })}
            className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20"
            placeholder="+91..."
          />
        </div>

        <div>
          <label className="text-xs font-bold text-stone-400 block mb-1">Bio / About</label>
          <textarea 
            value={edited.bio || ''}
            onChange={e => setEdited({ ...edited, bio: e.target.value })}
            className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b7d36]/20 min-h-[100px]"
            placeholder="Brief bio about the expert..."
          />
        </div>

        <div>
          <label className="text-xs font-bold text-stone-400 block mb-1">Photo</label>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-xl bg-stone-100 overflow-hidden">
              {edited.photoUrl ? (
                <img src={edited.photoUrl} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-stone-300">
                  <UserCircle size={40} />
                </div>
              )}
            </div>
            <label className="flex-1 cursor-pointer">
              <div className="border-2 border-dashed border-stone-200 rounded-xl p-4 text-center hover:border-[#1b7d36] transition-colors">
                <span className="text-xs text-stone-500">Upload Photo</span>
              </div>
              <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
            </label>
          </div>
        </div>
      </div>

      <div className="flex gap-3 pt-4">
        <button 
          onClick={() => onSave(edited)}
          className="flex-1 bg-[#1b7d36] text-white py-4 rounded-2xl font-bold shadow-md hover:bg-[#16652b] transition-colors"
        >
          {t.save || 'Save Expert'}
        </button>
        <button 
          onClick={onCancel}
          className="flex-1 bg-stone-100 text-stone-600 py-4 rounded-2xl font-bold hover:bg-stone-200 transition-colors"
        >
          {t.cancel || 'Cancel'}
        </button>
      </div>
    </div>
  );
}
